import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { quizGeneratorAPI } from '../api/quizGeneratorAPI';
import { questionPreview } from './questionPreview';
import { resolveExamKeyboardAction, resolveTmuaPearsonAccessKey } from '../examKeyboardPolicy';
import { getColorSchemesForExam, getPearsonColorSchemeDropdownLabel } from '../constants/tmuaColorSchemes';
import { getSimScheme, simInk as simInkFor, tmuaInk as tmuaInkFor, simDanger as simDangerFor } from '../utils/simulatorScheme';
import { getTsaEndReviewCopy, resolveTsaSectionDurationMinutes } from '../utils/tsaPearsonCopy';
import { isTmuaPearsonPlayerSitting, isTaraBmatPearsonSource } from '../utils/isTmuaPearsonPlayerSitting';
import { isEssayChoiceGroup, resolveEssayPrompts, getEssayPromptDisplayText, resolveLnatEssayItemPrompt } from '../utils/essayChoiceGroup';
import { getTmuaSittingQuestions, getPearsonSittingQuestions } from '../utils/tagTmuaPaperQuestions';
import { nextTmuaPearsonPhase } from '../utils/nextTmuaPearsonPhase';
import {
  nextPearsonSittingPhase,
  parsePearsonItemsIndex,
  parsePearsonReviewIndex,
  parsePearsonInstructionsIndex,
  currentIndexFromPearsonPhase,
  isPearsonAwaitingPhase,
} from '../utils/nextPearsonSittingPhase';
import { ESAT_MODULE_LABELS } from '../utils/classifyEsatModule';
import { TARA_MODULE_LABELS } from '../utils/classifyTaraPaperModule';
import {
  BMAT_FALLBACK_QUESTION_COUNT,
  BMAT_MODULE_LABELS,
  BMAT_PEARSON_TITLE,
  bmatWritingInstructionBodyCopy,
  getBmatSectionDurationMinutes,
} from '../utils/classifyBmatPaperSection';
import { shouldSplitLnatPearsonItem } from '../utils/shouldSplitLnatPearsonItem';
import { shouldPauseTopicDrillOnEndExam } from '../utils/topicAttemptRow';
import { shouldClearLnatAnnotations } from '../utils/lnatPearsonAnnotationLifecycle';
import {
  applyHighlightToItem,
  removeHighlightFromItem,
  toggleStrikeOnItem,
} from '../utils/lnatPearsonAnnotationRanges';
import { readLnatSelection } from '../utils/lnatPearsonTextOffsets';
import { showLnatPearsonHighlightToolbar } from '../utils/showLnatPearsonHighlightToolbar';
import { DEFAULT_LNAT_HIGHLIGHT_COLOR } from '../constants/lnatPearsonHighlightTokens';
import { shouldSplitTaraCtPearsonItem } from '../utils/shouldSplitTaraCtPearsonItem';
import { shouldSplitBmatThinkingSkillsPearsonItem } from '../utils/shouldSplitBmatThinkingSkillsPearsonItem';
import { pearsonWritingMaxWords } from '../utils/pearsonWritingMaxWords';
import {
  hidePearsonWritingFlagNavigator,
  hidePearsonWritingFlagReview,
} from '../utils/hidePearsonWritingFlag';
import { shouldSplitUcatPearsonItem } from '../utils/shouldSplitUcatPearsonItem';
import { isTaraWritingFormatG } from '../utils/taraWritingPrompts';
import { expandTaraWritingPearsonItems, hasAnsweredOneTaraWritingPrompt, questionsForAttemptPayloads } from '../utils/expandTaraWritingPearsonItems';
import TmuaPearsonWritingEditor from './TmuaPearsonWritingEditor';
import { useCollapseOnFirstFocus } from '../hooks/useCollapseOnFirstFocus';
import { useTmuaPearsonKeyboardInset } from '../hooks/TmuaPearsonKeyboardInsetContext';
import { useVisualViewportKeyboardInset } from '../hooks/useVisualViewportKeyboardInset';
import { pearsonLiveModuleCount } from '../utils/pearsonLiveModuleCount';
import {
  getLnatTopicEndReviewCopy,
  isLnatEssayReviewPhase,
  isLnatTopicSectionB,
  pickLnatEssayReviewList,
  pickLnatOneResponseQuestionList,
} from '../utils/lnatTopicPearsonCopy';
import {
  buildLnatSectionBInstructionBody,
  lnatEssayOneResponseMessage,
  lnatEssayEndReviewMessage,
  lnatOrTsaPaper2InstructionCount,
} from '../utils/lnatSectionBInstructionCopy';
import { getUcatModuleDurationMinutes, ucatInstructionAdvanceEvent } from '../utils/getUcatModuleDurationMinutes';
import { getTopicDrillRestartMinutes } from '../utils/getTopicDrillRestartMinutes';
import { isPearsonTimeRemainingWarning } from '../utils/pearsonTimerChrome';
import { isTimedMode } from '../utils/examTimerDuration';
import {
  lnatSecondEssayBlocked,
  lnatSectionHasEssayText,
} from '../utils/expandLnatSectionBQuestions';
import TmuaPearsonShell, {
  DoorIcon,
  NextIcon,
  PrevIcon,
  NavigatorIcon,
  HelpIcon,
  RetakeIcon,
  ReviewIcon,
  TmuaPearsonFooterButton,
  getPearsonPlayerExamTitle,
  isLnatPearsonExam,
  isEsatPearsonExam,
  isTaraPearsonExam,
  isUcatPearsonExam,
} from './TmuaPearsonShell';
import TmuaPearsonDialog from './TmuaPearsonDialog';
import TmuaPearsonDragYesNo from './TmuaPearsonDragYesNo';
import TmuaPearsonDragMostLeast from './TmuaPearsonDragMostLeast';
import TmuaPearsonNavigatorModal from './TmuaPearsonNavigatorModal';
import TmuaPearsonReviewTable from './TmuaPearsonReviewTable';
import TmuaPearsonSolutionModal from './TmuaPearsonSolutionModal';
import LnatPearsonAnnotatable from './LnatPearsonAnnotatable';
import LnatPearsonHighlightToolbar from './LnatPearsonHighlightToolbar';
import TmuaInstructionsScreen from './TmuaInstructionsScreen';
import UcatSubtestInstructionsScreen from './UcatSubtestInstructionsScreen';
import UcatPearsonCalculator from './UcatPearsonCalculator';
import UcatPearsonHelpOverlay from './UcatPearsonHelpOverlay';
import { TMUA_PEARSON_TOKENS, getTmuaPearsonChrome } from '../constants/tmuaPearsonTokens';
import { prepareExamHtml, ensureLatexDelimiters } from '../utils/examLatexDelimiters';
import {
  ExhibitLightboxContext,
  ExhibitLightboxProvider,
  MathContent,
  PaperImage,
  keepPunctuationWithMath,
  markWideMath,
  OPTION_CARD_CLASS,
  optionHasText,
  optionListGap,
  optionRadioStyle,
  optionRowAlignment,
  optionsHavePictures,
  prepareOptionHtml,
  prepareSittingHtml,
  stripHtmlToText,
  waitForMathJax,
} from './examRender';
import { sanitizeHtml } from '../../../lib/sanitizeHtml';

export { prepareExamHtml, ensureLatexDelimiters };

const normalizeQuestionSpacing = (html) => {
  if (!html || typeof html !== 'string') return html;

  return html
    // Remove empty paragraphs
    .replace(/<p>\s*(?:<br\s*\/?>\s*)*\s*<\/p>/gi, '')
    // Collapse multiple <br> tags into one
    .replace(/(?:<br\s*\/?>\s*){2,}/gi, '<br>')
    // Remove <br> tags immediately between paragraphs
    .replace(/<\/p>\s*<br\s*\/?>\s*<p>/gi, '</p><p>')
    // Remove whitespace-only paragraphs
    .replace(/<p>\s+<\/p>/gi, '');
};



// Style objects passed to MathContent must be stable references across renders —
// React.memo only does a shallow prop comparison, so an inline `style={{...}}`
// object literal at the call site is a *new* object on every parent render (even
// with identical contents), which defeats the memoization and was why the timer-tick
// bug persisted after the first MathContent pass. Hoisting these to module-level
// constants (or, where the style genuinely depends on state like `isSelected`,
// to a small fixed set of constants selected by reference) keeps the reference
// stable so React.memo can actually skip re-rendering.
const IVORI_PASSAGE_STYLE = { fontFamily: 'var(--iv-font-body)', fontSize: '16.5px', lineHeight: '1.78', letterSpacing: '0.005em', color: 'var(--iv-body)' };
const IVORI_QUESTION_STYLE = { fontFamily: 'var(--iv-font-body)', fontSize: '16px', lineHeight: '1.48', fontWeight: 400, color: 'var(--iv-body)', margin: '0 0 24px', textAlign: 'left' };
const IVORI_ITEM_TEXT_STYLE = { fontFamily: 'var(--iv-font-body)', fontSize: '15.5px', lineHeight: '1.52', color: 'var(--iv-body)' };
// The MC option text's color/fontWeight legitimately change with `isSelected`, so
// they can't live on MathContent's own style prop without forcing it to re-render
// (and reset its MathJax-rendered DOM) on every click. Instead MathContent gets one
// single stable style (fontFamily/fontSize/lineHeight only), and the selection-
// dependent color/fontWeight/layout live on a plain wrapping <span> around it —
// color and font-weight inherit through the DOM, so the visual result is unchanged.
const IVORI_OPTION_TEXT_STYLE = { fontFamily: 'var(--iv-font-body)', fontSize: '14px', lineHeight: '1.5' };
const IVORI_OPTION_TEXT_WRAPPER_SELECTED = { flex: 1, fontWeight: '600', color: 'var(--iv-body)', marginTop: '3px' };
const IVORI_OPTION_TEXT_WRAPPER_UNSELECTED = { flex: 1, fontWeight: '450', color: 'var(--iv-body)', marginTop: '3px' };
// The explanation pop-up (renderExplainModal) draws its HTML through MathContent
// for the same reason, with these stable styles. React 19 rewrites the innerHTML
// of an element whose dangerouslySetInnerHTML object is new on a re-render, even
// when the string is the same; written inline, every re-render of the results
// screen after MathJax had typeset the pop-up put the raw source back ($3$,
// $$\int_0^2 ...$$), and the effect's later attempts were skipped as that run
// had already typeset.
const EXPLAIN_PASSAGE_STYLE = { fontSize: '14px', lineHeight: '1.7', color: 'var(--iv-body)', maxHeight: '220px', overflowY: 'auto', paddingRight: '8px' };
const EXPLAIN_QUESTION_STYLE = { fontSize: '15.5px', fontWeight: 400, color: 'var(--iv-body)', lineHeight: '1.45' };
const EXPLAIN_OPTION_STYLE = { flex: 1, fontSize: '14px', color: 'var(--iv-body)', lineHeight: '1.45', fontWeight: 400, paddingTop: '3px' };
const EXPLAIN_OPTION_STYLE_CORRECT = { ...EXPLAIN_OPTION_STYLE, color: 'var(--iv-body)', fontWeight: 600 };
const EXPLAIN_CRITERIA_STYLE = { fontSize: '14px', lineHeight: '1.65', color: 'var(--iv-body)' };
const EXPLAIN_ANSWER_STYLE = { fontSize: '14.5px', lineHeight: '1.65', color: 'var(--iv-body)', marginTop: '10px' };
const EXPLAIN_AFTER_LIST_STYLE = { ...EXPLAIN_ANSWER_STYLE, marginTop: '14px' };
const EXPLAIN_INLINE_STYLE = { display: 'inline' };
const PEARSON_QUESTION_STYLE = { fontFamily: 'Tahoma, Arial, Helvetica, sans-serif', fontSize: '16px', lineHeight: 1.45, color: '#000000', margin: '0 0 18px' };
const PEARSON_OPTION_TEXT_STYLE = { fontFamily: 'Tahoma, Arial, Helvetica, sans-serif', fontSize: '16px', lineHeight: 1.45, color: '#000000' };
// LNAT Pearson VUE item text is 12px Tahoma at the 1024-wide harvest
// (sample/04, linguistics Section A). TMUA tokens stay 16px.
const LNAT_PEARSON_QUESTION_STYLE = { fontFamily: 'Tahoma, Arial, Helvetica, sans-serif', fontSize: '14px', lineHeight: 1.25, color: '#000000', margin: '0 0 8px' };
const LNAT_PEARSON_OPTION_TEXT_STYLE = { fontFamily: 'Tahoma, Arial, Helvetica, sans-serif', fontSize: '14px', lineHeight: 1.25, color: '#000000' };
const EMPTY_LNAT_ANNOTATIONS = { highlights: [], strikes: [] };






const getSelectedEssayPromptText = (question, answer) => {
  const prompts = resolveEssayPrompts(question);
  const selected = prompts.find((p) => p.label === answer?.label);
  const target = selected || prompts[0];
  if (!target) return '';
  return getEssayPromptDisplayText(target, question);
};

/** LNAT Section B: pick ONE of several essay prompts on a single question. */
const isLnatSectionBEssay = (q) => isEssayChoiceGroup(q);

/** UCAT Decision Making style Yes/No statement groups (website yes_no_group). */
const isYesNoQuestion = (q) =>
  Boolean(
    q &&
      q.interaction_kind === 'yes_no_group' &&
      Array.isArray(q.yes_no_items) &&
      q.yes_no_items.length > 0
  );

/** UCAT SJT most/least appropriate action groups. */
const isMostLeastQuestion = (q) =>
  Boolean(
    q &&
      q.interaction_kind === 'most_least_group' &&
      Array.isArray(q.most_least_options) &&
      q.most_least_options.length > 0
  );

/** Free-text practice (e.g. Medical Interviews) — one written answer per question, navigate the full set. */
const isFreeTextPractice = (q) =>
  Boolean(
    q &&
      q.multiple_choice === false &&
      !isEssayChoiceGroup(q) &&
      !isYesNoQuestion(q) &&
      !isMostLeastQuestion(q)
  );

/** @deprecated alias — kept for Section B call sites that mean essay-choice chrome */
const isEssayQuestion = (q) => isEssayChoiceGroup(q);

const isMcqQuestion = (q) =>
  Boolean(
    q &&
      !isYesNoQuestion(q) &&
      !isMostLeastQuestion(q) &&
      !isEssayChoiceGroup(q) &&
      q.multiple_choice !== false &&
      Array.isArray(q.options) &&
      q.options.length > 1
  );

const isTaraExamContext = (examName, question, activeQuizSource) => {
  const exam = String(examName || '').toUpperCase();
  if (exam === 'TARA') return true;
  const blob = [
    question?.parameter,
    question?.external_question_id,
    activeQuizSource?.id,
  ].map((v) => String(v || '').toUpperCase()).join(' ');
  return /\bTARA\b/.test(blob) || blob.includes('TARA.');
};

/** Official UAT-UK Writing Task welcome / NDA (Page 1). */
const TARA_NDA_PARAGRAPHS = [
  'Welcome to the Writing Task.',
  'Non-disclosure agreement and general terms of use for tests developed for UAT-UK:',
  'The test is made available to you as a candidate solely for the purpose of being assessed in this test. You are expressly prohibited from disclosing, publishing, reproducing or transmitting this test, in whole or in part, in any form or by any means including visual, verbal, written, electronic or mechanical means, for any purpose, without the prior express written permission of UAT-UK.',
  'By clicking Next you agree to accept the terms of this non-disclosure agreement.',
  'Click the Next button when you are ready to begin the test.',
];

/** Official UAT-UK Writing Task information screen (Page 2). */
const TARA_TEST_INFO_PARAGRAPHS = [
  { text: 'Number of Questions: 1' },
  { text: 'Time: Untimed' },
  { text: 'Test Information', heading: true },
  { text: 'Answer ONE of the following three questions.' },
  { text: "You can use the 'Next' and 'Previous' buttons to view all three questions before selecting the one you wish to answer." },
  { text: 'You must only respond to ONE question. Do not write answers for more than one question. Leave the other two questions completely blank.', bold: true },
  { text: 'The tasks each provide an opportunity for you to show how well you can select, develop and organise ideas and communicate them effectively in writing.' },
  { text: 'Before you begin writing, take time to think carefully about what you need to say and the ways in which the organisation and layout of your response might help convey your message.' },
  { text: 'Take care to show how well you can write and be concise, clear and accurate.' },
  { text: 'Your answer should not exceed 750 words.', bold: true },
];

/** Shape a TARA Writing Task prompt for display (statement + attribution + task lines). */
const parseTaraEssayPrompt = (rawText) => {
  const plain = stripHtmlToText(rawText || '')
    .replace(/\u00a0/g, ' ')
    .replace(/\r\n/g, '\n')
    .trim();
  if (!plain) {
    return { statement: '', attribution: '', tasks: [] };
  }

  const lines = plain.split(/\n+/).map((l) => l.trim()).filter(Boolean);
  const taskStart = lines.findIndex((l) =>
    /^explain what you think is meant/i.test(l)
    || /^give a reasoned argument/i.test(l)
    || /^to what extent do you agree/i.test(l)
  );

  const headLines = taskStart >= 0 ? lines.slice(0, taskStart) : lines;
  const tasks = taskStart >= 0 ? lines.slice(taskStart) : [];

  let statement = headLines.join(' ').trim();
  let attribution = '';

  const attrMatch = statement.match(/^(.*?)\s*[\(\[]([^\)\]]+)[\)\]]\s*$/);
  if (attrMatch) {
    statement = attrMatch[1].trim();
    attribution = attrMatch[2].trim();
  }

  statement = statement.replace(/^["'“‘]+|["'”’]+$/g, '').trim();
  return { statement, attribution, tasks };
};

const essayNavBtnStyle = (enabled = true) => ({
  background: 'var(--iv-surface)',
  color: enabled ? 'var(--iv-body)' : 'var(--iv-muted)',
  border: '1px solid var(--iv-line)',
  borderRadius: '999px',
  padding: '8px 20px',
  fontSize: '13px',
  fontWeight: '600',
  cursor: enabled ? 'pointer' : 'default',
  opacity: enabled ? 1 : 0.65,
});

const essayPrimaryBtnStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'var(--iv-btn)',
  color: 'var(--iv-btn-fg)',
  border: 'none',
  borderRadius: '999px',
  padding: '11px 28px',
  minHeight: '42px',
  fontSize: '13.5px',
  fontWeight: '700',
  lineHeight: 1.2,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const formatExamDisplayName = (name) => {
  const raw = String(name || 'Practice').replace(/[_-]+/g, ' ').trim();
  if (!raw) return 'Practice';
  return raw
    .split(/\s+/)
    .map((w) => (w.length <= 3 && w === w.toUpperCase() ? w : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join(' ')
    .replace(/\bMmi\b/g, 'MMI')
    .replace(/\bLnat\b/g, 'LNAT')
    .replace(/\bUcAt\b/gi, 'UCAT');
};

// The label above an interview question: the topic paper's own title (set when the
// paper was started), else the question's topic or subtopic name.
const getMedintTopicLabel = (question, activeQuizSource) => (
  activeQuizSource?.paperTitle || question?.topic_name || question?.subtopic_name || null
);
export { getMedintTopicLabel };

const inferInterviewFormatTags = (questionText, examName) => {
  const t = String(questionText || '').toLowerCase();
  const exam = String(examName || '').toLowerCase();
  const tags = [];
  if (exam.includes('aston') || /\baston\b/.test(t)) tags.push('Aston');
  if (exam.includes('birmingham') || /\bbirmingham\b/.test(t)) tags.push('Birmingham');
  if (exam.includes('cambridge') || /cambridge/.test(t)) tags.push('Cambridge');
  else if (/oxford|oxbridge|collegiate|tutorial|supervision/.test(t)) tags.push('Oxbridge');
  if (exam.includes('mmi') || /\bmmi\b|multiple mini|station\b/.test(t)) tags.push('MMI');
  if (exam.includes('panel') || /\bpanel\b/.test(t)) tags.push('Panel');
  return [...new Set(tags)];
};

const QuizDisplaySectionInner = ({
  questions,
  userAnswers,
  setUserAnswers,
  onSubmit,
  onCompleteAttempts,
  onRetryComplete,
  paperCompleteError = false,
  pauseSaveError = false,
  onBack,
  timeRemaining = 0,
  formatTime,
  examName = 'LNAT',
  activeQuizSource,
  onStartTimer,
  colorScheme = 'ivori',
  setColorScheme,
  onStartPaperTimer,
  onRegisterTmuaExpire,
  onSwapTmuaPaper,
  tmuaPaper1Questions = [],
  tmuaPaper2Questions = [],
  tmuaPaper2Status = 'idle',
  tmuaModeChoice = null,
  tmuaP1TimerDuration = 75,
  tmuaP2TimerDuration = 75,
  pearsonModules = [],
  resumeState = null,
}) => {
  // The simulator's scheme: tokens on ivori, the literal Pearson scheme otherwise (see utils/simulatorScheme.js).
  const SIM_SCHEME = getSimScheme(colorScheme);
  const simInk = (token) => simInkFor(colorScheme, SIM_SCHEME, token);
  const simDanger = simDangerFor(colorScheme);
  const { openLightbox, lightboxOpenRef } = useContext(ExhibitLightboxContext) || {};
  const isTmuaExam = String(examName).toLowerCase() === 'tmua';
  // Text where the background is the scheme's only for a TMUA sitting.
  const tmuaInk = (token) => tmuaInkFor(isTmuaExam, colorScheme, SIM_SCHEME, token);
  const isLnatExam = isLnatPearsonExam(examName);
  const pearsonPlayer = isTmuaPearsonPlayerSitting(examName, activeQuizSource);
  const isEsatExam = isEsatPearsonExam(examName) && pearsonPlayer;
  const isTaraExam = isTaraPearsonExam(examName) && pearsonPlayer;
  const isTaraTsaSitting = isTaraExam && Boolean(activeQuizSource?.tsaSitting);
  const isTaraBmatSitting = isTaraExam && isTaraBmatPearsonSource(activeQuizSource);
  const isUcatExam = isUcatPearsonExam(examName) && pearsonPlayer
    && Array.isArray(pearsonModules) && pearsonModules.length >= 1;
  const isTopicDrill = Boolean(activeQuizSource?.topicDrill);
  // TSA under TARA is TMUA/LNAT-style p1→p2 (Section 2 essay), not CT/PS/Writing modules
  const usesPearsonModules = isEsatExam || (isTaraExam && !isTaraTsaSitting) || isUcatExam;
  const esatBootstrapPhase = (
    isEsatPearsonExam(examName)
    || isUcatPearsonExam(examName)
    || (isTaraPearsonExam(examName) && !activeQuizSource?.tsaSitting)
  ) ? 'm0_items' : 'p1_items';
  const [examMode, setExamMode] = useState('ivori'); // Default directly to IVORI simulation, bypass Pearson selection modal
  
  // Pearson simulation accessibility contrast & text sizing options
  const [pearsonColorScheme, setPearsonColorScheme] = useState('default');

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(
    Number.isFinite(Number(resumeState?.questionIndex)) ? Number(resumeState.questionIndex) : 0
  );
  const [flaggedQuestions, setFlaggedQuestions] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(Boolean(resumeState?.reviewMode));
  const [reviewScreenOpen, setReviewScreenOpen] = useState(false);
  const [selectedReviewQuestion, setSelectedReviewQuestion] = useState(0);
  const [reviewTab, setReviewTab] = useState('all');
  const [visitedQuestions, setVisitedQuestions] = useState({}); // { [question.id]: true } — TMUA Navigator only
  const [showNavigatorModal, setShowNavigatorModal] = useState(false); // TMUA Navigator popup only
  const [navigatorSortAsc, setNavigatorSortAsc] = useState(true); // TMUA Navigator Question # column
  const [showEntireScreenModal, setShowEntireScreenModal] = useState(false); // TMUA scroll-to-end gate
  const [showEndReviewModal, setShowEndReviewModal] = useState(false); // Item Review end confirmation (step 1 — incorrect)
  const [showEndReviewConfirmModal, setShowEndReviewConfirmModal] = useState(false); // Item Review end confirmation (step 2 — unanswered)
  const [showEndExamModal, setShowEndExamModal] = useState(false);
  const [showEndReviewConfirm, setShowEndReviewConfirm] = useState(false);
  const [showResponseRequired, setShowResponseRequired] = useState(false);
  const [responseRequiredMessage, setResponseRequiredMessage] = useState('');
  const [lnatAnnotationsByItemId, setLnatAnnotationsByItemId] = useState({});
  const [lnatActiveHighlightColor, setLnatActiveHighlightColor] = useState(DEFAULT_LNAT_HIGHLIGHT_COLOR);
  const [lnatMathJaxEpoch, setLnatMathJaxEpoch] = useState(0);
  const lnatOptionPointerRef = useRef({});
  const lnatAnnotateEnabledRef = useRef(false);
  lnatAnnotateEnabledRef.current = Boolean(pearsonPlayer && isLnatExam);
  const [tmuaPaperPhase, setTmuaPaperPhase] = useState(
    resumeState?.phase || esatBootstrapPhase
  );
  const [tmuaPaper1Frozen, setTmuaPaper1Frozen] = useState(false);
  const [frozenModuleCount, setFrozenModuleCount] = useState(0);
  const [tmuaCounterVisible, setTmuaCounterVisible] = useState(true);
  const [showSolutionModal, setShowSolutionModal] = useState(false);
  const [solutionQuestion, setSolutionQuestion] = useState(null);
  const [showPostExamInstructions, setShowPostExamInstructions] = useState(false);
  const [awaitingP2Reason, setAwaitingP2Reason] = useState('lock');
  const [ucatHelpOpen, setUcatHelpOpen] = useState(false);
  const [ucatCalculatorOpen, setUcatCalculatorOpen] = useState(false);
  const [ucatCalculatorEnabled, setUcatCalculatorEnabled] = useState(true);
  // Mobile split-pane divider state — persists for the session (not reset
  // per-question), shared by both split-pane rendering sites since only one
  // is ever mounted at a time.
  // A passage-fraction: the question pane's own share is always derived as
  // 1 - splitPaneRatio (see splitPaneQuestionStyle below), never stored
  // separately. Defaults to 60/40 passage/question per the real Pearson VUE
  // exam (docs/research/ucat-pearson-vue-harvest/sample/uk-pt-a/13-vr-split-ratio.png);
  // a user drag can move it anywhere within the drag handler's own clamp.
  const [splitPaneRatio, setSplitPaneRatio] = useState(0.6);
  const [splitPaneCollapsed, setSplitPaneCollapsed] = useState('none'); // 'none' | 'passage' | 'question'
  const [compactViewport, setCompactViewport] = useState(() => (
    typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(max-width: 768px)').matches
  ));
  // Writing-task mobile behavior for the TARA "stopgap" inline editor,
  // which renders inline here rather than as a standalone component —
  // TmuaPearsonWritingEditor.js calls the same hooks itself.
  const {
    collapsed: taraWritingPromptCollapsed,
    expand: expandTaraWritingPrompt,
    handleTextareaFocus: handleTaraWritingTextareaFocus,
  } = useCollapseOnFirstFocus();
  const taraWritingKeyboardInset = useTmuaPearsonKeyboardInset();
  const taraWritingKeyboardOpen = taraWritingKeyboardInset > 0;
  // Standalone IVORI essay canvas (LNAT Section B / TARA writing when not
  // routed through the Pearson shell) does not render inside
  // TmuaPearsonShell, so it reads visualViewport directly rather than via
  // context — see docs/plans/MOBILE_EXAM_RUNNER_PLAN.md Phase 5.
  const {
    collapsed: ivoriEssayPromptCollapsed,
    expand: expandIvoriEssayPrompt,
    handleTextareaFocus: handleIvoriEssayTextareaFocus,
  } = useCollapseOnFirstFocus();
  const ivoriEssayKeyboardInset = useVisualViewportKeyboardInset();
  const ivoriEssayKeyboardOpen = ivoriEssayKeyboardInset > 80;
  const solutionModalRef = useRef(null);
  const readOnlyItemRef = useRef(null);
  const tmuaPhaseRef = useRef(esatBootstrapPhase);
  const tmuaP2StatusRef = useRef('idle');
  const prevTmuaPhaseRef = useRef(resumeState?.phase || esatBootstrapPhase);
  const pearsonModulesRef = useRef(pearsonModules);
  const lastModuleIndexRef = useRef(0);

  const [showExplainModal, setShowExplainModal] = useState(false);
  const [explainQuestionIndex, setExplainQuestionIndex] = useState(null);
  const [stopwatchSeconds, setStopwatchSeconds] = useState(0);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [essayShowInstructions, setEssayShowInstructions] = useState(true);
  /** TARA Writing Task flow: nda → info → write → thanks (LNAT still uses essayShowInstructions). */
  const [taraEssayPhase, setTaraEssayPhase] = useState('nda');
  const essayTimerStartedRef = useRef(false);
  const topicAllAnsweredRef = useRef(false);
  const taraPhaseQuestionRef = useRef(null);
  
  const containerRef = useRef(null);
  const questionPaneRef = useRef(null);
  const passagePaneRef = useRef(null);
  const splitBodyRef = useRef(null);
  const SPLIT_DIVIDER_HEIGHT = 24;
  // The collapsed-pane restore bar is a separate control from the divider's
  // own hit area — it's the only way to re-expand a collapsed pane mid-exam,
  // so it needs the full 44px touch target, not the divider's 24px.
  const SPLIT_COLLAPSED_BAR_HEIGHT = 44;
  // Tracks whether each pane has EVER reached its scroll bottom for the
  // current question, so scrolling back up afterward doesn't re-block Next.
  const questionPaneSeenBottomRef = useRef(false);
  const passagePaneSeenBottomRef = useRef(false);
  const essayTextareaRef = useRef(null);
  const paper1Qs = (Array.isArray(tmuaPaper1Questions) && tmuaPaper1Questions.length)
    ? tmuaPaper1Questions
    : questions;
  const paper2Qs = Array.isArray(tmuaPaper2Questions) ? tmuaPaper2Questions : [];
  const isP2Phase = pearsonPlayer && ['p2_instructions', 'p2_items', 'p2_review'].includes(tmuaPaperPhase);
  const pearsonModuleIndex = usesPearsonModules
    ? currentIndexFromPearsonPhase(tmuaPaperPhase, lastModuleIndexRef.current)
    : (isP2Phase ? 1 : 0);
  const esatModuleQuestions = Array.isArray(pearsonModules[pearsonModuleIndex]?.questions)
    ? pearsonModules[pearsonModuleIndex].questions
    : [];
  const activeModuleKey = pearsonModules[pearsonModuleIndex]?.moduleKey;
  const isTaraWritingModuleActive = isTaraExam && activeModuleKey === 'writing';
  // Fail closed to the cream-styled stopgap unless the module's payload is exactly
  // one essay_choice_group question with exactly 3 essay_options (spec "format G").
  const isTaraWritingFormatGActive = isTaraWritingModuleActive && isTaraWritingFormatG(esatModuleQuestions);
  const taraWritingModuleItems = isTaraWritingFormatGActive
    ? expandTaraWritingPearsonItems(esatModuleQuestions, pearsonModules[pearsonModuleIndex]?.paperId)
    : [];
  const takingQuestions = pearsonPlayer
    ? (usesPearsonModules
      ? (isTaraWritingFormatGActive
        ? taraWritingModuleItems
        : (esatModuleQuestions.length ? esatModuleQuestions : paper1Qs))
      : (isP2Phase ? paper2Qs : paper1Qs))
    : questions;
  tmuaPhaseRef.current = tmuaPaperPhase;
  tmuaP2StatusRef.current = tmuaPaper2Status;
  pearsonModulesRef.current = pearsonModules;
  const trackedModuleIndex = parsePearsonItemsIndex(tmuaPaperPhase) ?? parsePearsonReviewIndex(tmuaPaperPhase);
  if (trackedModuleIndex != null) lastModuleIndexRef.current = trackedModuleIndex;

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(max-width: 768px)');
    const onChange = (event) => setCompactViewport(event.matches);
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);

  // Auto-focus active question pane so mouse wheel, touch, and arrow keys scroll immediately without requiring a click on the page.
  // Writing-task textareas are deliberately excluded: they should start (and
  // stay, once blurred) unfocused like any ordinary text box, so this always
  // focuses the pane/container instead. The data-writing-keep-focus bail-out
  // is the actual correctness guarantee that this can never steal focus back
  // out of a textarea a student is using, even if this effect re-runs.
  useEffect(() => {
    window.focus();
    const timer = setTimeout(() => {
      if (document.activeElement?.closest?.('[data-writing-keep-focus]')) return;
      if (questionPaneRef.current) {
        questionPaneRef.current.focus({ preventScroll: true });
      } else if (containerRef.current) {
        containerRef.current.focus({ preventScroll: true });
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [currentQuestionIndex, examMode, isSubmitted, showExplainModal, reviewScreenOpen, pearsonPlayer, tmuaPaperPhase]);

  // Reset scroll position to the top when moving to a new question, so a
  // scrolled-down question doesn't carry its scroll offset into the next one.
  // Also reset the "seen bottom" flags so each question requires its own scroll-through.
  useEffect(() => {
    if (questionPaneRef.current) questionPaneRef.current.scrollTop = 0;
    if (passagePaneRef.current) passagePaneRef.current.scrollTop = 0;
    questionPaneSeenBottomRef.current = false;
    passagePaneSeenBottomRef.current = false;
  }, [currentQuestionIndex]);

  // Mark the current question as visited so the Navigator can distinguish
  // "seen but unanswered" (Incomplete) from "never opened" (Unseen).
  useEffect(() => {
    const q = takingQuestions[currentQuestionIndex];
    if (!q) return;
    setVisitedQuestions(prev => (prev[q.id] ? prev : { ...prev, [q.id]: true }));
  }, [currentQuestionIndex, takingQuestions]);

  useEffect(() => {
    if (!pearsonPlayer || typeof onRegisterTmuaExpire !== 'function') return undefined;
    const handler = () => {
      const phase = tmuaPhaseRef.current;
      if (usesPearsonModules) {
        const modules = pearsonModulesRef.current || [];
        const i = lastModuleIndexRef.current;
        const nextMod = modules[i + 1];
        const nextStatus = nextMod?.status || 'idle';
        const next = nextPearsonSittingPhase(phase, 'timer0', {
          moduleCount: pearsonLiveModuleCount(modules, { isUcatExam }),
          currentIndex: i,
          nextStatus,
          hasNext: Boolean(nextMod && (nextStatus === 'ready' || nextStatus === 'loading' || (nextMod.questions || []).length)),
          siblingExpected: i < (modules.length || 3) - 1 || nextStatus === 'loading' || nextStatus === 'ready',
        });
        setShowEndExamModal(false);
        setShowEntireScreenModal(false);
        setShowNavigatorModal(false);
        setShowEndReviewConfirm(false);
        setReviewScreenOpen(false);
        setShowSolutionModal(false);
        setShowResponseRequired(false);
        setFrozenModuleCount((n) => Math.max(n, i + 1));
        if (next === 'awaiting_next') setAwaitingP2Reason('continue');
        if (shouldClearLnatAnnotations({ event: 'timer0', nextPhase: next })) {
          setLnatAnnotationsByItemId({});
          setLnatActiveHighlightColor(DEFAULT_LNAT_HIGHLIGHT_COLOR);
        }
        if (next === 'post_exam') setIsSubmitted(true);
        setTmuaPaperPhase(next);
        if (next === 'post_exam' && typeof onCompleteAttempts === 'function') onCompleteAttempts();
        return;
      }
      const status = tmuaP2StatusRef.current;
      const hasP2 = status === 'ready' && Array.isArray(tmuaPaper2Questions) && tmuaPaper2Questions.length > 0;
      const next = nextTmuaPearsonPhase(phase, 'timer0', {
        hasP2,
        p2Status: status,
        siblingExpected: status === 'loading' || status === 'ready' || hasP2,
      });
      setShowEndExamModal(false);
      setShowEntireScreenModal(false);
      setShowNavigatorModal(false);
      setShowEndReviewConfirm(false);
      setReviewScreenOpen(false);
      setShowSolutionModal(false);
      setShowResponseRequired(false);
      if (phase === 'p1_items' || phase === 'p1_review') setTmuaPaper1Frozen(true);
      if (next === 'awaiting_p2') setAwaitingP2Reason('continue');
      if (shouldClearLnatAnnotations({ event: 'timer0', nextPhase: next })) {
        setLnatAnnotationsByItemId({});
        setLnatActiveHighlightColor(DEFAULT_LNAT_HIGHLIGHT_COLOR);
      }
      if (next === 'post_exam') setIsSubmitted(true);
      setTmuaPaperPhase(next);
      if (next === 'post_exam' && typeof onCompleteAttempts === 'function') onCompleteAttempts();
    };
    onRegisterTmuaExpire(handler);
    return () => onRegisterTmuaExpire(null);
  }, [pearsonPlayer, onRegisterTmuaExpire, tmuaPaper2Questions, usesPearsonModules, isUcatExam, onCompleteAttempts]);

  useEffect(() => {
    if (!pearsonPlayer) return;
    const prev = prevTmuaPhaseRef.current;
    if (usesPearsonModules) {
      const itemsIndex = parsePearsonItemsIndex(tmuaPaperPhase);
      const prevItemsIndex = parsePearsonItemsIndex(prev);
      if (itemsIndex != null && itemsIndex > 0 && prevItemsIndex !== itemsIndex) {
        setCurrentQuestionIndex(0);
        setReviewScreenOpen(false);
        if (typeof onSwapTmuaPaper === 'function') onSwapTmuaPaper(itemsIndex + 1);
        if (isTimedMode(tmuaModeChoice) && typeof onStartPaperTimer === 'function') {
          const minutes = isUcatExam
            ? getUcatModuleDurationMinutes(pearsonModules[itemsIndex] || { moduleKey: pearsonModules[itemsIndex]?.moduleKey })
            : isTaraBmatSitting
              ? getBmatSectionDurationMinutes(pearsonModules[itemsIndex]?.moduleKey)
              : 40;
          onStartPaperTimer(minutes);
        }
      }
      if (tmuaPaperPhase === 'm0_items' && prev === 'post_exam') {
        setCurrentQuestionIndex(0);
        if (typeof onSwapTmuaPaper === 'function') onSwapTmuaPaper(1);
        if (isTimedMode(tmuaModeChoice) && typeof onStartPaperTimer === 'function') {
          onStartPaperTimer(getTopicDrillRestartMinutes({
            isTopicDrill,
            isUcatExam,
            isTaraBmatSitting,
            pearsonModules,
            tmuaP1TimerDuration,
          }));
        }
      }
      prevTmuaPhaseRef.current = tmuaPaperPhase;
      return;
    }
    if (tmuaPaperPhase === 'p2_items' && prev !== 'p2_items') {
      setCurrentQuestionIndex(0);
      setReviewScreenOpen(false);
      if (typeof onSwapTmuaPaper === 'function') onSwapTmuaPaper(2);
      if (tmuaModeChoice === 'timed' && typeof onStartPaperTimer === 'function') {
        onStartPaperTimer(isLnatExam ? tmuaP2TimerDuration : 75);
      }
    }
    if (tmuaPaperPhase === 'p1_items' && prev === 'post_exam') {
      setCurrentQuestionIndex(0);
      if (typeof onSwapTmuaPaper === 'function') onSwapTmuaPaper(1);
    }
    prevTmuaPhaseRef.current = tmuaPaperPhase;
  }, [pearsonPlayer, tmuaPaperPhase, onSwapTmuaPaper, onStartPaperTimer, tmuaModeChoice, isLnatExam, usesPearsonModules, tmuaP2TimerDuration, isUcatExam, isTaraBmatSitting, pearsonModules, isTopicDrill, tmuaP1TimerDuration]);

  useEffect(() => {
    if (!pearsonPlayer) return;
    if (usesPearsonModules) {
      if (!isPearsonAwaitingPhase(tmuaPaperPhase)) return;
      const modules = pearsonModules || [];
      const i = lastModuleIndexRef.current;
      const nextMod = modules[i + 1];
      const status = nextMod?.status;
      if (status === 'ready') {
        const next = nextPearsonSittingPhase(tmuaPaperPhase, 'nextReady', {
          moduleCount: modules.length || 3,
          currentIndex: i,
          awaitingReason: awaitingP2Reason,
          nextStatus: 'ready',
          hasNext: true,
        });
        if (next === 'post_exam') setIsSubmitted(true);
        setTmuaPaperPhase(next);
      } else if (status === 'missing' || status === 'error') {
        setIsSubmitted(true);
        setTmuaPaperPhase('post_exam');
      }
      return;
    }
    if (tmuaPaperPhase !== 'awaiting_p2') return;
    if (tmuaPaper2Status === 'ready') {
      const next = nextTmuaPearsonPhase('awaiting_p2', 'p2Ready', {
        awaitingReason: awaitingP2Reason,
        hasP2: true,
        p2Status: 'ready',
      });
      if (next === 'post_exam') setIsSubmitted(true);
      setTmuaPaperPhase(next);
    } else if (tmuaPaper2Status === 'missing' || tmuaPaper2Status === 'error') {
      setIsSubmitted(true);
      setTmuaPaperPhase('post_exam');
    }
  }, [pearsonPlayer, tmuaPaperPhase, tmuaPaper2Status, awaitingP2Reason, usesPearsonModules, pearsonModules]);

  // Section B / free-text: init answers + timer
  useEffect(() => {
    const q = takingQuestions[currentQuestionIndex];
    if (isFreeTextPractice(q)) {
      setEssayShowInstructions(false);
      setUserAnswers((prev) => {
        const existing = prev[q.id] || {};
        if (existing.is_text_answer) return prev;
        return {
          ...prev,
          [q.id]: {
            ...existing,
            text_response: existing.text_response || '',
            question_db_id: q.db_id,
            is_text_answer: true,
          },
        };
      });
      if (!essayTimerStartedRef.current && typeof onStartTimer === 'function') {
        essayTimerStartedRef.current = true;
        onStartTimer();
      }
      return;
    }

    if (!isEssayQuestion(q)) {
      essayTimerStartedRef.current = false;
      setEssayShowInstructions(true);
      setTaraEssayPhase('nda');
      taraPhaseQuestionRef.current = null;
      return;
    }

    const taraEssay = isTaraExamContext(examName, q, activeQuizSource);
    if (pearsonPlayer) {
      setEssayShowInstructions(false);
    } else if (taraEssay) {
      setEssayShowInstructions(false);
      if (taraPhaseQuestionRef.current !== q.id) {
        taraPhaseQuestionRef.current = q.id;
        setTaraEssayPhase('nda');
        essayTimerStartedRef.current = false;
      }
    } else {
      // LNAT Section B: only show instructions when entering a *new* essay question.
      // Do not reset on later effect re-runs (that trapped users on the instructions page).
      if (taraPhaseQuestionRef.current !== q.id) {
        taraPhaseQuestionRef.current = q.id;
        setEssayShowInstructions(true);
        essayTimerStartedRef.current = false;
      }
    }

    const prompts = resolveEssayPrompts(q);
    if (prompts.length === 0) return;

    setUserAnswers((prev) => {
      const existing = prev[q.id] || {};
      if (existing.label) return prev;
      const first = prompts[0];
      const label = q.writingPromptLabel || first.label || 'A';
      return {
        ...prev,
        [q.id]: {
          ...existing,
          label,
          text_response: existing.text_response || '',
          prompt_drafts: existing.prompt_drafts || {},
          question_db_id: q.db_id,
          is_text_answer: true,
        },
      };
    });

    // LNAT: start timer on instructions. TARA: start when entering write phase.
    if (!taraEssay && !pearsonPlayer && !essayTimerStartedRef.current && typeof onStartTimer === 'function') {
      essayTimerStartedRef.current = true;
      onStartTimer();
    }
  }, [currentQuestionIndex, takingQuestions, setUserAnswers, onStartTimer, examName, activeQuizSource, pearsonPlayer]);

  const prevEssayItemIdRef = useRef(null);
  useEffect(() => {
    if (!pearsonPlayer) return;
    const q = takingQuestions[currentQuestionIndex];
    if (!isEssayChoiceGroup(q)) return;
    if (prevEssayItemIdRef.current !== q.id) {
      prevEssayItemIdRef.current = q.id;
      setUndoStack([]);
      setRedoStack([]);
    }
  }, [pearsonPlayer, currentQuestionIndex, takingQuestions]);

  const startTaraEssayTimer = () => {
    if (!essayTimerStartedRef.current && typeof onStartTimer === 'function') {
      essayTimerStartedRef.current = true;
      onStartTimer();
    }
  };

  // Global wheel handler to ensure seamless scrolling anywhere on the viewport
  const handleContainerWheel = (e) => {
    const target = e.target;
    if (target && target.closest && (target.closest('.ivori-exam-passage-pane') || target.closest('.ivori-exam-question-pane') || target.closest('.ivori-review-panel'))) {
      return;
    }
    const scrollTarget = questionPaneRef.current || passagePaneRef.current;
    if (scrollTarget) {
      scrollTarget.scrollTop += e.deltaY;
    }
  };

  const lnatEssayOneResponseBlocks = (q, proposedText) => {
    if (!(pearsonPlayer && isLnatExam && q)) return false;
    const topicB = isLnatTopicSectionB({
      isTopicDrill,
      lnatSection: activeQuizSource?.lnatSection,
    });
    if (!(isP2Phase || topicB)) return false;
    if (!String(proposedText || '').trim()) return false;
    const list = pickLnatOneResponseQuestionList({
      isTopicDrill,
      lnatSection: activeQuizSource?.lnatSection,
      paper1Qs,
      paper2Qs,
      takingQuestions,
    });
    if (!lnatSecondEssayBlocked(userAnswers, list, q.id)) return false;
    setResponseRequiredMessage(lnatEssayOneResponseMessage(list.length));
    setShowResponseRequired(true);
    return true;
  };

  const handleCut = async () => {
    const el = essayTextareaRef.current;
    if (!el) return;
    const q = takingQuestions[currentQuestionIndex];
    if (!q) return;
    el.focus();
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const currentVal = el.value || '';
    if (start === end) return;
    const selected = currentVal.substring(start, end);
    try {
      await navigator.clipboard.writeText(selected);
    } catch {
      document.execCommand('copy');
    }
    const updated = currentVal.substring(0, start) + currentVal.substring(end);
    if (lnatEssayOneResponseBlocks(q, updated)) return;
    setUndoStack(prev => [...prev.slice(-40), currentVal]);
    setRedoStack([]);
    handleTextInput(q.id, q, updated);
    setTimeout(() => {
      if (essayTextareaRef.current) {
        essayTextareaRef.current.focus();
        essayTextareaRef.current.setSelectionRange(start, start);
      }
    }, 0);
  };

  const handleCopy = async () => {
    const el = essayTextareaRef.current;
    if (!el) return;
    el.focus();
    const start = el.selectionStart;
    const end = el.selectionEnd;
    if (start === end) return;
    const selected = (el.value || '').substring(start, end);
    try {
      await navigator.clipboard.writeText(selected);
    } catch {
      document.execCommand('copy');
    }
  };

  const handlePaste = async () => {
    const el = essayTextareaRef.current;
    if (!el) return;
    const q = takingQuestions[currentQuestionIndex];
    if (!q) return;
    el.focus();
    let textToPaste = '';
    try {
      textToPaste = await navigator.clipboard.readText();
    } catch {
      textToPaste = '';
    }
    if (!textToPaste) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const currentVal = el.value || '';
    const updated = currentVal.substring(0, start) + textToPaste + currentVal.substring(end);
    if (lnatEssayOneResponseBlocks(q, updated)) return;
    setUndoStack(prev => [...prev.slice(-40), currentVal]);
    setRedoStack([]);
    handleTextInput(q.id, q, updated);
    setTimeout(() => {
      if (essayTextareaRef.current) {
        essayTextareaRef.current.focus();
        const newPos = start + textToPaste.length;
        essayTextareaRef.current.setSelectionRange(newPos, newPos);
      }
    }, 0);
  };

  const handleUndo = () => {
    const q = takingQuestions[currentQuestionIndex];
    if (!q || undoStack.length === 0) return;
    const prevVal = undoStack[undoStack.length - 1];
    const currentVal = userAnswers[q.id]?.text_response || '';
    if (lnatEssayOneResponseBlocks(q, prevVal)) return;
    setUndoStack(prev => prev.slice(0, -1));
    setRedoStack(prev => [...prev.slice(-40), currentVal]);
    handleTextInput(q.id, q, prevVal);
    setTimeout(() => {
      essayTextareaRef.current?.focus();
    }, 0);
  };

  const handleRedo = () => {
    const q = takingQuestions[currentQuestionIndex];
    if (!q || redoStack.length === 0) return;
    const nextVal = redoStack[redoStack.length - 1];
    const currentVal = userAnswers[q.id]?.text_response || '';
    if (lnatEssayOneResponseBlocks(q, nextVal)) return;
    setRedoStack(prev => prev.slice(0, -1));
    setUndoStack(prev => [...prev.slice(-40), currentVal]);
    handleTextInput(q.id, q, nextVal);
    setTimeout(() => {
      essayTextareaRef.current?.focus();
    }, 0);
  };

  const handleEssayChange = (val) => {
    const q = takingQuestions[currentQuestionIndex];
    if (!q) return;
    if (lnatEssayOneResponseBlocks(q, val)) return;
    const currentVal = userAnswers[q.id]?.text_response || '';
    if (val !== currentVal) {
      setUndoStack(prev => [...prev.slice(-40), currentVal]);
      setRedoStack([]);
      handleTextInput(q.id, q, val);
    }
  };

  // local stopwatch for untimed practice
  useEffect(() => {
    if (timeRemaining === 0 && (examMode === 'ivori' || examMode === 'pearson')) {
      const interval = setInterval(() => {
        setStopwatchSeconds(prev => prev + 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timeRemaining, examMode]);

  const displayTime = timeRemaining > 0 
    ? (formatTime ? formatTime(timeRemaining) : '00:00')
    : `${String(Math.floor(stopwatchSeconds / 60)).padStart(2, '0')}:${String(stopwatchSeconds % 60).padStart(2, '0')}`;

  const isUrgent = timeRemaining > 0 && timeRemaining <= 300;

  // Writing-task textareas (TARA format-G and stopgap) don't autofocus, but
  // once the student taps in there was previously no way to dismiss the
  // keyboard except submitting or the OS "Done" key — tapping anywhere else
  // on the page (including the question pane's own chrome) did nothing.
  // Blurring here closes the on-screen keyboard, which shrinks the visual
  // viewport back down, which is what naturally restores the Previous/
  // Navigator/Next footer and the writing editor's own toolbar (both gated
  // on the shared keyboardOpen/taraWritingKeyboardOpen flags).
  useEffect(() => {
    const handleClickOutsideWritingPane = (event) => {
      const activeTextarea = essayTextareaRef.current;
      if (!activeTextarea || document.activeElement !== activeTextarea) return;
      // questionPaneRef is attached to the entire content-area container on
      // every writing screen, so "outside the pane" was never true and this
      // blur never fired. Exempt only the editor itself (and its toolbar,
      // whose buttons rely on their own onMouseDown preventDefault to keep
      // the textarea's focus/selection - a document-level mousedown listener
      // fires regardless of that, so without this exemption Cut/Copy/Paste/
      // Undo/Redo would break) via an explicit opt-out attribute instead.
      if (event.target.closest && event.target.closest('[data-writing-keep-focus]')) return;
      activeTextarea.blur();
    };
    // touchstart alongside mousedown: on iOS Safari and some Android
    // WebViews, a tap on a plain background div (exactly what's left once
    // the keyboard-open header/footer collapse away) doesn't reliably
    // synthesize a mousedown/click the way a tap on a native control does.
    document.addEventListener('mousedown', handleClickOutsideWritingPane);
    document.addEventListener('touchstart', handleClickOutsideWritingPane);
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideWritingPane);
      document.removeEventListener('touchstart', handleClickOutsideWritingPane);
    };
  }, []);

  // MathJax Processing
  // mathJaxRunIdRef identifies which effect *run* (not which individual call) is
  // current: it's bumped once when the effect fires, and the immediate/+100ms/
  // +350ms calls it schedules all share that same id, since they're redundant
  // attempts at typesetting the same content rather than separate generations.
  // mathJaxDoneRunIdRef records the id of the run that last completed a typeset
  // successfully.
  //
  // Together these close two gaps:
  // 1. If a newer effect run starts (dependency-array change) while an older
  //    run's call is still awaiting MathJax readiness, the older one bails out
  //    before touching the DOM instead of clearing/retypesetting content the
  //    newer run is already handling. This matters most under React StrictMode
  //    in development, which deliberately double-invokes this effect on mount.
  // 2. Within the *same* run, once one of the three scheduled calls has already
  //    typeset successfully, the other(s) skip redoing typesetClear()+
  //    typesetPromise() on content that hasn't changed - previously every one
  //    of the three calls unconditionally re-cleared and re-rendered, which is
  //    wasted work and a visible (if usually brief) flash.
  const mathJaxRunIdRef = useRef(0);
  const mathJaxDoneRunIdRef = useRef(-1);
  const processMathJax = useCallback(async (runId, extraEl) => {
    if (!window.MathJax) return;
    await waitForMathJax();
    // waitForMathJax()'s "script tag already exists" branch resolves as soon as
    // typesetPromise exists as a function, without waiting for MathJax's own
    // startup.promise the way its other branch does. Wait on it here too (without
    // modifying waitForMathJax itself) so we don't call typesetPromise before
    // MathJax's internal document/output-jax setup has actually finished.
    if (window.MathJax.startup && window.MathJax.startup.promise) {
      try { await window.MathJax.startup.promise; } catch (e) {}
    }
    if (runId !== mathJaxRunIdRef.current && extraEl == null) return; // a newer run has started
    if (mathJaxDoneRunIdRef.current >= runId && extraEl == null) return; // this run already typeset successfully
    if (!window.MathJax || !window.MathJax.typesetPromise) return;
    const nodes = [containerRef.current, extraEl].filter(Boolean);
    if (!nodes.length) return;
    try {
      if (window.MathJax.typesetClear) {
        try { window.MathJax.typesetClear(nodes); } catch (e) {}
      }
      await window.MathJax.typesetPromise(nodes);
      // A formula too wide for its line (a table written in LaTeX) scrolls
      // sideways inside the question instead of running off a phone, and a
      // full stop or closing bracket after a formula stays on its line.
      nodes.forEach(keepPunctuationWithMath);
      nodes.forEach(markWideMath);
      mathJaxDoneRunIdRef.current = runId;
      if (lnatAnnotateEnabledRef.current) {
        setLnatMathJaxEpoch((n) => n + 1);
      }
    } catch (error) {
      console.warn('MathJax typeset notice:', error);
    }
  }, []);

  useEffect(() => {
    const runId = ++mathJaxRunIdRef.current;
    processMathJax(runId);
    const timeout1 = setTimeout(() => processMathJax(runId), 100);
    const timeout2 = setTimeout(() => processMathJax(runId), 350);
    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
    };
    // userAnswers and pearsonColorScheme are intentionally excluded: neither ever
    // changes the question/option/explanation HTML that MathJax needs to process
    // (userAnswers only drives selected-option styling, essay text echo, and
    // progress calculations; pearsonColorScheme only drives an inline color theme
    // object). Including them caused a full typesetClear()+typesetPromise() cycle
    // on every option click / theme change, flashing already-correct math back to
    // raw source before it re-rendered.
  }, [questions, currentQuestionIndex, examMode, isSubmitted, explainQuestionIndex, showExplainModal, reviewScreenOpen, tmuaPaperPhase, processMathJax]);

  const getTheme = () => {
    // The practice workspace follows the site's tokens, so it is dark when the site is.
    const base = {
      ground: 'var(--iv-bg)',
      surface: 'var(--iv-surface)',
      surfaceAlt: 'var(--iv-fill)',
      cardBg: 'var(--iv-surface)',
      cardHover: 'var(--iv-raised)',
      text: 'var(--iv-head)',
      textSoft: 'var(--iv-muted)',
      hairline: 'var(--iv-line)',
      brandBg: 'var(--iv-band)',
      brandText: 'var(--iv-on-band)',
      brandSoft: 'color-mix(in srgb, var(--iv-head) 58%, transparent)',
      brandTrack: 'color-mix(in srgb, var(--iv-head) 12%, transparent)',
      brandRing: 'color-mix(in srgb, var(--iv-head) 25%, transparent)',
    };
    return {
      ...base,
      accentFill: 'var(--iv-btn)',
      accentInk: 'var(--iv-btn)',
      accentText: 'var(--iv-btn-fg)',
      brandAccent: 'var(--iv-btn)',
      selBg: 'var(--iv-raised)',
      selBorder: 'var(--iv-head)',
    };
  };

  const t = getTheme();
  const currentQuestion = takingQuestions[currentQuestionIndex];
  const totalQuestions = takingQuestions.length;
  const flaggedCount = Object.values(flaggedQuestions).filter(Boolean).length;

  const selectOption = (questionId, optionLabel, question) => {
    if (pearsonPlayer && isSubmitted) return;
    if (pearsonPlayer && tmuaPaper1Frozen && question?.tmuaPaper === 1) return;
    if (pearsonPlayer && usesPearsonModules && Number(question?.tmuaPaper) <= frozenModuleCount) return;
    const opts = question.options || question.essay_options || [];
    const selectedOpt = opts.find(o => o.label === optionLabel);
    setUserAnswers(prev => ({
      ...prev,
      [questionId]: {
        label: optionLabel,
        answer_id: selectedOpt ? selectedOpt.db_answer_id : null,
        question_db_id: question.db_id
      }
    }));
  };

  const selectYesNo = (questionId, question, itemId, value) => {
    if (pearsonPlayer && isSubmitted) return;
    if (pearsonPlayer && tmuaPaper1Frozen && question?.tmuaPaper === 1) return;
    if (pearsonPlayer && usesPearsonModules && Number(question?.tmuaPaper) <= frozenModuleCount) return;
    setUserAnswers((prev) => {
      const existing = prev[questionId] || {};
      const yn = { ...(existing.yes_no_answers || {}) };
      yn[String(itemId)] = !!value;
      return {
        ...prev,
        [questionId]: {
          ...existing,
          yes_no_answers: yn,
          question_db_id: question.db_id,
          is_yes_no_answer: true,
        },
      };
    });
  };

  const clearYesNo = (questionId, question, itemId) => {
    if (pearsonPlayer && isSubmitted) return;
    if (pearsonPlayer && tmuaPaper1Frozen && question?.tmuaPaper === 1) return;
    if (pearsonPlayer && usesPearsonModules && Number(question?.tmuaPaper) <= frozenModuleCount) return;
    setUserAnswers((prev) => {
      const existing = prev[questionId];
      if (!existing || !existing.yes_no_answers) return prev;
      const yn = { ...existing.yes_no_answers };
      delete yn[String(itemId)];
      return { ...prev, [questionId]: { ...existing, yes_no_answers: yn } };
    });
  };

  // Replicates the live UCAT Pearson VUE exam: returning to a *partially*
  // answered yes/no group from the end-of-section review table clears it.
  // Next/Previous does not trigger this - see onReviewRow below.
  const clearPartialYesNoGroup = (q) => {
    if (!isYesNoQuestion(q)) return;
    const ans = userAnswers[q.id];
    const yn = (ans && ans.yes_no_answers) || {};
    const items = q.yes_no_items || [];
    const answeredCount = items.filter((it) => String(it.id) in yn).length;
    if (answeredCount === 0 || answeredCount === items.length) return;
    setUserAnswers((prev) => {
      const existing = prev[q.id] || {};
      return { ...prev, [q.id]: { ...existing, yes_no_answers: {} } };
    });
  };

  const selectMostLeast = (questionId, question, slot, optionId) => {
    if (pearsonPlayer && isSubmitted) return;
    if (pearsonPlayer && tmuaPaper1Frozen && question?.tmuaPaper === 1) return;
    if (pearsonPlayer && usesPearsonModules && Number(question?.tmuaPaper) <= frozenModuleCount) return;
    setUserAnswers((prev) => {
      const existing = prev[questionId] || {};
      const ml = { ...(existing.most_least_answers || {}) };
      const id = String(optionId);
      if (slot === 'most') {
        if (ml.least_option_id && String(ml.least_option_id) === id) {
          ml.least_option_id = null;
        }
        ml.most_option_id = id;
      } else {
        if (ml.most_option_id && String(ml.most_option_id) === id) {
          ml.most_option_id = null;
        }
        ml.least_option_id = id;
      }
      return {
        ...prev,
        [questionId]: {
          ...existing,
          most_least_answers: ml,
          question_db_id: question.db_id,
          is_most_least_answer: true,
        },
      };
    });
  };

  const clearMostLeast = (questionId, question, slot) => {
    if (pearsonPlayer && isSubmitted) return;
    if (pearsonPlayer && tmuaPaper1Frozen && question?.tmuaPaper === 1) return;
    if (pearsonPlayer && usesPearsonModules && Number(question?.tmuaPaper) <= frozenModuleCount) return;
    setUserAnswers((prev) => {
      const existing = prev[questionId] || {};
      const ml = { ...(existing.most_least_answers || {}) };
      if (slot === 'most') ml.most_option_id = null;
      else ml.least_option_id = null;
      return {
        ...prev,
        [questionId]: {
          ...existing,
          most_least_answers: ml,
          question_db_id: question.db_id,
          is_most_least_answer: true,
        },
      };
    });
  };

  const handleTextInput = (questionId, question, text) => {
    setUserAnswers(prev => {
      const existing = prev[questionId] || {};
      const label = existing.label || question.writingPromptLabel;
      const matchedOpt = (question.essay_options || question.options || []).find(
        (opt) => opt.label === label || String(opt.id) === String(existing.essay_option_id)
      );
      const drafts = { ...(existing.prompt_drafts || {}) };
      if (label) drafts[label] = text;
      return {
        ...prev,
        [questionId]: {
          ...existing,
          ...(label ? { label } : {}),
          text_response: text,
          prompt_drafts: drafts,
          question_db_id: question.db_id,
          is_text_answer: true,
          essay_option_id: existing.essay_option_id
            || matchedOpt?.id
            || matchedOpt?.db_id
            || matchedOpt?.db_answer_id
            || matchedOpt?.external_option_id
            || null,
        }
      };
    });
  };

  /** Switch among Section B essay prompts while preserving per-prompt drafts. */
  const goToEssayPrompt = (targetIndex) => {
    // Only restore focus if the student already had it here (e.g. mid-edit
    // and used a prompt tab/arrow key to switch) - a deliberately blurred
    // textarea should stay blurred, matching the click-outside-to-blur fix.
    const hadFocus = document.activeElement === essayTextareaRef.current;
    const q = takingQuestions[currentQuestionIndex];
    if (!isEssayQuestion(q) && !(Array.isArray(q?.essay_options) && q.essay_options.length > 0)) return;
    const promptsResolved = resolveEssayPrompts(q);
    const prompts =
      promptsResolved.length > 0
        ? promptsResolved
        : (q.essay_options || []).map((opt, idx) => ({
            ...opt,
            label: opt.label || String.fromCharCode(65 + idx),
            text: opt.text || opt.question_text || opt.prompt_text || '',
          }));
    if (targetIndex < 0 || targetIndex >= prompts.length) return;

    const nextPrompt = prompts[targetIndex];
    const nextLabel = nextPrompt.label || String.fromCharCode(65 + targetIndex);
    const opts = q.essay_options || q.options || [];
    const selectedOpt = opts.find((o) => o.label === nextLabel) || nextPrompt;

    setUserAnswers((prev) => {
      const existing = prev[q.id] || {};
      const currentLabel = existing.label || prompts[0]?.label || 'A';
      const drafts = { ...(existing.prompt_drafts || {}) };
      drafts[currentLabel] = existing.text_response || '';
      const nextText = drafts[nextLabel] || '';
      return {
        ...prev,
        [q.id]: {
          ...existing,
          label: nextLabel,
          answer_id: selectedOpt ? selectedOpt.db_answer_id : null,
          text_response: nextText,
          prompt_drafts: drafts,
          question_db_id: q.db_id,
          is_text_answer: true,
        },
      };
    });
    setUndoStack([]);
    setRedoStack([]);
    if (hadFocus) setTimeout(() => essayTextareaRef.current?.focus(), 0);
  };

  const handleFlag = (questionId, question) => {
    if (pearsonPlayer && isSubmitted) return;
    if (pearsonPlayer && tmuaPaper1Frozen && question?.tmuaPaper === 1) return;
    if (pearsonPlayer && usesPearsonModules && Number(question?.tmuaPaper) <= frozenModuleCount) return;
    setFlaggedQuestions(prev => ({ ...prev, [questionId]: !prev[questionId] }));
  };

  const isPaneAtBottom = (el) => {
    if (!el) return true;
    return el.scrollHeight - el.clientHeight - el.scrollTop <= 4;
  };

  // A pane is "fully viewed" once the user has scrolled it to the bottom at
  // least once for this question — scrolling back up afterward should not
  // re-block Next, so this checks (and latches) a persistent flag rather than
  // the live scroll position.
  const isPaneFullyViewed = (el, seenBottomRef) => {
    if (!el) return true;
    if (seenBottomRef.current) return true;
    if (isPaneAtBottom(el)) {
      seenBottomRef.current = true;
      return true;
    }
    return false;
  };

  const handlePaneScroll = (seenBottomRef) => (e) => {
    if (!seenBottomRef.current && isPaneAtBottom(e.currentTarget)) {
      seenBottomRef.current = true;
    }
  };

  const hasViewedEntireScreen = () => (
    isPaneFullyViewed(questionPaneRef.current, questionPaneSeenBottomRef)
    && isPaneFullyViewed(passagePaneRef.current, passagePaneSeenBottomRef)
  );

  // Mobile split-pane divider drag: computes a percentage against the
  // available height (container height minus the divider's own fixed
  // footprint), then snaps to collapsed-passage/collapsed-question past the
  // 15%/85% thresholds, or clamps to the 120px-equivalent minimum otherwise.
  const handleSplitDividerPointerDown = (e) => {
    e.preventDefault();
    const container = splitBodyRef.current;
    if (!container) return;
    const containerHeight = container.getBoundingClientRect().height;
    const availableHeight = Math.max(1, containerHeight - SPLIT_DIVIDER_HEIGHT);
    const minRatio = 120 / availableHeight;
    const maxRatio = 1 - minRatio;

    const applyRatioFromClientY = (clientY) => {
      const rect = container.getBoundingClientRect();
      const offsetY = clientY - rect.top;
      const rawRatio = offsetY / availableHeight;
      if (rawRatio >= 0.85) {
        setSplitPaneCollapsed('question');
        return;
      }
      if (rawRatio <= 0.15) {
        setSplitPaneCollapsed('passage');
        return;
      }
      setSplitPaneCollapsed('none');
      setSplitPaneRatio(Math.min(maxRatio, Math.max(minRatio, rawRatio)));
    };

    const onMove = (ev) => {
      const clientY = ev.touches ? ev.touches[0].clientY : ev.clientY;
      applyRatioFromClientY(clientY);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
  };

  const handleSplitPaneLabelTap = (pane) => {
    if (splitPaneCollapsed === pane) setSplitPaneCollapsed('none');
  };

  // Question/option/passage HTML is injected via dangerouslySetInnerHTML, and
  // papers carry artwork two ways: an <img> baked into that HTML, or a bare
  // URL in a sibling image_url field (rendered separately by PaperImage). This
  // delegates a tap on the HTML-embedded kind to the same lightbox PaperImage
  // uses, stopping propagation so it doesn't also select the option/toggle a
  // pane underneath; anything else (text selection, etc.) is a no-op.
  //
  // Wrapped in useCallback because it's passed as MathContent's onClick prop,
  // and MathContent is React.memo'd specifically so MathJax's typeset DOM
  // (injected into its dangerouslySetInnerHTML div after mount) survives
  // unrelated parent re-renders — an unstable onClick reference here defeats
  // that memo every render, forcing MathContent to reset back to raw
  // (un-typeset) HTML, which is exactly what broke LaTeX rendering before
  // this was memoized (see the style-object comment a few lines below).
  const handleExhibitTextClick = useCallback((e) => {
    const img = e.target.closest ? e.target.closest('img') : null;
    if (img && openLightbox) {
      // Some option rows are a <label> wrapping the radio/checkbox input, where
      // a native click anywhere inside activates the input regardless of
      // stopPropagation — preventDefault is what actually blocks that.
      e.preventDefault();
      e.stopPropagation();
      openLightbox({ url: img.currentSrc || img.src, alt: img.alt || '' });
    }
  }, [openLightbox]);

  // Same as handleExhibitTextClick, but composed with a pane's existing
  // tap-to-collapse behavior: an image tap opens the lightbox instead of
  // toggling the pane. Also useCallback'd for the same MathContent-memo reason.
  const handlePassagePaneClick = useCallback((e) => {
    const img = e.target.closest ? e.target.closest('img') : null;
    if (img && openLightbox) {
      openLightbox({ url: img.currentSrc || img.src, alt: img.alt || '' });
      return;
    }
    setSplitPaneCollapsed((prev) => (prev === 'passage' ? 'none' : prev));
  }, [openLightbox]);

  // Same as handlePassagePaneClick, for the question pane's own tap-to-collapse.
  const handleQuestionPaneClick = useCallback((e) => {
    const img = e.target.closest ? e.target.closest('img') : null;
    if (img && openLightbox) {
      openLightbox({ url: img.currentSrc || img.src, alt: img.alt || '' });
      return;
    }
    setSplitPaneCollapsed((prev) => (prev === 'question' ? 'none' : prev));
  }, [openLightbox]);

  // Below 768px, --ivori-split-pane-height drives each pane's height (see
  // the .ivori-exam-passage-pane/.ivori-exam-question-pane CSS rule); above
  // 768px these vars are simply unused by the (unmodified) desktop rules.
  // The collapsed pane is SPLIT_COLLAPSED_BAR_HEIGHT (44px, its own restore
  // touch target) while the divider itself stays SPLIT_DIVIDER_HEIGHT (24px)
  // — the two are no longer equal, so the expanded sibling's calc() must
  // subtract each separately rather than doubling one of them.
  const splitPanePassageStyle = splitPaneCollapsed === 'passage'
    ? { '--ivori-split-pane-height': `${SPLIT_COLLAPSED_BAR_HEIGHT}px` }
    : splitPaneCollapsed === 'question'
      ? { '--ivori-split-pane-height': `calc(100% - ${SPLIT_DIVIDER_HEIGHT}px - ${SPLIT_COLLAPSED_BAR_HEIGHT}px)` }
      : { '--ivori-split-pane-height': `${splitPaneRatio * 100}%` };
  const splitPaneQuestionStyle = splitPaneCollapsed === 'question'
    ? { '--ivori-split-pane-height': `${SPLIT_COLLAPSED_BAR_HEIGHT}px` }
    : splitPaneCollapsed === 'passage'
      ? { '--ivori-split-pane-height': `calc(100% - ${SPLIT_DIVIDER_HEIGHT}px - ${SPLIT_COLLAPSED_BAR_HEIGHT}px)` }
      : { '--ivori-split-pane-height': `${(1 - splitPaneRatio) * 100}%` };

  const sittingQuestions = pearsonPlayer
    ? (usesPearsonModules
      ? getPearsonSittingQuestions(pearsonModules)
      : getTmuaSittingQuestions(paper1Qs, paper2Qs, tmuaPaper2Status))
    : questions;

  const siblingExpected = tmuaPaper2Status === 'loading' || tmuaPaper2Status === 'ready' || paper2Qs.length > 0;

  const applyLnatHighlightFromSelection = () => {
    const sel = readLnatSelection();
    if (!sel) return;
    const q = takingQuestions[currentQuestionIndex];
    if (!q) return;
    const id = String(q.id);
    setLnatAnnotationsByItemId((prev) => ({
      ...prev,
      [id]: applyHighlightToItem(prev[id], { ...sel, color: lnatActiveHighlightColor }),
    }));
  };

  const applyLnatStrikethroughFromSelection = () => {
    const sel = readLnatSelection();
    if (!sel) return;
    const q = takingQuestions[currentQuestionIndex];
    if (!q) return;
    const id = String(q.id);
    setLnatAnnotationsByItemId((prev) => ({
      ...prev,
      [id]: toggleStrikeOnItem(prev[id], sel),
    }));
  };

  const removeLnatHighlightFromSelection = () => {
    const sel = readLnatSelection();
    if (!sel) return;
    const q = takingQuestions[currentQuestionIndex];
    if (!q) return;
    const id = String(q.id);
    setLnatAnnotationsByItemId((prev) => ({
      ...prev,
      [id]: removeHighlightFromItem(prev[id], sel),
    }));
  };

  const lockTmuaSitting = () => {
    if (shouldPauseTopicDrillOnEndExam({
      topicDrill: isTopicDrill,
      isSubmitted,
      phase: tmuaPaperPhase,
      allAnswered: topicAllAnsweredRef.current,
    })) {
      setShowEndExamModal(false);
      onBack({
        resume_phase: tmuaPaperPhase,
        resume_question_index: currentQuestionIndex,
        time_remaining_seconds: timeRemaining,
        attempt_mode: tmuaModeChoice,
      });
      return;
    }
    if (isSubmitted || tmuaPaperPhase === 'post_exam') {
      setShowEndExamModal(false);
      Promise.resolve(typeof onSubmit === 'function' ? onSubmit() : null)
        .finally(() => {
          if (typeof onBack === 'function') onBack({ skipPause: true });
        });
      return;
    }
    const next = usesPearsonModules
      ? nextPearsonSittingPhase(tmuaPaperPhase, 'endExamYes', {
        moduleCount: pearsonLiveModuleCount(pearsonModules, { isUcatExam }),
        currentIndex: lastModuleIndexRef.current,
        nextStatus: (pearsonModules[lastModuleIndexRef.current + 1]?.status) || 'idle',
        siblingExpected: lastModuleIndexRef.current < ((pearsonModules || []).length || 3) - 1
          || pearsonModules[lastModuleIndexRef.current + 1]?.status === 'loading',
      })
      : nextTmuaPearsonPhase(tmuaPaperPhase, 'endExamYes', {
        hasP2: tmuaPaper2Status === 'ready' && paper2Qs.length > 0,
        p2Status: tmuaPaper2Status,
        siblingExpected,
      });
    setShowEndExamModal(false);
    setShowNavigatorModal(false);
    setShowEntireScreenModal(false);
    setShowEndReviewConfirm(false);
    setReviewScreenOpen(false);
    setShowSolutionModal(false);
    if (next === 'awaiting_p2' || next === 'awaiting_next') {
      setAwaitingP2Reason('lock');
      setTmuaPaperPhase(next);
      return;
    }
    if (shouldClearLnatAnnotations({ event: 'lockSitting', nextPhase: 'post_exam' })) {
      setLnatAnnotationsByItemId({});
      setLnatActiveHighlightColor(DEFAULT_LNAT_HIGHLIGHT_COLOR);
    }
    setIsSubmitted(true);
    setTmuaPaperPhase('post_exam');
    if (typeof onCompleteAttempts === 'function') {
      onCompleteAttempts();
    }
  };

  const confirmTmuaEndReview = () => {
    setShowEndReviewConfirm(false);
    const reviewIdx = parsePearsonReviewIndex(tmuaPaperPhase);
    const moduleCount = usesPearsonModules
      ? pearsonLiveModuleCount(pearsonModules, { isUcatExam })
      : 2;
    if (reviewIdx === moduleCount - 1) {
      lockTmuaSitting();
      return;
    }
    if (usesPearsonModules) {
      setFrozenModuleCount((n) => Math.max(n, (reviewIdx ?? 0) + 1));
      const nextMod = pearsonModules[(reviewIdx ?? 0) + 1];
      const nextStatus = nextMod?.status || 'idle';
      const next = nextPearsonSittingPhase(tmuaPaperPhase, 'endReviewConfirm', {
        moduleCount,
        currentIndex: reviewIdx ?? 0,
        nextStatus,
        hasNext: Boolean(nextMod && (nextStatus === 'ready' || nextStatus === 'loading' || (nextMod.questions || []).length)),
        siblingExpected: (reviewIdx ?? 0) < moduleCount - 1,
      });
      if (next === 'awaiting_next') setAwaitingP2Reason('continue');
      if (next === 'post_exam') {
        setIsSubmitted(true);
        if (shouldClearLnatAnnotations({ event: 'endReviewConfirm', nextPhase: next })) {
          setLnatAnnotationsByItemId({});
          setLnatActiveHighlightColor(DEFAULT_LNAT_HIGHLIGHT_COLOR);
        }
      }
      setReviewScreenOpen(false);
      setTmuaPaperPhase(next);
      if (next === 'post_exam' && typeof onCompleteAttempts === 'function') onCompleteAttempts();
      return;
    }
    setTmuaPaper1Frozen(true);
    const next = nextTmuaPearsonPhase('p1_review', 'endReviewConfirm', {
      hasP2: tmuaPaper2Status === 'ready' && paper2Qs.length > 0,
      p2Status: tmuaPaper2Status,
      siblingExpected,
    });
    if (next === 'awaiting_p2') setAwaitingP2Reason('continue');
    if (next === 'post_exam') {
      setIsSubmitted(true);
      if (shouldClearLnatAnnotations({ event: 'endReviewConfirm', nextPhase: next })) {
        setLnatAnnotationsByItemId({});
        setLnatActiveHighlightColor(DEFAULT_LNAT_HIGHLIGHT_COLOR);
      }
    }
    setReviewScreenOpen(false);
    setTmuaPaperPhase(next);
    if (next === 'post_exam' && typeof onCompleteAttempts === 'function') onCompleteAttempts();
  };

  const requestEndReview = () => {
    const lnatEssayList = pickLnatEssayReviewList({
      isLnatExam,
      isTopicDrill,
      tmuaPaperPhase,
      paper1Qs,
      paper2Qs,
      takingQuestions,
    });
    if (isLnatEssayReviewPhase({ isLnatExam, isTopicDrill, tmuaPaperPhase })
      && (isLnatExam || isTaraTsaSitting)
      && !lnatSectionHasEssayText(lnatEssayList, userAnswers)) {
      setResponseRequiredMessage(isTaraTsaSitting
        ? 'You must write an essay before ending review.'
        : lnatEssayEndReviewMessage((lnatEssayList || []).length));
      setShowResponseRequired(true);
      return;
    }
    setShowEndReviewConfirm(true);
  };

  const handleNext = () => {
    if ((isTmuaExam || pearsonPlayer) && !hasViewedEntireScreen()) {
      setShowEntireScreenModal(true);
      return;
    }
    // Format-G TARA writing prompts are separate takingQuestions entries
    // (see expandTaraWritingPearsonItems), so they already navigate correctly
    // via the ordinary setCurrentQuestionIndex path below. This essay-choice
    // relabeling branch is only for a single essay_choice_group question with
    // 3 essay_options answered in place - applying it to format-G's already-
    // separate items races goToEssayPrompt's per-item label bookkeeping
    // against the real navigation index, which is why it silently took
    // 2-3 presses to leave the middle prompt.
    if (pearsonPlayer && isTaraExam && !isTaraBmatSitting && !isTaraWritingFormatGActive && pearsonModules[pearsonModuleIndex]?.moduleKey === 'writing') {
      const q = takingQuestions[currentQuestionIndex];
      const prompts = resolveEssayPrompts(q);
      const label = q ? userAnswers[q.id]?.label : null;
      const idx = prompts.findIndex((p) => p.label === label);
      const currentIdx = idx >= 0 ? idx : 0;
      if (currentIdx < prompts.length - 1) {
        goToEssayPrompt(currentIdx + 1);
        return;
      }
    }
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else if (pearsonPlayer) {
      const next = usesPearsonModules
        ? nextPearsonSittingPhase(tmuaPaperPhase, 'lastNext', {
          moduleCount: pearsonLiveModuleCount(pearsonModules, { isUcatExam }),
        })
        : nextTmuaPearsonPhase(tmuaPaperPhase, 'lastNext');
      setTmuaPaperPhase(next);
      setReviewTab('all');
      setReviewScreenOpen(true);
    } else {
      setReviewScreenOpen(true);
    }
  };

  const handlePrev = () => {
    // See the matching comment in handleNext: format-G TARA writing prompts
    // are separate takingQuestions entries and already navigate correctly
    // via setCurrentQuestionIndex below.
    if (pearsonPlayer && isTaraExam && !isTaraBmatSitting && !isTaraWritingFormatGActive && pearsonModules[pearsonModuleIndex]?.moduleKey === 'writing') {
      const q = takingQuestions[currentQuestionIndex];
      const prompts = resolveEssayPrompts(q);
      const label = q ? userAnswers[q.id]?.label : null;
      const idx = prompts.findIndex((p) => p.label === label);
      const currentIdx = idx >= 0 ? idx : 0;
      if (currentIdx > 0) {
        goToEssayPrompt(currentIdx - 1);
        return;
      }
    }
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const examKeyDownRef = useRef(null);

  examKeyDownRef.current = (event) => {
    // Exam shortcuts (n/e/f, digits, arrows) must not act behind an open
    // lightbox. Read via ref (not the memoized context value) so this stays
    // current at keydown time without needing the context/effect deps to change.
    if (lightboxOpenRef && lightboxOpenRef.current) return;
    const q = takingQuestions[currentQuestionIndex];
    if (pearsonPlayer && (
      parsePearsonInstructionsIndex(tmuaPaperPhase) != null
      || isPearsonAwaitingPhase(tmuaPaperPhase)
    )) {
      return;
    }
    const pearsonIsMc =
      Boolean(q) &&
      q.multiple_choice !== false &&
      Array.isArray(q.options) &&
      q.options.length > 1;

    const reviewIsOpen = parsePearsonReviewIndex(tmuaPaperPhase) != null || tmuaPaperPhase === 'post_exam' || isSubmitted;
    const reviewIdxForKeys = parsePearsonReviewIndex(tmuaPaperPhase);
    const nextModForKeys = usesPearsonModules && reviewIdxForKeys != null ? pearsonModules[reviewIdxForKeys + 1] : null;
    const hasLaterModuleForKeys = Boolean(nextModForKeys && (
      nextModForKeys.status === 'ready'
      || nextModForKeys.status === 'loading'
      || (nextModForKeys.questions || []).length
    ));
    const endReviewIsShown = usesPearsonModules
      ? (reviewIdxForKeys != null && reviewIdxForKeys < Math.max((pearsonModules || []).length, 1) - 1 && hasLaterModuleForKeys)
      : false;

    const overlaySnapshot = {
      examMode,
      isSubmitted,
      hasCurrentQuestion: Boolean(q),
      currentQuestionIndex,
      totalQuestions,
      optionCount: Array.isArray(q?.options) ? q.options.length : 0,
      isMcq: isMcqQuestion(q),
      isEssay: isEssayQuestion(q),
      isFreeText: isFreeTextPractice(q),
      pearsonLiveShowsTextarea: examMode === 'pearson' && Boolean(q) && !pearsonIsMc,
      lightboxOpen: Boolean(lightboxOpenRef && lightboxOpenRef.current),
      reviewScreenOpen,
      showExplainModalVisible: Boolean(showExplainModal) && examMode === 'ivori',
      showEntireScreenModalVisible: Boolean(showEntireScreenModal) && (pearsonPlayer || isTmuaExam),
      showEndExamModalVisible: Boolean(showEndExamModal),
      showNavigatorModalVisible: Boolean(showNavigatorModal),
      showSolutionModalVisible: Boolean(showSolutionModal),
      showEndReviewConfirmVisible: Boolean(showEndReviewConfirm),
      showResponseRequiredVisible: Boolean(showResponseRequired),
      isTmuaPearsonPlayer: pearsonPlayer,
      isUcatPearsonPlayer: isUcatExam,
      flagAvailable: isUcatExam && !isSubmitted && parsePearsonItemsIndex(tmuaPaperPhase) != null
        && !showNavigatorModal && !showEndExamModal && !showEndReviewConfirm && !ucatHelpOpen,
      calculatorActive: Boolean(
        isUcatExam
        && ucatCalculatorOpen
        && ucatCalculatorEnabled
        && typeof document !== 'undefined'
        && document.activeElement?.closest?.('[data-ucat-calculator]')
      ),
      endExamAvailable: pearsonPlayer && !(
        parsePearsonReviewIndex(tmuaPaperPhase) != null
        && parsePearsonReviewIndex(tmuaPaperPhase) < (usesPearsonModules ? Math.max((pearsonModules || []).length, 1) : 2) - 1
        && (usesPearsonModules
          ? Boolean(pearsonModules[parsePearsonReviewIndex(tmuaPaperPhase) + 1]
            && ['ready', 'loading'].includes(pearsonModules[parsePearsonReviewIndex(tmuaPaperPhase) + 1]?.status))
          : (tmuaPaper2Status === 'loading' || (tmuaPaper2Status === 'ready' && paper2Qs.length > 0)))
      ),
      nextAvailable: pearsonPlayer && !isSubmitted && parsePearsonItemsIndex(tmuaPaperPhase) != null,
      prevAvailable: isUcatExam && !isSubmitted && parsePearsonItemsIndex(tmuaPaperPhase) != null
        && currentQuestionIndex > 0,
      calculatorToggleAvailable: isUcatExam && !isSubmitted && parsePearsonItemsIndex(tmuaPaperPhase) != null,
      lnatAnnotateAvailable: Boolean(
        pearsonPlayer && isLnatExam
        && !isSubmitted
        && parsePearsonItemsIndex(tmuaPaperPhase) != null
        && !reviewScreenOpen
        && !showEndExamModal && !showNavigatorModal && !showEndReviewConfirm
        && !showResponseRequired && !showEntireScreenModal && !showSolutionModal
      ),
      reviewTabsAvailable: isUcatExam && Boolean(reviewIsOpen),
      returnToReviewAvailable: isUcatExam && !isSubmitted && !reviewIsOpen
        && parsePearsonItemsIndex(tmuaPaperPhase) != null,
      endReviewAvailable: isUcatExam && Boolean(reviewIsOpen) && !isSubmitted && endReviewIsShown,
    };

    if (pearsonPlayer) {
      const access = resolveTmuaPearsonAccessKey(event, overlaySnapshot);
      if (access.type !== 'ignore') {
        if (access.preventDefault) event.preventDefault();
        if (access.type === 'next') handleNext();
        if (access.type === 'endExam') setShowEndExamModal(true);
        if (access.type === 'confirmYes') {
          if (showEndReviewConfirm) {
            confirmTmuaEndReview();
          } else if (showEndExamModal) {
            lockTmuaSitting();
          }
        }
        if (access.type === 'confirmNo') {
          setShowEndExamModal(false);
          setShowEndReviewConfirm(false);
        }
        if (access.type === 'dismissOk') {
          setShowEntireScreenModal(false);
          setShowResponseRequired(false);
        }
        if (access.type === 'instructions') setShowPostExamInstructions(true);
        if (access.type === 'flag' && currentQuestion) {
          handleFlag(currentQuestion.id, currentQuestion);
          setUcatCalculatorEnabled(false);
        }
        if (access.type === 'prev') handlePrev();
        if (access.type === 'openCalculator') {
          setUcatCalculatorOpen(true);
          setUcatCalculatorEnabled(true);
        }
        if (access.type === 'reviewFlagged') setReviewTab('flagged');
        if (access.type === 'reviewIncomplete') setReviewTab('incomplete');
        if (access.type === 'reviewAll') setReviewTab('all');
        if (access.type === 'returnToReview') {
          const itemsIdx = parsePearsonItemsIndex(tmuaPaperPhase);
          if (itemsIdx != null) {
            setReviewTab('all');
            setReviewScreenOpen(true);
            setTmuaPaperPhase(`m${itemsIdx}_review`);
          }
        }
        if (access.type === 'endReview') requestEndReview();
        if (access.type === 'highlight') applyLnatHighlightFromSelection();
        if (access.type === 'strikethrough') applyLnatStrikethroughFromSelection();
        return;
      }
    }

    const decision = resolveExamKeyboardAction(event, overlaySnapshot);

    if (decision.preventDefault) event.preventDefault();

    if (decision.type === 'prev') {
      handlePrev();
      return;
    }
    if (decision.type === 'next') {
      handleNext();
      return;
    }
    if (decision.type === 'selectOption') {
      const option = q?.options?.[decision.optionIndex];
      if (!option) return;
      selectOption(q.id, option.label, q);
    }
  };

  useEffect(() => {
    const onKeyDown = (event) => {
      if (examKeyDownRef.current) examKeyDownRef.current(event);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Shared by getUnansweredQuestions (submit validation) and the Navigator
  // popup's "Complete" status, so the two can never drift apart.
  const isQuestionAnswered = (q) => {
    const ans = userAnswers[q.id];
    if (isMcqQuestion(q)) return Boolean(ans && ans.label);
    if (isYesNoQuestion(q)) {
      const yn = (ans && ans.yes_no_answers) || {};
      return (q.yes_no_items || []).every((it) => String(it.id) in yn);
    }
    if (isMostLeastQuestion(q)) {
      const ml = (ans && ans.most_least_answers) || {};
      return Boolean(ml.most_option_id && ml.least_option_id);
    }
    if (isEssayChoiceGroup(q) || q.pearsonWritingItem) {
      // Format G: answer one of three prompts. The other two are not incomplete.
      if (isTaraWritingFormatGActive && taraWritingModuleItems.length) {
        return hasAnsweredOneTaraWritingPrompt(userAnswers, taraWritingModuleItems);
      }
      const hasText = Boolean(ans && ans.text_response && ans.text_response.trim());
      if (pearsonPlayer) return hasText;
      return Boolean(hasText && ans.label);
    }
    return Boolean(ans && ans.text_response && ans.text_response.trim());
  };

  const topicDrillQuestions = !isTopicDrill
    ? []
    : (usesPearsonModules
      ? (pearsonModules || []).flatMap((mod) => {
        const items = Array.isArray(mod?.questions) ? mod.questions : [];
        if (mod?.moduleKey === 'writing' && isTaraWritingFormatG(items)) {
          return expandTaraWritingPearsonItems(items, mod.paperId);
        }
        return items;
      })
      : [...paper1Qs, ...paper2Qs]);
  const topicAllAnswered = topicDrillQuestions.length > 0
    && topicDrillQuestions.every((q) => isQuestionAnswered(q));
  topicAllAnsweredRef.current = topicAllAnswered;
  const endExamWillPause = shouldPauseTopicDrillOnEndExam({
    topicDrill: isTopicDrill,
    isSubmitted,
    phase: tmuaPaperPhase,
    allAnswered: topicAllAnswered,
  });

  const getUnansweredQuestions = () => {
    const unanswered = [];
    questions.forEach((q, idx) => {
      if (!isQuestionAnswered(q)) unanswered.push(idx);
    });
    return unanswered;
  };

  const handleReviewSubmit = () => {
    setIsSubmitted(true);
  };

  // Stats calculation for the review page
  const getStats = (list = questions) => {
    const rows = Array.isArray(list) ? list : [];
    let correct = 0;
    rows.forEach(q => {
      const ans = userAnswers[q.id];
      if (isMcqQuestion(q)) {
        if (ans && ans.label === q.correct_answer) correct++;
      } else if (isYesNoQuestion(q)) {
        const yn = (ans && ans.yes_no_answers) || {};
        const complete = (q.yes_no_items || []).every((it) => String(it.id) in yn);
        const allRight =
          complete &&
          (q.yes_no_items || []).every((it) => yn[String(it.id)] === !!it.correct_yes);
        if (allRight) correct++;
      } else if (isMostLeastQuestion(q)) {
        const ml = (ans && ans.most_least_answers) || {};
        const mostOpt = (q.most_least_options || []).find((o) => o.is_correct_most);
        const leastOpt = (q.most_least_options || []).find((o) => o.is_correct_least);
        if (
          mostOpt &&
          leastOpt &&
          String(ml.most_option_id) === String(mostOpt.id) &&
          String(ml.least_option_id) === String(leastOpt.id)
        ) {
          correct++;
        }
      } else {
        if (ans && ans.text_response && ans.text_response.trim().length > 0) {
          correct++;
        }
      }
    });
    const total = rows.length;
    const pct = total > 0 ? Math.round((correct / total) * 100) : 0;
    return {
      correct,
      total,
      pct: `${pct}%`,
      barCorrect: `${pct}%`,
      barIncorrect: `${100 - pct}%`
    };
  };

  const stats = getStats();

  const [aiEssayFeedback, setAiEssayFeedback] = useState({});
  const [gradingEssayId, setGradingEssayId] = useState(null);

  const handleExplainOpen = async (index) => {
    setExplainQuestionIndex(index);
    setShowExplainModal(true);

    const q = questions[index];
    if (q) {
      const isMC = q.multiple_choice !== false && q.options && q.options.length > 1;
      const ans = userAnswers[q.id];
      if (!isMC && ans && ans.text_response && ans.text_response.trim().length > 0 && !aiEssayFeedback[q.id]) {
        try {
          setGradingEssayId(q.id);
          const promptText = isFreeTextPractice(q)
            ? (stripHtmlToText(q.question) || 'Medical Interview Question')
            : (getSelectedEssayPromptText(q, ans) || (isTaraExamContext(examName, q, activeQuizSource) ? 'TARA Writing Task Prompt' : 'LNAT Section B Essay Prompt'));
          const result = await quizGeneratorAPI.markEssay(ans.text_response, promptText);
          if (result && result.success && result.feedback) {
            setAiEssayFeedback(prev => ({
              ...prev,
              [q.id]: result.feedback
            }));
          }
        } catch (err) {
          console.error('Error grading essay with AI:', err);
        } finally {
          setGradingEssayId(null);
        }
      }
    }
  };

  // Render the Explanation & AI Feedback Modal across all quiz views
  const renderExplainModal = () => {
    if (!showExplainModal || explainQuestionIndex === null || !questions[explainQuestionIndex]) return null;
    const q = questions[explainQuestionIndex];
    return (
      <div style={{ display: 'flex', position: 'fixed', inset: 0, zIndex: 99999, background: 'color-mix(in srgb, var(--iv-band) 65%, transparent)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{ width: '780px', maxWidth: '94vw', maxHeight: '88vh', background: 'var(--iv-surface)', border: '1px solid var(--iv-line)', borderRadius: '20px', boxShadow: '0 24px 60px color-mix(in srgb, var(--iv-shadow) 25%, transparent)', display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: 'var(--iv-font-body)' }}>
          
          {/* Modal Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', background: 'var(--iv-band)', color: 'var(--iv-on-band)', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.02em', color: 'var(--iv-on-band)' }}>
                Explanation & AI Reasoning — Question {explainQuestionIndex + 1}
              </span>
            </div>
            <span onClick={() => setShowExplainModal(false)} style={{ display: 'flex', cursor: 'pointer', opacity: 0.85, padding: '4px', transition: 'opacity 0.15s ease' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--iv-on-band)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="6" y1="6" x2="18" y2="18"></line>
                <line x1="18" y1="6" x2="6" y2="18"></line>
              </svg>
            </span>
          </div>

          {/* Modal Scrollable Body */}
          <div style={{ padding: '24px 28px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
            
            {/* 1. Passage / Stimulus Text (if available) */}
            {q.exhibit_text && (
              <div style={{ background: 'var(--iv-bg)', border: '1px solid var(--iv-line)', borderRadius: '14px', padding: '18px 22px' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', color: 'var(--iv-muted)', textTransform: 'uppercase', marginBottom: '10px' }}>
                  Reading Passage / Stimulus
                </div>
                <MathContent
                  style={EXPLAIN_PASSAGE_STYLE}
                  className="mathjax-process tex2jax_process exhibit-clickable-html"
                  onClick={handleExhibitTextClick}
                  html={prepareSittingHtml(q.exhibit_text)}
                />
              </div>
            )}

            {/* 2. Question Prompt */}
            <div style={{ background: 'var(--iv-surface)', border: '1px solid var(--iv-line)', borderRadius: '14px', padding: '16px 20px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', color: 'var(--iv-muted)', textTransform: 'uppercase', marginBottom: '6px' }}>
                Question {explainQuestionIndex + 1}
              </div>
              <MathContent
                style={EXPLAIN_QUESTION_STYLE}
                className="mathjax-process tex2jax_process"
                html={prepareSittingHtml(q.question)}
              />
            </div>

            {/* 3. Options List (MCQ / LNAT only — hide model-answer stubs on free-text banks) */}
            {q.options && q.options.length > 0 && !isFreeTextPractice(q) && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', color: 'var(--iv-muted)', textTransform: 'uppercase', marginBottom: '2px' }}>
                  Options
                </div>
                {q.options.map((opt, oIdx) => {
                  const isCorrect = String(opt.label).trim().toUpperCase() === String(q.correct_answer).trim().toUpperCase() || Boolean(opt.is_correct);
                  return (
                    <div
                      key={oIdx}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        padding: '12px 16px',
                        borderRadius: '10px',
                        background: isCorrect ? 'var(--iv-bg)' : 'var(--iv-bg)',
                        border: isCorrect ? '1.5px solid var(--iv-success)' : '1px solid var(--iv-line)',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      <span
                        style={{
                          flex: 'none',
                          width: '26px',
                          height: '26px',
                          borderRadius: '50%',
                          background: isCorrect ? 'var(--iv-success)' : 'var(--iv-bg)',
                          color: isCorrect ? 'var(--iv-btn-fg)' : 'var(--iv-body)',
                          fontWeight: 700,
                          fontSize: '13px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {opt.label || String.fromCharCode(65 + oIdx)}
                      </span>
                      {optionHasText(opt.text) && (
                        <MathContent
                          style={isCorrect ? EXPLAIN_OPTION_STYLE_CORRECT : EXPLAIN_OPTION_STYLE}
                          className="mathjax-process tex2jax_process exhibit-clickable-html"
                          onClick={handleExhibitTextClick}
                          html={prepareSittingHtml(opt.text)}
                        />
                      )}
                      <PaperImage
                        url={opt.image_url}
                        alt={`Option ${opt.label || String.fromCharCode(65 + oIdx)}`}
                        maxHeight="260px"
                      />
                      {isCorrect && (
                        <span style={{ flex: 'none', fontSize: '11px', fontWeight: 700, color: 'var(--iv-success)', background: 'var(--iv-bg)', padding: '3px 9px', borderRadius: '999px', letterSpacing: '0.04em' }}>
                          ✓ Correct Answer
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* 4. Essay Assessment or Detailed MC Explanation */}
            {(isEssayChoiceGroup(q) || isFreeTextPractice(q)) ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {isFreeTextPractice(q) && q.explanation && (
                  <div style={{ background: 'var(--iv-bg)', border: '1.5px solid var(--iv-accent)', borderRadius: '14px', padding: '18px 22px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', color: 'var(--iv-accent)', textTransform: 'uppercase', marginBottom: '10px' }}>
                      Model Answer
                    </div>
                    {/* This screen already typesets its whole container, so the
                        maths here should render without the class. Marked all
                        the same, to match every other explanation on the screen
                        and to stay right if this block is ever moved outside
                        that container. Kept as plain text rather than innerHTML
                        so a stray < cannot swallow the rest of an expression. */}
                    <div
                      className="mathjax-process tex2jax_process"
                      style={{ fontSize: '14.5px', lineHeight: '1.7', color: 'var(--iv-head)', whiteSpace: 'pre-wrap' }}
                    >
                      {q.explanation}
                    </div>
                  </div>
                )}
                {/* Student's Written Response */}
                <div style={{ background: 'var(--iv-surface)', border: '1.5px solid var(--iv-line)', borderRadius: '14px', padding: '18px 22px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', color: 'var(--iv-muted)', textTransform: 'uppercase' }}>
                      {isFreeTextPractice(q) ? 'Your Answer' : 'Your Written Essay'}
                    </span>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--iv-body)' }}>
                      {userAnswers[q.id]?.text_response ? `${userAnswers[q.id].text_response.trim().split(/\s+/).filter(Boolean).length} words` : '0 words'}
                    </span>
                  </div>
                  <div style={{ fontSize: '14.5px', lineHeight: '1.7', color: 'var(--iv-body)', whiteSpace: 'pre-wrap', maxHeight: '200px', overflowY: 'auto' }}>
                    {userAnswers[q.id]?.text_response || 'No essay response was entered yet.'}
                  </div>
                </div>

                {/* AI Rubric & Feedback Box */}
                <div style={{ background: 'var(--iv-bg)', border: '1.5px solid var(--iv-line)', borderRadius: '14px', padding: '20px 22px', boxShadow: '0 2px 10px color-mix(in srgb, var(--iv-shadow) 3%, transparent)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <span style={{ fontSize: '12.5px', fontWeight: 700, letterSpacing: '0.06em', color: 'var(--iv-head)', textTransform: 'uppercase' }}>
                      AI Admissions Essay Evaluation & Rubric Scoring
                    </span>
                  </div>
                  {gradingEssayId === q.id ? (
                    <div style={{ padding: '24px', textAlign: 'center', color: 'var(--iv-muted)', fontSize: '14px' }}>
                      Analyzing essay structure, thesis clarity, and counter-argument refutations...
                    </div>
                  ) : aiEssayFeedback[q.id] ? (
                    <div 
                      style={{ fontSize: '14px', lineHeight: '1.7', color: 'var(--iv-body)', whiteSpace: 'pre-line' }}
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(aiEssayFeedback[q.id].replace(/\n/g, '<br/>')) }}
                    />
                  ) : (
                    <div>
                      <p style={{ fontSize: '13.5px', color: 'var(--iv-muted)', margin: '0 0 12px' }}>
                        Get instant admissions-standard grading, Band estimation (Band 1–5), and argumentative critique.
                      </p>
                      <button
                        type="button"
                        onClick={() => handleExplainOpen(explainQuestionIndex)}
                        style={{
                          background: 'var(--iv-btn)',
                          color: 'var(--iv-btn-fg)',
                          border: 'none',
                          borderRadius: '999px',
                          padding: '8px 22px',
                          fontSize: '13px',
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                      >
                        Grade Essay with AI
                      </button>
                    </div>
                  )}
                </div>

                {/* Model Marking Scheme / Points */}
                {q.explanation && (
                  <div style={{ background: 'var(--iv-bg)', border: '1.5px solid var(--iv-line)', borderRadius: '14px', padding: '18px 22px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', color: 'var(--iv-muted)', textTransform: 'uppercase', marginBottom: '8px' }}>
                      Official Marking Criteria & Suggested Angles
                    </div>
                    <MathContent
                      style={EXPLAIN_CRITERIA_STYLE}
                      className="mathjax-process tex2jax_process"
                      html={prepareSittingHtml(q.explanation)}
                    />
                  </div>
                )}
              </div>
            ) : isYesNoQuestion(q) ? (
              <div style={{ background: 'var(--iv-bg)', border: '1.5px solid var(--iv-line)', borderRadius: '14px', padding: '18px 22px' }}>
                <div style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--iv-body)', marginBottom: '10px' }}>
                  Correct Yes / No responses
                </div>
                <ul style={{ margin: 0, paddingLeft: '18px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {(q.yes_no_items || []).map((item) => (
                    <li key={item.id} style={{ fontSize: '14.5px', lineHeight: 1.55, color: 'var(--iv-head)' }}>
                      <strong>{item.correct_yes ? 'Yes' : 'No'}</strong>
                      {' — '}
                      <MathContent
                        style={EXPLAIN_INLINE_STYLE}
                        className="mathjax-process tex2jax_process"
                        html={prepareSittingHtml(item.text || '')}
                      />
                    </li>
                  ))}
                </ul>
                {q.explanation ? (
                  <MathContent
                    style={EXPLAIN_AFTER_LIST_STYLE}
                    className="mathjax-process tex2jax_process"
                    html={prepareSittingHtml(q.explanation)}
                  />
                ) : null}
              </div>
            ) : isMostLeastQuestion(q) ? (
              <div style={{ background: 'var(--iv-bg)', border: '1.5px solid var(--iv-line)', borderRadius: '14px', padding: '18px 22px' }}>
                <div style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--iv-body)', marginBottom: '10px' }}>
                  Correct Most / Least actions
                </div>
                {(q.most_least_options || []).filter((o) => o.is_correct_most || o.is_correct_least).map((opt) => (
                  <div key={opt.id} style={{ fontSize: '14.5px', lineHeight: 1.55, color: 'var(--iv-body)', marginBottom: '8px' }}>
                    <strong>{opt.is_correct_most ? 'Most' : 'Least'}:</strong>{' '}
                    <MathContent
                      style={EXPLAIN_INLINE_STYLE}
                      className="mathjax-process tex2jax_process"
                      html={prepareSittingHtml(opt.text || '')}
                    />
                  </div>
                ))}
                {q.explanation ? (
                  <MathContent
                    style={EXPLAIN_AFTER_LIST_STYLE}
                    className="mathjax-process tex2jax_process"
                    html={prepareSittingHtml(q.explanation)}
                  />
                ) : null}
              </div>
            ) : (
              <div style={{ background: 'var(--iv-bg)', border: '1.5px solid var(--iv-line)', borderRadius: '14px', padding: '18px 22px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'var(--iv-success)', color: 'var(--iv-btn-fg)', fontWeight: 700, fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {q.correct_answer || 'A'}
                  </span>
                  <span style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--iv-body)' }}>
                    Option {q.correct_answer} is correct
                  </span>
                </div>
                <MathContent
                  style={EXPLAIN_ANSWER_STYLE}
                  className="mathjax-process tex2jax_process"
                  html={prepareSittingHtml(q.explanation || 'No detailed explanation provided for this question.')}
                />
              </div>
            )}

          </div>

          {/* Modal Footer */}
          <div style={{ padding: '14px 28px', background: 'var(--iv-bg)', borderTop: '1px solid var(--iv-line)', display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => setShowExplainModal(false)}
              style={{
                fontSize: '13.5px',
                fontWeight: 700,
                color: 'var(--iv-btn-fg)',
                background: 'var(--iv-btn)',
                border: '1px solid var(--iv-btn)',
                borderRadius: '999px',
                padding: '10px 28px',
                cursor: 'pointer',
                boxShadow: '0 2px 8px color-mix(in srgb, var(--iv-shadow) 12%, transparent)'
              }}
            >
              Got it
            </button>
          </div>

        </div>
      </div>
    );
  };

  // Pearson navigation filters
  const getUnansweredIndex = () => {
    return questions.findIndex(q => !userAnswers[q.id]);
  };

  const getFlaggedIndex = () => {
    return questions.findIndex(q => flaggedQuestions[q.id]);
  };

  /* =================================================================== */
  /* ===== 1. SELECTION CARD VIEW ===================================== */
  /* =================================================================== */
  if (examMode === 'select') {
    return (
      <div className="quiz-gen-main-content quiz-gen-main-content-active quiz-gen-vh-fallback" style={{ minWidth: '100%', background: 'var(--iv-raised)', fontFamily: 'var(--iv-font-body)', padding: '24px 28px 56px', boxSizing: 'border-box' }}>
        <style>{`
          .exam-mode-choice-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 24px;
            width: 100%;
            max-width: 880px;
            box-sizing: border-box;
          }
          @media (max-width: 768px) {
            .quiz-gen-main-content {
              padding: 16px 18px 40px !important;
            }
            .exam-mode-choice-grid {
              display: flex !important;
              flex-direction: column !important;
              gap: 16px !important;
              width: 100% !important;
            }
            .exam-choice-card {
              width: 100% !important;
              box-sizing: border-box !important;
              padding: 24px 20px !important;
              min-height: auto !important;
            }
          }
        `}</style>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', width: '100%', boxSizing: 'border-box' }}>
          {/* Circular Dark Back Button on Left-Hand Side */}
          <div 
            onClick={onBack}
            title="Back"
            style={{ 
              width: '44px', 
              height: '44px', 
              borderRadius: '50%', 
              background: 'color-mix(in srgb, var(--iv-band) 88%, transparent)', 
              backdropFilter: 'blur(10px)', 
              WebkitBackdropFilter: 'blur(10px)', 
              border: '1px solid var(--iv-line)', 
              color: 'var(--iv-btn-fg)', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              cursor: 'pointer', 
              boxShadow: '0 4px 14px color-mix(in srgb, var(--iv-shadow) 20%, transparent)', 
              transition: 'transform 0.15s ease'
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--iv-on-band)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </div>

          {/* Right-Hand Side Fixed Title */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '11.5px', fontWeight: '700', letterSpacing: '0.18em', color: 'var(--iv-head)', fontFamily: 'var(--iv-font-body)' }}>
              {examName || 'TMUA'} EXAM MODE
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '16px 0 24px' }}>
          <div style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.22em', color: 'var(--iv-muted)', marginBottom: '14px' }}>BEGIN YOUR SITTING</div>
          <h1 style={{ fontFamily: 'var(--iv-font-body)', fontWeight: 700, fontSize: '48px', lineHeight: 1, letterSpacing: '-0.02em', margin: '0 0 14px', color: 'var(--iv-body)', textAlign: 'center' }}>Exam Mode</h1>
          <p style={{ maxWidth: '560px', textAlign: 'center', fontSize: '15px', lineHeight: '1.6', color: 'var(--iv-muted)', margin: '0 0 32px' }}>Choose how you would like to sit the National Admissions Test for Law. Book the official test at a Pearson VUE centre, or take a full timed simulation here in ivori.</p>

          <div className="exam-mode-choice-grid">
            {/* Option 1 — Pearson VUE */}
            <div onClick={() => setExamMode('vue_redirect')} className="exam-choice-card pearson-choice-card" style={{ position: 'relative', overflow: 'hidden', background: 'var(--iv-bg)', border: '1px solid var(--iv-line)', borderRadius: '12px', padding: '32px 30px', cursor: 'pointer', boxShadow: '0 10px 34px color-mix(in srgb, var(--iv-shadow) 7%, transparent)', display: 'flex', flexDirection: 'column', minHeight: '300px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
                <span style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'var(--iv-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="var(--iv-muted)" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 21h18"></path>
                    <path d="M5 21V8l7-5 7 5v13"></path>
                    <path d="M9 21v-6h6v6"></path>
                  </svg>
                </span>
                <span style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.12em', color: 'var(--iv-muted)', background: 'var(--iv-bg)', padding: '5px 11px', borderRadius: '999px' }}>EXTERNAL</span>
              </div>
              <div style={{ fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.16em', color: 'var(--iv-muted)', marginBottom: '9px' }}>OFFICIAL · ONLINE</div>
              <div style={{ fontFamily: 'var(--iv-font-body)', fontWeight: 600, fontSize: '26px', lineHeight: '1.12', color: 'var(--iv-body)', marginBottom: '12px' }}>Take me to Pearson VUE</div>
              <p style={{ fontSize: '14px', lineHeight: '1.55', color: 'var(--iv-muted)', margin: '0 0 auto' }}>Sit the real, invigilated LNAT online through the official Pearson VUE portal.</p>
              <span style={{ display: 'inline-flex', alignSelf: 'flex-start', alignItems: 'center', gap: '8px', marginTop: '24px', fontSize: '13.5px', fontWeight: 700, color: 'var(--iv-head)', border: '1.5px solid var(--iv-line)', borderRadius: '7px', padding: '11px 20px' }}>
                Continue to Pearson VUE
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M7 17L17 7"></path>
                  <polyline points="8,7 17,7 17,16"></polyline>
                </svg>
              </span>
            </div>

            {/* Option 2 — ivori Simulation */}
            <div onClick={() => setExamMode('ivori')} className="exam-choice-card ivori-choice-card" style={{ position: 'relative', overflow: 'hidden', background: 'var(--iv-bg)', border: '1px solid var(--iv-line)', borderRadius: '12px', padding: '32px 30px', cursor: 'pointer', boxShadow: '0 10px 34px color-mix(in srgb, var(--iv-shadow) 7%, transparent)', display: 'flex', flexDirection: 'column', minHeight: '300px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
                <span style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'var(--iv-bg)', display: 'flex', alignItems: 'center', justifycontent: 'center', justifyContent: 'center' }}>
                  <svg width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="var(--iv-accent)" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="13" r="8"></circle>
                    <polyline points="12,9 12,13 15,15"></polyline>
                    <path d="M9 2h6"></path>
                  </svg>
                </span>
                <span style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.12em', color: 'var(--iv-accent)', background: 'var(--iv-bg)', padding: '5px 11px', borderRadius: '999px' }}>TIMED</span>
              </div>
              <div style={{ fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.16em', color: 'var(--iv-muted)', marginBottom: '9px' }}>IVORI SIMULATION</div>
              <div style={{ fontFamily: 'var(--iv-font-body)', fontWeight: 600, fontSize: '26px', lineHeight: '1.12', color: 'var(--iv-body)', marginBottom: '12px' }}>Complete the exam here</div>
              <p style={{ fontSize: '14px', lineHeight: '1.55', color: 'var(--iv-muted)', margin: '0 0 auto' }}>A full timed simulation under real conditions — 42 questions, 95 minutes, one live countdown.</p>
              <span style={{ display: 'inline-flex', alignSelf: 'flex-start', alignItems: 'center', gap: '8px', marginTop: '24px', fontSize: '13.5px', fontWeight: 700, color: 'var(--iv-btn-fg)', background: 'var(--iv-btn)', borderRadius: '7px', padding: '12px 22px' }}>
                Start timed exam
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="13,6 19,12 13,18"></polyline>
                </svg>
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* =================================================================== */
  /* ===== 2. VUE REDIRECT / SIMULATION CHOICE ROW ==================== */
  /* =================================================================== */
  if (examMode === 'vue_redirect') {
    return (
      <div className="quiz-gen-main-content quiz-gen-main-content-active quiz-gen-vh-fallback" style={{ minWidth: '100%', background: 'var(--iv-raised)', fontFamily: 'var(--iv-font-body)', padding: '44px 56px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '34px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontFamily: 'var(--iv-font-body)', fontWeight: 700, fontSize: '17px', letterSpacing: '0.12em', color: 'var(--iv-head)' }}>ivori</span>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--iv-accent)' }}></span>
            <span style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.20em', color: 'var(--iv-muted)' }}>LNAT · EXAM MODE</span>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '56px 0' }}>
          <div style={{ width: '100%', maxWidth: '560px', background: 'var(--iv-bg)', border: '1px solid var(--iv-line)', borderRadius: '10px', padding: '44px 44px 38px', boxShadow: '0 18px 46px color-mix(in srgb, var(--iv-shadow) 12%, transparent)', textAlign: 'center' }}>
            <span style={{ display: 'inline-flex', width: '56px', height: '56px', borderRadius: '14px', background: 'var(--iv-bg)', border: '1px solid var(--iv-line)', alignItems: 'center', justifyContent: 'center', marginBottom: '24px' }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--iv-muted)" strokeWidth="1.9" stroke-linecap="round" stroke-linejoin="round">
                <path d="M3 21h18"></path>
                <path d="M5 21V8l7-5 7 5v13"></path>
                <path d="M9 21v-6h6v6"></path>
              </svg>
            </span>
            <div style={{ fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.18em', color: 'var(--iv-muted)', marginBottom: '10px' }}>LEAVING IVORI</div>
            <h2 style={{ fontFamily: 'var(--iv-font-body)', fontWeight: 700, fontSize: '32px', lineHeight: '1.1', margin: '0 0 14px', color: 'var(--iv-body)' }}>Pearson VUE Simulation</h2>
            <div style={{ fontSize: '14.5px', lineHeight: '1.6', color: 'var(--iv-muted)', margin: '0 0 30px' }}>
              <p style={{ margin: 0 }}>This simulation replicates the strict look, layout, and dated blue styling of the official Pearson VUE LNAT computer exam portal.</p>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
              <button onClick={() => setExamMode('pearson')} style={{ display: 'inline-flex', alignItems: 'center', gap: '9px', fontSize: '14.5px', fontWeight: 700, color: 'var(--iv-btn-fg)', background: 'var(--iv-btn)', border: 'none', borderRadius: '6px', padding: '13px 28px', cursor: 'pointer' }}>
                Start Pearson VUE Simulation
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M7 17L17 7"></path>
                  <polyline points="8,7 17,7 17,16"></polyline>
                </svg>
              </button>
              
              <span onClick={() => setExamMode('select')} style={{ display: 'inline-block', fontSize: '13px', fontWeight: 600, color: 'var(--iv-muted)', cursor: 'pointer', marginTop: '12px' }}>
                ← Back to Exam Mode
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* =================================================================== */
  /* ===== TMUA PEARSON-PLAYER SITTING (mocks / past papers only) ===== */
  /* =================================================================== */
  if (pearsonPlayer && examMode !== 'pearson') {
    const T = TMUA_PEARSON_TOKENS;
    const chrome = getTmuaPearsonChrome(colorScheme);
    const timedLabel = timeRemaining > 0 && isTimedMode(tmuaModeChoice)
      ? (formatTime ? formatTime(timeRemaining) : '00:00')
      : null;
    const hasP2Ready = tmuaPaper2Status === 'ready' && paper2Qs.length > 0;
    const hasP2Pending = tmuaPaper2Status === 'loading';
    // Writing rows have no correct_answer and must never inflate the MCQ score
    // line (a filled prompt is not "correct", and N must exclude writing rows).
    const mcqScoringQuestions = isTaraExam
      ? sittingQuestions.filter((q) => !(q.is_essay === true || q.interaction_kind === 'essay_choice_group'))
      : sittingQuestions;
    const combinedStats = getStats(isLnatExam ? paper1Qs : mcqScoringQuestions);
    const isFlagged = Boolean(currentQuestion && flaggedQuestions[currentQuestion.id]);
    const userAnswer = currentQuestion ? userAnswers[currentQuestion.id] : null;
    const nextYellow = !showNavigatorModal;
    const chromeFg = chrome.chromeFg;
    const currentModuleKey = pearsonModules[pearsonModuleIndex]?.moduleKey;
    const reviewIdxForTitle = parsePearsonReviewIndex(tmuaPaperPhase);
    const titleModuleKey = (tmuaPaperPhase === 'post_exam' || isSubmitted)
      ? undefined
      : (reviewIdxForTitle != null
        ? pearsonModules[reviewIdxForTitle]?.moduleKey
        : currentModuleKey);
    const pearsonTitle = isUcatExam
      ? (isTopicDrill
        ? getPearsonPlayerExamTitle('UCAT')
        : (activeQuizSource?.paperTitle || getPearsonPlayerExamTitle('UCAT')))
      : getPearsonPlayerExamTitle(examName, titleModuleKey, {
        tsaSitting: isTaraTsaSitting,
        bmatSitting: isTaraBmatSitting,
        lsatPractice: Boolean(activeQuizSource?.lsatPractice),
        heritageExam: activeQuizSource?.heritageExam,
      });
    const pearsonShellExamName = isLnatExam || isEsatExam || isTaraExam || isUcatExam ? examName : 'TMUA';
    const pearsonSchemeLabel = (isLnatExam || isUcatExam) ? getPearsonColorSchemeDropdownLabel(examName) : undefined;
    // After End Exam, complete the sitting. Pausing here would write
    // resume_phase onto in_progress rows and the hub would show Continue.
    const handleBackToSelection = () => {
      if (shouldClearLnatAnnotations({ event: 'leave' })) {
        setLnatAnnotationsByItemId({});
        setLnatActiveHighlightColor(DEFAULT_LNAT_HIGHLIGHT_COLOR);
      }
      quizGeneratorAPI
        .saveSectionProgress(examName, currentModuleKey || activeQuizSource?.id, isSubmitted ? 'complete' : 'incomplete')
        .catch((err) => console.warn('saveSectionProgress failed (backend not implemented yet)', err));
      if (isSubmitted || tmuaPaperPhase === 'post_exam') {
        Promise.resolve(typeof onSubmit === 'function' ? onSubmit() : null)
          .finally(() => {
            if (typeof onBack === 'function') onBack({ skipPause: true });
          });
        return;
      }
      const pauseMeta = {
        resume_phase: tmuaPaperPhase,
        resume_question_index: currentQuestionIndex,
        time_remaining_seconds: timeRemaining,
        attempt_mode: tmuaModeChoice,
      };
      if (typeof onBack === 'function') onBack(pauseMeta);
    };
    const nextVariant = isLnatExam || isEsatExam || isTaraExam || isUcatExam ? 'chrome' : (nextYellow ? 'yellow' : 'outline');
    const nextIconColor = isLnatExam || isEsatExam || isTaraExam || isUcatExam ? '#FFFFFF' : (nextYellow ? T.nextDefaultFg : chromeFg);
    const splitItem = shouldSplitLnatPearsonItem(
      examName,
      (isTopicDrill && (activeQuizSource?.lnatSection === 'B' || activeQuizSource?.lnatSection === 'section_b'))
        ? 'p2_items'
        : tmuaPaperPhase,
      currentQuestion
    )
      || shouldSplitTaraCtPearsonItem(examName, currentModuleKey, currentQuestion)
      || shouldSplitBmatThinkingSkillsPearsonItem(examName, currentModuleKey, currentQuestion, isTaraBmatSitting)
      || shouldSplitUcatPearsonItem(examName, currentQuestion, currentModuleKey, { compactViewport });
    const pearsonQuestionStyle = isLnatExam ? LNAT_PEARSON_QUESTION_STYLE : PEARSON_QUESTION_STYLE;
    const pearsonOptionTextStyle = isLnatExam ? LNAT_PEARSON_OPTION_TEXT_STYLE : PEARSON_OPTION_TEXT_STYLE;
    const ucatTimeWarning = isPearsonTimeRemainingWarning(timeRemaining, {
      enabled: isUcatExam && isTimedMode(tmuaModeChoice),
    });
    const isPearsonEssay = pearsonPlayer && isEssayQuestion(currentQuestion) && (!isTaraExam || isTaraTsaSitting);
    const isTaraWritingStopgap = isTaraExam && !isTaraTsaSitting && !isTaraBmatSitting && currentModuleKey === 'writing' && !isTaraWritingFormatGActive;
    const writingPromptsResolved = isTaraWritingStopgap ? resolveEssayPrompts(currentQuestion) : [];
    const writingPrompts = !isTaraWritingStopgap
      ? []
      : (writingPromptsResolved.length > 0
        ? writingPromptsResolved
        : (currentQuestion?.essay_options || []).map((opt, idx) => ({
          ...opt,
          label: opt.label || String.fromCharCode(65 + idx),
          text: opt.text || opt.question_text || opt.prompt_text || '',
        })));
    const writingPromptMatch = writingPrompts.findIndex((p) => p.label === userAnswer?.label);
    const activeWritingPromptIndex = writingPromptMatch >= 0 ? writingPromptMatch : 0;
    const activeWritingPrompt = writingPrompts[activeWritingPromptIndex] || writingPrompts[0] || {};
    const parsedWritingPrompt = isTaraWritingStopgap
      ? parseTaraEssayPrompt(getEssayPromptDisplayText(activeWritingPrompt, currentQuestion))
      : null;
    const writingMaxWords = pearsonWritingMaxWords(currentQuestion, { bmatSitting: isTaraBmatSitting });
    const essayWordCount = (userAnswer?.text_response || '').trim().split(/\s+/).filter(Boolean).length;
    const essayMaxWords = Number(currentQuestion?.essay_max_words) > 0 ? Number(currentQuestion.essay_max_words) : null;

    const endExamFooter = (
      <TmuaPearsonFooterButton
        onClick={() => setShowEndExamModal(true)}
        icon={<DoorIcon color={chromeFg} />}
        underline="e"
        keepLabelOnMobile
      >
        End Exam
      </TmuaPearsonFooterButton>
    );

    const reviewScreenFooter = isLnatExam ? (
      <TmuaPearsonFooterButton
        onClick={() => {
          setReviewTab('all');
          setReviewScreenOpen(true);
          setTmuaPaperPhase(tmuaPaperPhase === 'p2_items' ? 'p2_review' : 'p1_review');
        }}
        icon={<ReviewIcon color={chromeFg} />}
      >
        Review Screen
      </TmuaPearsonFooterButton>
    ) : null;

    const saveNoticeStyle = {
      position: 'fixed',
      left: '50%',
      bottom: 24,
      transform: 'translateX(-50%)',
      zIndex: 10060,
      maxWidth: '92vw',
      padding: '10px 14px',
      background: '#fff',
      color: '#1a1a1a',
      border: '1px solid #1a1a1a',
      fontFamily: 'Tahoma, "Segoe UI", sans-serif',
      fontSize: 13,
      lineHeight: 1.4,
      display: 'flex',
      alignItems: 'center',
      gap: 12,
    };
    const dialogs = (
      <>
        {pauseSaveError ? (
          <div role="alert" style={saveNoticeStyle}>
            <span>We couldn&apos;t save your progress. You&apos;re still on this paper.</span>
            <button
              type="button"
              onClick={() => {
                if (typeof onBack === 'function') onBack({ skipPause: true });
              }}
            >
              Leave anyway
            </button>
          </div>
        ) : null}
        {paperCompleteError && (tmuaPaperPhase === 'post_exam' || isSubmitted) ? (
          <div role="status" style={saveNoticeStyle}>
            <span>We couldn&apos;t save that you finished this paper. Your answers are saved.</span>
            <button type="button" onClick={() => { if (typeof onRetryComplete === 'function') onRetryComplete(); }}>
              Try again
            </button>
          </div>
        ) : null}
        {showEntireScreenModal ? (
          <TmuaPearsonDialog title="Unseen Content" onOk={() => setShowEntireScreenModal(false)}>
            You have not yet viewed the entire screen. Make sure you play all multimedia content, select every tab and scroll to every corner.
          </TmuaPearsonDialog>
        ) : null}
        {showResponseRequired ? (
          <TmuaPearsonDialog title="Response Required" onOk={() => setShowResponseRequired(false)}>
            {responseRequiredMessage || 'You cannot continue with this question unanswered.'}
          </TmuaPearsonDialog>
        ) : null}
        {showEndExamModal ? (
          <TmuaPearsonDialog
            title="End Exam"
            info
            onYes={lockTmuaSitting}
            onNo={() => setShowEndExamModal(false)}
          >
            {isTopicDrill
              ? (endExamWillPause
                ? 'Your progress is saved so you can resume later. Are you sure you want to end?'
                : 'This ends the topic. Are you sure you want to end?')
              : 'You have chosen to end this exam. Are you sure you want to end this exam?'}
          </TmuaPearsonDialog>
        ) : null}
        {showEndReviewConfirm ? (
          <TmuaPearsonDialog
            title="End Review"
            info
            onYes={confirmTmuaEndReview}
            onNo={() => setShowEndReviewConfirm(false)}
          >
            {usesPearsonModules
              ? 'You will not be able to return to this module. Are you sure you want to continue?'
              : isTaraTsaSitting
                ? getTsaEndReviewCopy(tmuaPaperPhase)
              : isLnatExam
                ? (isTopicDrill
                  ? getLnatTopicEndReviewCopy(activeQuizSource?.lnatSection)
                  : (tmuaPaperPhase === 'p2_review'
                    ? 'You will not be able to return to Section B. Are you sure you want to continue?'
                    : 'You will not be able to return to Section A. Are you sure you want to continue?'))
                : `You will not be able to return to ${takingQuestions[0]?.tmuaPaperLabel || 'Paper 1'}. Are you sure you want to continue?`}
          </TmuaPearsonDialog>
        ) : null}
        {showNavigatorModal ? (
          <TmuaPearsonNavigatorModal
            questions={takingQuestions}
            currentQuestionIndex={currentQuestionIndex}
            visitedQuestions={visitedQuestions}
            flaggedQuestions={flaggedQuestions}
            isQuestionAnswered={isQuestionAnswered}
            navigatorSortAsc={navigatorSortAsc}
            setNavigatorSortAsc={setNavigatorSortAsc}
            colorScheme={colorScheme}
            hideFlagged={hidePearsonWritingFlagNavigator({
              formatG: isTaraWritingFormatGActive,
              bmatSitting: isTaraBmatSitting,
              moduleKey: currentModuleKey,
            })}
            onJump={(idx) => { setCurrentQuestionIndex(idx); setShowNavigatorModal(false); }}
            onClose={() => setShowNavigatorModal(false)}
          />
        ) : null}
        {showSolutionModal && solutionQuestion ? (
          <TmuaPearsonSolutionModal
            question={solutionQuestion}
            questionIndex={sittingQuestions.findIndex((item) => item.id === solutionQuestion.id)}
            userAnswerLabel={userAnswers[solutionQuestion.id]?.label}
            prepareExamHtml={prepareSittingHtml}
            MathContent={MathContent}
            PaperImage={PaperImage}
            optionHasText={optionHasText}
            questionStyle={pearsonQuestionStyle}
            optionStyle={pearsonOptionTextStyle}
            onClose={() => { setShowSolutionModal(false); setSolutionQuestion(null); }}
            onExhibitTextClick={handleExhibitTextClick}
          />
        ) : null}
        {showPostExamInstructions ? (
          <TmuaPearsonDialog title="Instructions" onOk={() => setShowPostExamInstructions(false)}>
            This is a summary of your marked answers. Use the tabs to filter the table. Use Review to see the item and worked solution. Answers cannot be changed.
          </TmuaPearsonDialog>
        ) : null}
        {showResponseRequired ? (
          <TmuaPearsonDialog
            title="Response Required"
            onOk={() => {
              setShowResponseRequired(false);
            }}
          >
            {responseRequiredMessage || (isLnatExam
              ? lnatEssayOneResponseMessage(isTopicDrill ? paper1Qs.length : paper2Qs.length)
              : 'You must only respond to ONE question. Leave the other two blank.')}
          </TmuaPearsonDialog>
        ) : null}
      </>
    );

    if (isPearsonAwaitingPhase(tmuaPaperPhase)) {
      return (
        <TmuaPearsonShell examName={pearsonShellExamName} title={pearsonTitle} colorSchemeLabel={pearsonSchemeLabel} colorScheme={colorScheme} setColorScheme={setColorScheme} containerRef={containerRef} followSchemeInk={isUcatExam} onBackToSelection={handleBackToSelection}>
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: T.fontUi, fontSize: 16 }}>
            Loading, please wait…
          </div>
        </TmuaPearsonShell>
      );
    }

    const instrIndex = parsePearsonInstructionsIndex(tmuaPaperPhase);
    if (instrIndex != null) {
      const sittingMod = usesPearsonModules ? pearsonModules[instrIndex] : null;
      if (isUcatExam) {
        return (
          <UcatSubtestInstructionsScreen
            examName="UCAT"
            paperTitle={pearsonTitle}
            colorScheme={colorScheme}
            setColorScheme={setColorScheme}
            moduleKey={sittingMod?.moduleKey}
            questionCount={sittingMod?.questions?.length}
            durationMinutes={
              (isTopicDrill && Number(sittingMod?.durationMinutes) > 0)
                ? sittingMod.durationMinutes
                : getUcatModuleDurationMinutes(sittingMod || { moduleKey: sittingMod?.moduleKey })
            }
            onNext={() => {
              const next = nextPearsonSittingPhase(tmuaPaperPhase, ucatInstructionAdvanceEvent(), {
                moduleCount: pearsonLiveModuleCount(pearsonModules, { isUcatExam: true }),
              });
              setTmuaPaperPhase(next);
            }}
            onEndExam={lockTmuaSitting}
            pearsonSitting
          />
        );
      }
      const taraFallbackCount = sittingMod?.moduleKey === 'writing' ? 1 : 22;
      const bmatMins = isTaraBmatSitting ? getBmatSectionDurationMinutes(sittingMod?.moduleKey) : null;
      const bmatFallbackCount = isTaraBmatSitting
        ? (BMAT_FALLBACK_QUESTION_COUNT[sittingMod?.moduleKey] || 32)
        : taraFallbackCount;
      return (
        <>
          <TmuaInstructionsScreen
            examName={examName}
            title={isTaraBmatSitting ? BMAT_PEARSON_TITLE : undefined}
            tsaSitting={isTaraTsaSitting}
            heritageExam={activeQuizSource?.heritageExam}
            variant={instrIndex > 0 ? 'paper2' : 'paper1'}
            sectionLabel={isTaraTsaSitting
              ? (instrIndex > 0 ? 'Section 2' : 'Section 1')
              : isTaraBmatSitting
              ? (BMAT_MODULE_LABELS[sittingMod?.moduleKey] || sittingMod?.moduleKey)
              : isTaraExam
              ? (TARA_MODULE_LABELS[sittingMod?.moduleKey] || sittingMod?.moduleKey)
              : isEsatExam
                ? (ESAT_MODULE_LABELS[sittingMod?.moduleKey] || sittingMod?.moduleKey)
                : undefined}
            questionCount={usesPearsonModules
              ? (sittingMod?.questions?.length || (isTaraExam ? bmatFallbackCount : 27))
              : (isLnatExam || isTaraTsaSitting
                ? lnatOrTsaPaper2InstructionCount(paper2Qs.length, { isLnatExam, isTaraTsaSitting })
                : (paper2Qs.length || 20))}
            durationMinutes={usesPearsonModules
              ? (isTaraBmatSitting ? (bmatMins || 60) : 40)
              : isTaraTsaSitting
                ? (instrIndex > 0
                  ? resolveTsaSectionDurationMinutes(tmuaP2TimerDuration, { section: 2 })
                  : resolveTsaSectionDurationMinutes(tmuaP1TimerDuration, { section: 1 }))
              : isLnatExam
                ? (tmuaP2TimerDuration || 40)
                : 75}
            timeCell={usesPearsonModules
              ? (tmuaModeChoice === 'timed'
                ? `${isTaraBmatSitting ? (bmatMins || 60) : 40} minutes`
                : 'Untimed')
              : isLnatExam || isTaraTsaSitting
                ? (tmuaModeChoice === 'timed'
                  ? `${isTaraTsaSitting
                    ? (instrIndex > 0
                      ? resolveTsaSectionDurationMinutes(tmuaP2TimerDuration, { section: 2 })
                      : resolveTsaSectionDurationMinutes(tmuaP1TimerDuration, { section: 1 }))
                    : (tmuaP2TimerDuration || 40)} minutes`
                  : 'Untimed')
                : undefined}
            bodyCopy={isTaraBmatSitting && sittingMod?.moduleKey === 'writing'
              ? bmatWritingInstructionBodyCopy(sittingMod?.questions?.[0]?.essay_max_words)
              : undefined}
            autoAdvance={tmuaModeChoice === 'timed'}
            modeChoice={tmuaModeChoice}
            colorScheme={colorScheme}
            setColorScheme={setColorScheme}
            onNext={() => {
              const next = usesPearsonModules
                ? nextPearsonSittingPhase(tmuaPaperPhase, 'instructionsNext', {
                  moduleCount: pearsonLiveModuleCount(pearsonModules, { isUcatExam }),
                })
                : 'p2_items';
              setTmuaPaperPhase(next);
            }}
            onEndExam={lockTmuaSitting}
          />
        </>
      );
    }

    const isReview = parsePearsonReviewIndex(tmuaPaperPhase) != null || tmuaPaperPhase === 'post_exam' || isSubmitted;
    const isPostExam = tmuaPaperPhase === 'post_exam' || isSubmitted;

    if (isReview) {
      // A TARA writing answer is stored against the prompt the student picked,
      // not against the single source row (see expandTaraWritingPearsonItems).
      // The post-exam list has to be expanded the same way persistence expands
      // it, or the essay row finds no text on its own id and reads Unseen.
      const reviewList = isPostExam
        ? questionsForAttemptPayloads(sittingQuestions)
        : takingQuestions;
      const p1HasSibling = hasP2Ready || hasP2Pending;
      const reviewIdx = parsePearsonReviewIndex(tmuaPaperPhase);
      const esatNextMod = usesPearsonModules && reviewIdx != null ? pearsonModules[reviewIdx + 1] : null;
      const esatHasLater = Boolean(esatNextMod && (
        esatNextMod.status === 'ready'
        || esatNextMod.status === 'loading'
        || (esatNextMod.questions || []).length
      ));
      const showEndReview = !isPostExam && (
        usesPearsonModules
          ? (reviewIdx != null && reviewIdx < Math.max((pearsonModules || []).length, 1) - 1 && esatHasLater)
          : (
            (tmuaPaperPhase === 'p1_review' && (p1HasSibling || (isLnatExam && isTopicDrill)))
            || ((isLnatExam || isTaraTsaSitting) && tmuaPaperPhase === 'p2_review')
          )
      );
      const footerLeft = showEndReview
        ? (
          <>
            { (isLnatExam || isTaraTsaSitting) && tmuaPaperPhase === 'p2_review' ? endExamFooter : null }
            <TmuaPearsonFooterButton
              onClick={requestEndReview}
              icon={<DoorIcon color={chromeFg} />}
              keepLabelOnMobile
            >
              End Review
            </TmuaPearsonFooterButton>
          </>
        )
        : (
          <TmuaPearsonFooterButton
            onClick={() => {
              if (isPostExam) {
                const leaveFinishedExam = () => {
                  Promise.resolve(typeof onSubmit === 'function' ? onSubmit() : null)
                    .finally(() => {
                      if (typeof onBack === 'function') onBack({ skipPause: true });
                    });
                };
                if (paperCompleteError) {
                  if (typeof onRetryComplete !== 'function') return;
                  Promise.resolve(onRetryComplete()).then((saved) => {
                    if (saved !== true) return;
                    leaveFinishedExam();
                  }).catch(() => {
                    // A rejected retry is a failed save: stay on the post-exam screen.
                  });
                  return;
                }
                leaveFinishedExam();
              } else {
                setShowEndExamModal(true);
              }
            }}
            icon={<DoorIcon color={chromeFg} />}
            underline="e"
            keepLabelOnMobile
          >
            End Exam
          </TmuaPearsonFooterButton>
        );

      return (
        <TmuaPearsonShell
          examName={pearsonShellExamName}
          title={pearsonTitle}
          colorSchemeLabel={pearsonSchemeLabel}
          colorScheme={colorScheme}
          setColorScheme={setColorScheme}
          containerRef={containerRef}
          followSchemeInk={isUcatExam}
          onBackToSelection={handleBackToSelection}
          toolbarLeft={isPostExam ? (
            <button
              type="button"
              onClick={() => setShowPostExamInstructions(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: 'transparent',
                border: 'none',
                color: chromeFg,
                fontFamily: 'Tahoma, "Segoe UI", sans-serif',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
              </svg>
              Instructions
            </button>
          ) : null}
          footerLeft={footerLeft}
          footerRight={isPostExam ? (
            <TmuaPearsonFooterButton
              variant="outline"
              onClick={() => {
                setIsSubmitted(false);
                setCurrentQuestionIndex(0);
                setFlaggedQuestions({});
                setVisitedQuestions({});
                setTmuaPaper1Frozen(false);
                setFrozenModuleCount(0);
                setReviewScreenOpen(false);
                setShowSolutionModal(false);
                setSolutionQuestion(null);
                setTmuaPaperPhase(usesPearsonModules ? 'm0_items' : 'p1_items');
                setLnatAnnotationsByItemId({});
                setLnatActiveHighlightColor(DEFAULT_LNAT_HIGHLIGHT_COLOR);
                if (typeof onSwapTmuaPaper === 'function') onSwapTmuaPaper(1);
                if (isTimedMode(tmuaModeChoice) && typeof onStartPaperTimer === 'function') {
                  onStartPaperTimer(usesPearsonModules
                    ? getTopicDrillRestartMinutes({
                      isTopicDrill,
                      isUcatExam,
                      isTaraBmatSitting,
                      pearsonModules,
                      tmuaP1TimerDuration,
                    })
                    : (isTaraTsaSitting
                      ? resolveTsaSectionDurationMinutes(tmuaP1TimerDuration, { section: 1 })
                      : (isLnatExam ? tmuaP1TimerDuration : 75)));
                }
              }}
              icon={<RetakeIcon color={chromeFg} />}
            >
              Retake
            </TmuaPearsonFooterButton>
          ) : null}
        >
          <style>{`.product-back-btn { display: none !important; }`}</style>
          <TmuaPearsonReviewTable
            chrome={isTaraTsaSitting ? 'tsa' : isLnatExam ? 'lnat' : isEsatExam ? 'esat' : isTaraExam ? 'tara' : isUcatExam ? 'ucat' : 'tmua'}
            mode={isPostExam ? 'post-exam' : 'pre-submit'}
            questions={reviewList}
            userAnswers={userAnswers}
            flaggedQuestions={flaggedQuestions}
            visitedQuestions={visitedQuestions}
            isQuestionAnswered={isQuestionAnswered}
            reviewTab={reviewTab}
            setReviewTab={setReviewTab}
            hideFlagged={hidePearsonWritingFlagReview({
              formatG: isTaraWritingFormatGActive,
              bmatSitting: isTaraBmatSitting,
              moduleKey: currentModuleKey,
              isPostExam,
            })}
            bmatSitting={isTaraBmatSitting}
            scoreLine={isPostExam && combinedStats.total > 0 ? `Correct: ${combinedStats.correct} / ${combinedStats.total}` : null}
            onReviewRow={(idx, q) => {
              if (isPostExam) {
                setSolutionQuestion(q);
                setShowSolutionModal(true);
              } else {
                clearPartialYesNoGroup(q);
                setCurrentQuestionIndex(idx);
                setReviewScreenOpen(false);
                setTmuaPaperPhase(usesPearsonModules
                  ? `m${parsePearsonReviewIndex(tmuaPaperPhase) ?? 0}_items`
                  : (tmuaPaperPhase === 'p2_review' ? 'p2_items' : 'p1_items'));
              }
            }}
          />
          {dialogs}
        </TmuaPearsonShell>
      );
    }

    if (!currentQuestion || totalQuestions < 1) {
      return (
        <TmuaPearsonShell examName={pearsonShellExamName} title={pearsonTitle} colorSchemeLabel={pearsonSchemeLabel} colorScheme={colorScheme} setColorScheme={setColorScheme} containerRef={containerRef} followSchemeInk={isUcatExam} onBackToSelection={handleBackToSelection}>
          <div style={{ padding: 32, fontFamily: T.fontUi }}>No questions in this paper.</div>
        </TmuaPearsonShell>
      );
    }

    const lnatItemId = currentQuestion ? String(currentQuestion.id) : '';
    const lnatItemAnnotations = lnatAnnotationsByItemId[lnatItemId] || EMPTY_LNAT_ANNOTATIONS;
    const wrapLnatPane = (pane, inner, extra = {}) => {
      if (!(pearsonPlayer && isLnatExam) || !currentQuestion) return inner;
      return (
        <LnatPearsonAnnotatable
          pane={pane}
          optionLabel={extra.optionLabel}
          className={extra.className}
          highlights={lnatItemAnnotations.highlights}
          strikes={lnatItemAnnotations.strikes}
          colorScheme={colorScheme}
          mathJaxEpoch={lnatMathJaxEpoch}
          questionId={lnatItemId}
        >
          {inner}
        </LnatPearsonAnnotatable>
      );
    };

    const yesNoBlock = (
      <TmuaPearsonDragYesNo
        items={currentQuestion.yes_no_items || []}
        value={(userAnswer && userAnswer.yes_no_answers) || {}}
        onPlace={(itemId, val) => selectYesNo(currentQuestion.id, currentQuestion, itemId, val)}
        onClear={(itemId) => clearYesNo(currentQuestion.id, currentQuestion, itemId)}
        readOnly={Boolean(isSubmitted)}
        getCorrectness={(itemId) => {
          const item = (currentQuestion.yes_no_items || []).find((it) => String(it.id) === String(itemId));
          const yn = (userAnswer && userAnswer.yes_no_answers) || {};
          if (!item || !(String(itemId) in yn)) return null;
          return yn[String(itemId)] === !!item.correct_yes;
        }}
        colorScheme={colorScheme}
        prepareExamHtml={prepareSittingHtml}
        splitItem={splitItem}
        questionId={currentQuestion.id}
      />
    );

    const mostLeastBlock = (
      <TmuaPearsonDragMostLeast
        options={currentQuestion.most_least_options || []}
        value={(userAnswer && userAnswer.most_least_answers) || {}}
        onPlace={(slot, optionId) => selectMostLeast(currentQuestion.id, currentQuestion, slot, optionId)}
        onClear={(slot) => clearMostLeast(currentQuestion.id, currentQuestion, slot)}
        readOnly={Boolean(isSubmitted)}
        colorScheme={colorScheme}
        prepareExamHtml={prepareSittingHtml}
        questionId={currentQuestion.id}
        compactLayout={Boolean(compactViewport)}
      />
    );

    // Picture options are each a card (examOptions.css), so a label and its
    // picture read as one answer; fractions get more room between them.
    const optionCards = optionsHavePictures(currentQuestion.options);
    const mcqBlock = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: optionListGap(currentQuestion.options), maxWidth: splitItem ? 'none' : 720 }}>
        {(currentQuestion.options || []).map((option) => {
          const isSelected = userAnswer && userAnswer.label === option.label;
          const hasText = optionHasText(option.text);
          return (
            <label
              key={option.label}
              className={optionCards ? OPTION_CARD_CLASS : undefined}
              style={{ display: 'flex', alignItems: optionRowAlignment(hasText), gap: 10, cursor: isSubmitted ? 'default' : 'pointer' }}
              onMouseDown={pearsonPlayer && isLnatExam ? (e) => {
                lnatOptionPointerRef.current[option.label] = { x: e.clientX, y: e.clientY };
              } : undefined}
              onClick={pearsonPlayer && isLnatExam ? (e) => {
                const down = lnatOptionPointerRef.current[option.label];
                if (down && (Math.abs(e.clientX - down.x) > 4 || Math.abs(e.clientY - down.y) > 4)) {
                  e.preventDefault();
                }
              } : undefined}
            >
              <input
                type="radio"
                name={`tmua-q-${currentQuestion.id}`}
                checked={Boolean(isSelected)}
                disabled={Boolean(isSubmitted)}
                onChange={() => selectOption(currentQuestion.id, option.label, currentQuestion)}
                style={optionRadioStyle(hasText)}
              />
              {/* minWidth 0: a flex item is otherwise at least as wide as its
                  widest unbreakable content, so a long formula in an option
                  widened the option, and the question scrolled sideways on a
                  phone, before markWideMath could see it did not fit. */}
              <span style={{ flex: 1, minWidth: 0 }}>
                {hasText ? wrapLnatPane(
                  'option',
                  (
                  <MathContent
                    className="mathjax-process tex2jax_process exhibit-clickable-html"
                    style={pearsonOptionTextStyle}
                    html={prepareOptionHtml(option.text)}
                    onClick={handleExhibitTextClick}
                  />
                  ),
                  { optionLabel: option.label }
                ) : null}
                <PaperImage url={option.image_url} alt="" maxHeight="260px" />
              </span>
            </label>
          );
        })}
      </div>
    );

    const answerBlock = isYesNoQuestion(currentQuestion)
      ? yesNoBlock
      : isMostLeastQuestion(currentQuestion)
        ? mostLeastBlock
        : mcqBlock;

    const stemHtmlSource = normalizeQuestionSpacing(
      String(currentQuestion.question || '').replace(/^Question\s+\d+\s*:\s*/i, '')
    );
    const preparedStemHtml = prepareSittingHtml(stemHtmlSource);
    const stemImageUrl = String(currentQuestion.image_url || '').trim();
    const skipStemPaperImage = Boolean(stemImageUrl && String(preparedStemHtml).includes(stemImageUrl));
    const exhibitText = String(currentQuestion.exhibit_text || '').trim();
    const mostLeastPhoneSplit = Boolean(
      splitItem && isMostLeastQuestion(currentQuestion)
    );

    const stemBlock = (
      <>
        {exhibitText && !splitItem ? wrapLnatPane(
          'passage',
          (
          <MathContent
            style={pearsonQuestionStyle}
            className="question-text mathjax-process tex2jax_process exhibit-clickable-html"
            html={prepareSittingHtml(exhibitText)}
            onClick={handleExhibitTextClick}
          />
          )
        ) : null}
        {wrapLnatPane(
          'stem',
          (
        <MathContent
          style={pearsonQuestionStyle}
          className="question-text mathjax-process tex2jax_process exhibit-clickable-html"
          html={preparedStemHtml}
          onClick={handleExhibitTextClick}
        />
          )
        )}
        {skipStemPaperImage ? null : <PaperImage url={currentQuestion.image_url} alt="" />}
      </>
    );

    const mostLeastReadingBlock = (
      <>
        {exhibitText ? (
          <MathContent
            style={pearsonQuestionStyle}
            className="question-text mathjax-process tex2jax_process exhibit-clickable-html"
            html={prepareSittingHtml(exhibitText)}
            onClick={handleExhibitTextClick}
          />
        ) : null}
        <MathContent
          style={pearsonQuestionStyle}
          className="question-text mathjax-process tex2jax_process exhibit-clickable-html"
          html={preparedStemHtml}
          onClick={handleExhibitTextClick}
        />
        {skipStemPaperImage ? null : <PaperImage url={currentQuestion.image_url} alt="" />}
      </>
    );

    const lnatItemPrompt = resolveLnatEssayItemPrompt(currentQuestion);
    const essayPromptInner = pearsonPlayer && isLnatExam
      ? (
        lnatItemPrompt ? (
          <MathContent
            style={pearsonQuestionStyle}
            className="question-text mathjax-process tex2jax_process exhibit-clickable-html"
            html={prepareSittingHtml(lnatItemPrompt)}
            onClick={handleExhibitTextClick}
          />
        ) : (
          <p style={{ margin: 0, fontSize: 17 }}>Prompt unavailable</p>
        )
      )
      : (
      <>
        {exhibitText ? (
          <MathContent
            style={pearsonQuestionStyle}
            className="question-text mathjax-process tex2jax_process exhibit-clickable-html"
            html={prepareSittingHtml(exhibitText)}
            onClick={handleExhibitTextClick}
          />
        ) : null}
        <MathContent
          style={pearsonQuestionStyle}
          className="question-text mathjax-process tex2jax_process exhibit-clickable-html"
          html={preparedStemHtml}
          onClick={handleExhibitTextClick}
        />
        {skipStemPaperImage ? null : <PaperImage url={currentQuestion.image_url} alt="" />}
      </>
      );

    const essayBlock = (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        <div style={{ padding: '16px 22px 8px', overflow: 'auto', flex: 'none', maxHeight: '34%' }}>
          {pearsonPlayer && isLnatExam
            ? (lnatItemPrompt
              ? wrapLnatPane('prompt', essayPromptInner, { className: 'lnat-pearson-essay-prompt' })
              : essayPromptInner)
            : stemBlock}
          {!(pearsonPlayer && isLnatExam) ? (
            <p style={{ margin: '10px 0 0', fontSize: 13, color: '#000' }}>
              Aim for 500 to 600 words
              {essayMaxWords ? `. Maximum ${essayMaxWords} words.` : '.'}
            </p>
          ) : null}
        </div>
        <div data-writing-keep-focus style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: 'var(--iv-bg)', borderTop: '1px solid var(--iv-line)', borderBottom: '1px solid var(--iv-line)' }}>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={handleCut} style={{ background: 'var(--iv-bg)', border: '1px solid var(--iv-line)', padding: '3px 10px', fontSize: 12, cursor: 'pointer' }}>Cut</button>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={handleCopy} style={{ background: 'var(--iv-bg)', border: '1px solid var(--iv-line)', padding: '3px 10px', fontSize: 12, cursor: 'pointer' }}>Copy</button>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={handlePaste} style={{ background: 'var(--iv-bg)', border: '1px solid var(--iv-line)', padding: '3px 10px', fontSize: 12, cursor: 'pointer' }}>Paste</button>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={handleUndo} style={{ background: 'var(--iv-bg)', border: '1px solid var(--iv-line)', padding: '3px 10px', fontSize: 12, cursor: undoStack.length === 0 ? 'default' : 'pointer' }}>Undo</button>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={handleRedo} style={{ background: 'var(--iv-bg)', border: '1px solid var(--iv-line)', padding: '3px 10px', fontSize: 12, cursor: redoStack.length === 0 ? 'default' : 'pointer' }}>Redo</button>
        </div>
        <textarea
          ref={essayTextareaRef}
          data-writing-keep-focus
          value={userAnswer?.text_response || ''}
          onChange={(e) => handleEssayChange(e.target.value)}
          placeholder="Type your answer here…"
          style={{
            flex: 1,
            width: '100%',
            minHeight: 0,
            border: 'none',
            padding: 16,
            fontSize: 15,
            lineHeight: 1.5,
            fontFamily: T.fontUi,
            resize: 'none',
            outline: 'none',
            color: 'var(--iv-body)',
            background: 'var(--iv-surface)',
          }}
        />
        <div style={{ padding: '8px 16px', borderTop: '1px solid var(--iv-line)', fontSize: 13, fontWeight: 600 }}>
          Word Count : {essayWordCount}
        </div>
      </div>
    );

    return (
      <TmuaPearsonShell
        examName={isLnatExam || isEsatExam || isTaraExam || isUcatExam ? examName : 'TMUA'}
        title={pearsonTitle}
        colorSchemeLabel={pearsonSchemeLabel}
        colorScheme={colorScheme}
        setColorScheme={setColorScheme}
        containerRef={containerRef}
        onWheel={handleContainerWheel}
        followSchemeInk={isUcatExam}
        onBackToSelection={handleBackToSelection}
        timeRemainingWarning={ucatTimeWarning}
        timeRemainingLabel={timedLabel}
        counter={{
          current: isTaraWritingStopgap ? activeWritingPromptIndex + 1 : currentQuestionIndex + 1,
          total: isTaraWritingStopgap ? (writingPrompts.length || 3) : totalQuestions,
          visible: tmuaCounterVisible,
          onToggle: () => setTmuaCounterVisible((v) => !v),
          onOpenNavigator: () => setShowNavigatorModal(true),
        }}
        toolbarLeft={isUcatExam ? (
          <button
            type="button"
            onClick={() => {
              setUcatCalculatorOpen(true);
              setUcatCalculatorEnabled(true);
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: 'transparent',
              border: 'none',
              color: chromeFg,
              fontFamily: 'Tahoma, Arial, Helvetica, sans-serif',
              fontSize: 16,
              fontWeight: 700,
              cursor: 'pointer',
              padding: '0 10px',
              height: '100%',
            }}
          >
            Calculator
          </button>
        ) : showLnatPearsonHighlightToolbar({
          pearsonPlayer,
          isLnatExam,
          isReview: false,
          isPostExam: false,
          tmuaPaperPhase,
        }) ? (
          <LnatPearsonHighlightToolbar
            activeColor={lnatActiveHighlightColor}
            colorScheme={colorScheme}
            chromeFg={chromeFg}
            onHighlight={applyLnatHighlightFromSelection}
            onStrikethrough={applyLnatStrikethroughFromSelection}
            onRemoveHighlight={removeLnatHighlightFromSelection}
            onChangeColor={setLnatActiveHighlightColor}
          />
        ) : null}
        toolbarRight={
          ((isTaraWritingFormatGActive && currentQuestion?.pearsonWritingItem)
            || (isTaraBmatSitting && currentModuleKey === 'writing')) ? null : (
            <button
              type="button"
              onClick={() => {
                handleFlag(currentQuestion.id, currentQuestion);
                if (isUcatExam) setUcatCalculatorEnabled(false);
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: 'transparent',
                border: 'none',
                color: isFlagged ? T.flagOn : chromeFg,
                fontFamily: 'Tahoma, Arial, Helvetica, sans-serif',
                fontSize: 16,
                fontWeight: 700,
                cursor: 'pointer',
                padding: '0 10px',
                height: '100%',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill={isFlagged ? T.flagOn : 'none'} stroke="currentColor" strokeWidth="2">
                <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
                <line x1="4" y1="22" x2="4" y2="15" />
              </svg>
              Flag for Review
            </button>
          )
        }
        footerLeft={(
          <>
            {isUcatExam ? (
              <TmuaPearsonFooterButton onClick={() => setUcatHelpOpen(true)} icon={<HelpIcon color={chromeFg} />}>
                Help
              </TmuaPearsonFooterButton>
            ) : null}
            {endExamFooter}
            {reviewScreenFooter}
          </>
        )}
        footerRight={(
          <>
            {isTaraWritingStopgap
              ? (activeWritingPromptIndex > 0 ? (
                <TmuaPearsonFooterButton
                  onClick={handlePrev}
                  icon={<PrevIcon color={chromeFg} />}
                >
                  Previous
                </TmuaPearsonFooterButton>
              ) : null)
              : (currentQuestionIndex > 0 ? (
                <TmuaPearsonFooterButton onClick={handlePrev} icon={<PrevIcon color={chromeFg} />}>
                  Previous
                </TmuaPearsonFooterButton>
              ) : null)}
            <TmuaPearsonFooterButton onClick={() => setShowNavigatorModal(true)} icon={<NavigatorIcon color={chromeFg} />}>
              Navigator
            </TmuaPearsonFooterButton>
            <TmuaPearsonFooterButton
              variant={nextVariant}
              onClick={handleNext}
              icon={<NextIcon color={nextIconColor} />}
              iconPosition="end"
              underline="n"
            >
              Next
            </TmuaPearsonFooterButton>
          </>
        )}
      >
        <style>{`.product-back-btn { display: none !important; }`}</style>
        {isTaraWritingFormatGActive && currentQuestion?.pearsonWritingItem ? (
          <div ref={questionPaneRef} tabIndex={-1} style={{ position: 'absolute', inset: 0, outline: 'none' }}>
            <TmuaPearsonWritingEditor
              promptText={currentQuestion.question}
              textResponse={userAnswer?.text_response || ''}
              onChangeText={(text) => handleTextInput(currentQuestion.id, currentQuestion, text)}
              targetId={currentQuestion.id}
              userAnswers={userAnswers}
              siblingIds={taraWritingModuleItems.map((item) => item.id)}
              maxWords={pearsonWritingMaxWords(currentQuestion, { bmatSitting: isTaraBmatSitting })}
              textareaRef={essayTextareaRef}
              onBlocked={() => {
                setResponseRequiredMessage('You must only respond to ONE question. Leave the other two blank.');
                setShowResponseRequired(true);
              }}
            />
          </div>
        ) : isTaraWritingStopgap ? (
          <div
            ref={questionPaneRef}
            tabIndex={-1}
            style={{
              position: 'absolute',
              inset: 0,
              outline: 'none',
              display: 'flex',
              flexDirection: 'column',
              background: 'var(--iv-surface)',
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            <style>{`
              .tmua-tara-stopgap-textarea { font-size: 15.5px; }
              @media (max-width: 768px) {
                .tmua-tara-stopgap-textarea { font-size: 16px !important; }
              }
            `}</style>
            {!taraWritingKeyboardOpen ? (
              <div
                onClick={taraWritingPromptCollapsed ? expandTaraWritingPrompt : undefined}
                style={{
                  padding: '24px 32px 12px',
                  background: 'var(--iv-surface)',
                  overflowY: taraWritingPromptCollapsed ? 'hidden' : 'auto',
                  flex: 'none',
                  maxHeight: taraWritingPromptCollapsed ? '3.4em' : '40%',
                  cursor: taraWritingPromptCollapsed ? 'pointer' : 'default',
                }}
              >
              {parsedWritingPrompt ? (
                <div style={{ marginBottom: '10px', color: 'var(--iv-body)' }}>
                  <div style={{ fontSize: '15px', fontWeight: 700, fontFamily: 'Tahoma, "Segoe UI", sans-serif', marginBottom: '12px' }}>
                    Essay {activeWritingPromptIndex + 1}
                  </div>
                  {parsedWritingPrompt.statement ? (
                    <div style={{ fontSize: '16.5px', fontWeight: 400, lineHeight: 1.5, fontFamily: 'Tahoma, "Segoe UI", sans-serif', marginBottom: parsedWritingPrompt.attribution ? '6px' : '12px' }}>
                      {'\u2018'}{parsedWritingPrompt.statement}{'\u2019'}
                    </div>
                  ) : null}
                  {parsedWritingPrompt.attribution ? (
                    <div style={{ fontSize: '14.5px', fontStyle: 'italic', color: '#737787', marginBottom: '12px', fontFamily: 'Tahoma, "Segoe UI", sans-serif' }}>
                      ({parsedWritingPrompt.attribution})
                    </div>
                  ) : null}
                  <div style={{ fontSize: '15px', lineHeight: 1.65, fontFamily: 'Tahoma, "Segoe UI", sans-serif', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {(parsedWritingPrompt.tasks.length > 0
                      ? parsedWritingPrompt.tasks
                      : [
                        'Explain what you think is meant by the statement.',
                        'Give a reasoned argument against the statement.',
                        'To what extent do you agree with the statement?',
                      ]
                    ).map((task) => (
                      <div key={task}>{task}</div>
                    ))}
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: '16.5px', fontWeight: 400, color: '#14161F', lineHeight: 1.5, marginBottom: '10px', fontFamily: 'Tahoma, "Segoe UI", sans-serif', whiteSpace: 'pre-wrap' }}>
                  {getEssayPromptDisplayText(activeWritingPrompt, currentQuestion)}
                </div>
              )}
              <div style={{ fontSize: '12.5px', color: 'var(--iv-muted)', lineHeight: 1.5 }}>
                Write in the window box below. To cut text, first highlight the text inside the window box, then select the cut button. The process is the same to copy text, except you use the copy button instead. Select the Paste button to paste the text which was cut or copied.
              </div>
              {taraWritingPromptCollapsed ? (
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--iv-muted)' }}>Show more</span>
              ) : null}
              </div>
            ) : null}
            {!taraWritingKeyboardOpen ? (
              <div data-writing-keep-focus style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 32px', background: 'var(--iv-bg)', borderTop: '1px solid var(--iv-line)', borderBottom: '1px solid var(--iv-line)' }}>
                <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={handleCut} title="Cut selected text" style={{ background: 'var(--iv-surface)', border: '1px solid var(--iv-line)', borderRadius: '4px', padding: '4px 12px', fontSize: '12px', fontWeight: 600, color: 'var(--iv-head)', cursor: 'pointer' }}>Cut</button>
                <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={handleCopy} title="Copy selected text" style={{ background: 'var(--iv-surface)', border: '1px solid var(--iv-line)', borderRadius: '4px', padding: '4px 12px', fontSize: '12px', fontWeight: 600, color: 'var(--iv-head)', cursor: 'pointer' }}>Copy</button>
                <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={handlePaste} title="Paste from clipboard" style={{ background: 'var(--iv-surface)', border: '1px solid var(--iv-line)', borderRadius: '4px', padding: '4px 12px', fontSize: '12px', fontWeight: 600, color: 'var(--iv-head)', cursor: 'pointer' }}>Paste</button>
                <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={handleUndo} title="Undo" style={{ background: 'var(--iv-surface)', border: '1px solid var(--iv-line)', borderRadius: '4px', padding: '4px 12px', fontSize: '12px', fontWeight: 600, color: undoStack.length === 0 ? 'var(--iv-muted)' : 'var(--iv-head)', cursor: undoStack.length === 0 ? 'default' : 'pointer' }}>Undo</button>
                <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={handleRedo} title="Redo" style={{ background: 'var(--iv-surface)', border: '1px solid var(--iv-line)', borderRadius: '4px', padding: '4px 12px', fontSize: '12px', fontWeight: 600, color: redoStack.length === 0 ? 'var(--iv-muted)' : 'var(--iv-head)', cursor: redoStack.length === 0 ? 'default' : 'pointer' }}>Redo</button>
              </div>
            ) : null}
            <div style={{ flex: 1, padding: '16px 32px', background: 'var(--iv-surface)', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <textarea
                ref={essayTextareaRef}
                data-writing-keep-focus
                value={userAnswer?.text_response || ''}
                onChange={(e) => handleEssayChange(e.target.value)}
                onFocus={() => {
                  if (typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches) {
                    handleTaraWritingTextareaFocus();
                  }
                }}
                placeholder=""
                className="tmua-tara-stopgap-textarea"
                style={{
                  flex: 1,
                  width: '100%',
                  padding: '16px',
                  border: '1.5px solid #14161F',
                  borderRadius: '8px',
                  lineHeight: 1.65,
                  fontFamily: 'Tahoma, "Segoe UI", sans-serif',
                  boxSizing: 'border-box',
                  resize: 'none',
                  outline: 'none',
                  background: '#FFFFFF',
                  color: '#14161F',
                  boxShadow: 'none',
                }}
              />
            </div>
            <div style={{ background: 'var(--iv-surface)', borderTop: '1px solid var(--iv-line)', padding: '14px 32px' }}>
              <span style={{ fontSize: '13.5px', fontWeight: '600', color: 'var(--iv-muted)' }}>
                Word Count: <strong style={{ color: writingMaxWords && essayWordCount > writingMaxWords ? 'var(--iv-danger)' : 'var(--iv-head)' }}>{essayWordCount}</strong>
                {writingMaxWords ? <span style={{ color: 'var(--iv-muted)' }}> / {writingMaxWords}</span> : null}
              </span>
            </div>
          </div>
        ) : isPearsonEssay ? (
          <div ref={questionPaneRef} tabIndex={-1} style={{ position: 'absolute', inset: 0, outline: 'none' }}>
            {essayBlock}
          </div>
        ) : splitItem ? (
          <div className="ivori-exam-split-body" ref={splitBodyRef} style={{ position: 'absolute', inset: 0, display: 'flex', minHeight: 0 }}>
            <style>{`
              /* --ivori-split-pane-height is set inline on both panes (see
                 splitPanePassageStyle/splitPaneQuestionStyle) but is only
                 ever consumed here — the IVORI branch's own copy of this
                 rule lives in its own <style> block below, which a Pearson
                 sitting's return never reaches, so the drag-resize/collapse
                 state silently had nothing to apply to without this. */
              @media (max-width: 768px) {
                .ivori-exam-split-body {
                  flex-direction: column !important;
                  overflow: hidden !important;
                }
                .ivori-exam-passage-pane,
                .ivori-exam-question-pane {
                  flex: none !important;
                  width: 100% !important;
                  overflow-y: auto !important;
                  min-height: 44px !important;
                  max-height: none !important;
                  box-sizing: border-box !important;
                  height: var(--ivori-split-pane-height, 50%) !important;
                }
                .ivori-exam-split-divider {
                  width: 100% !important;
                  height: 24px !important;
                  margin: 0 !important;
                  flex: none !important;
                  position: relative !important;
                  cursor: row-resize !important;
                  touch-action: none !important;
                  display: flex !important;
                  align-items: center !important;
                  justify-content: center !important;
                  background: transparent !important;
                }
                .ivori-exam-split-divider-grip {
                  width: 36px;
                  height: 4px;
                  border-radius: 2px;
                  background: #C7C2B4;
                  pointer-events: none;
                }
              }
            `}</style>
            <div
              className="ivori-exam-passage-pane"
              ref={passagePaneRef}
              tabIndex={-1}
              onScroll={handlePaneScroll(passagePaneSeenBottomRef)}
              onClick={handlePassagePaneClick}
              style={{
                flex: '1 1 60%',
                minWidth: 0,
                overflowY: 'auto',
                padding: '20px 18px 56px',
                outline: 'none',
                fontFamily: T.fontUi,
                ...splitPanePassageStyle,
              }}
            >
              {splitPaneCollapsed === 'passage' ? (
                <span style={{ fontSize: 13, fontWeight: 700 }}>Passage</span>
              ) : mostLeastPhoneSplit ? (
                mostLeastReadingBlock
              ) : (
                wrapLnatPane(
                  'passage',
                  (
                <MathContent
                  style={pearsonQuestionStyle}
                  className="question-text mathjax-process tex2jax_process"
                  html={prepareSittingHtml(String(currentQuestion.exhibit_text || ''))}
                />
                  )
                )
              )}
            </div>
            <div
              className="ivori-exam-split-divider"
              onMouseDown={handleSplitDividerPointerDown}
              onTouchStart={handleSplitDividerPointerDown}
              style={{ flex: 'none', width: 7, background: 'var(--iv-btn)' }}
            >
              <span className="ivori-exam-split-divider-grip" />
            </div>
            <div
              key={currentQuestion.id}
              className="ivori-exam-question-pane"
              ref={questionPaneRef}
              tabIndex={-1}
              onScroll={handlePaneScroll(questionPaneSeenBottomRef)}
              onClick={() => handleSplitPaneLabelTap('question')}
              style={{
                flex: '1 1 40%',
                minWidth: 0,
                minHeight: 0,
                overflowY: mostLeastPhoneSplit ? 'hidden' : 'auto',
                display: mostLeastPhoneSplit ? 'flex' : undefined,
                flexDirection: mostLeastPhoneSplit ? 'column' : undefined,
                padding: mostLeastPhoneSplit ? '12px 12px 56px' : '20px 18px 56px',
                outline: 'none',
                fontFamily: T.fontUi,
                ...splitPaneQuestionStyle,
              }}
            >
              {splitPaneCollapsed === 'question' ? (
                <span style={{ fontSize: 13, fontWeight: 700 }}>Question</span>
              ) : mostLeastPhoneSplit ? (
                <div style={{ flex: 1, minHeight: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  {answerBlock}
                </div>
              ) : (
                <>
                  {stemBlock}
                  {answerBlock}
                </>
              )}
            </div>
          </div>
        ) : (
          <div
            key={currentQuestion.id}
            className="ivori-exam-question-pane ivori-exam-single-pane"
            ref={questionPaneRef}
            tabIndex={-1}
            onScroll={handlePaneScroll(questionPaneSeenBottomRef)}
            style={{
              position: 'absolute',
              inset: 0,
              overflowY: 'auto',
              overscrollBehavior: 'contain',
              WebkitOverflowScrolling: 'touch',
              touchAction: 'pan-y',
              padding: '20px 28px 56px',
              outline: 'none',
              fontFamily: T.fontUi,
            }}
          >
            {stemBlock}
            {answerBlock}
          </div>
        )}
        {dialogs}
        {isUcatExam && ucatCalculatorOpen ? (
          <UcatPearsonCalculator
            open={ucatCalculatorOpen}
            enabled={ucatCalculatorEnabled}
            onEnable={() => setUcatCalculatorEnabled(true)}
            onDisable={() => setUcatCalculatorEnabled(false)}
            onClose={() => setUcatCalculatorOpen(false)}
          />
        ) : null}
        {isUcatExam && ucatHelpOpen ? (
          <UcatPearsonHelpOverlay onClose={() => setUcatHelpOpen(false)} />
        ) : null}
      </TmuaPearsonShell>
    );
  }

  /* =================================================================== */
  /* ===== 3. RESULTS & EXAM REVIEW VIEW (SUBMITTED) ================== */
  /* =================================================================== */
  if (isSubmitted && !pearsonPlayer) {
    return (
      <div className="quiz-gen-main-content quiz-gen-main-content-active quiz-gen-vh-fallback" style={{ minWidth: '100%', background: isTmuaExam ? SIM_SCHEME.body : 'var(--iv-bg)', fontFamily: 'var(--iv-font-body)', padding: 0 }} ref={containerRef}>
        {/* Top Header Bar */}
        <div className="ivori-review-top-bar" style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', borderBottom: '1px solid var(--iv-line)', padding: '14px 28px 14px 72px', background: isTmuaExam ? SIM_SCHEME.chrome : 'var(--iv-bg)', boxSizing: 'border-box' }}>
          {/* Top Left — Exit Button */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => {
                onSubmit();
                onBack();
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                background: 'transparent',
                border: '1px solid var(--iv-line)',
                borderRadius: '999px',
                padding: '6px 18px',
                fontSize: '13px',
                fontWeight: '600',
                color: 'var(--iv-body)',
                cursor: 'pointer'
              }}
            >
              Exit
            </button>
          </div>

          {/* Top Right — Category Label */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '16px' }}>
            {isTmuaExam && (
              <select
                value={colorScheme}
                onChange={(e) => setColorScheme(e.target.value)}
                style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: SIM_SCHEME.text,
                  fontFamily: 'var(--iv-font-body)',
                  background: SIM_SCHEME.chrome,
                  border: `1.5px solid ${SIM_SCHEME.border}`,
                  borderRadius: '4px',
                  padding: '5px 10px',
                  cursor: 'pointer'
                }}
              >
                {Object.entries(getColorSchemesForExam('TMUA')).map(([key, scheme]) => (
                  <option key={key} value={key}>{scheme.label}</option>
                ))}
              </select>
            )}
            <span style={{ fontFamily: 'var(--iv-font-body)', fontWeight: 700, fontSize: '11px', letterSpacing: '0.14em', color: 'var(--iv-muted)', textTransform: 'uppercase' }}>
              RESULTS OVERVIEW
            </span>
          </div>
        </div>

        {/* Main Results Container */}
        <div className="ivori-review-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', padding: '40px 24px 80px', boxSizing: 'border-box' }}>
          <div className="ivori-review-inner-container" style={{ width: '100%', maxWidth: '960px', boxSizing: 'border-box' }}>
            
            {/* Page Header */}
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '28px', flexWrap: 'wrap', gap: '16px' }}>
              <div>
                <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '0.18em', color: 'var(--iv-muted)', marginBottom: '6px' }}>YOUR ATTEMPT · COMPLETED</div>
                <h1 style={{ fontFamily: 'var(--iv-font-head)', fontWeight: 700, fontSize: '36px', margin: 0, color: 'var(--iv-body)', lineHeight: 1.1 }}>Results Overview</h1>
              </div>
              <button
                type="button"
                onClick={() => {
                  setIsSubmitted(false);
                  setCurrentQuestionIndex(0);
                  setFlaggedQuestions({});
                  setVisitedQuestions({});
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '13.5px',
                  fontWeight: '600',
                  color: 'var(--iv-body)',
                  background: 'var(--iv-raised)',
                  border: '1px solid var(--iv-line)',
                  borderRadius: '999px',
                  padding: '10px 22px',
                  cursor: 'pointer'
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="1 4 1 10 7 10"></polyline>
                  <path d="M3.5 15a9 9 0 1 0 2-9.5L1 10"></path>
                </svg>
                Retake Exam
              </button>
            </div>

            {/* Balanced 3-Column Score Summary Card */}
            <div className="ivori-review-score-card" style={{ background: isTmuaExam ? SIM_SCHEME.body : 'var(--iv-bg)', border: '1px solid var(--iv-line)', borderRadius: '20px', padding: '32px', boxShadow: '0 12px 40px color-mix(in srgb, var(--iv-shadow) 5%, transparent)', marginBottom: '40px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px', marginBottom: '24px', alignItems: 'center' }}>
                
                {/* Column 1: Overall Score */}
                <div style={{ display: 'flex', flexDirection: 'column', borderRight: '1px solid var(--iv-line)', paddingRight: '20px' }}>
                  <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', color: tmuaInk('var(--iv-muted)'), marginBottom: '8px', textTransform: 'uppercase' }}>OVERALL SCORE</span>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                    <span style={{ fontFamily: 'var(--iv-font-body)', fontWeight: 800, fontSize: '48px', lineHeight: 1, color: tmuaInk('var(--iv-head)') }}>{stats.correct}</span>
                    <span style={{ fontFamily: 'var(--iv-font-body)', fontWeight: 600, fontSize: '22px', color: tmuaInk('var(--iv-muted)') }}>/ {stats.total}</span>
                  </div>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--iv-muted)', marginTop: '6px' }}>{stats.pct} correct</span>
                </div>

                {/* Column 2: Correct Answers */}
                <div style={{ display: 'flex', flexDirection: 'column', borderRight: '1px solid var(--iv-line)', paddingRight: '20px', paddingLeft: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--iv-muted)' }}></span>
                    <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', color: 'var(--iv-muted)', textTransform: 'uppercase' }}>CORRECT ANSWERS</span>
                  </div>
                  <span style={{ fontFamily: 'var(--iv-font-body)', fontWeight: 700, fontSize: '36px', color: 'var(--iv-body)' }}>{stats.correct}</span>
                </div>

                {/* Column 3: Incorrect Answers */}
                <div style={{ display: 'flex', flexDirection: 'column', paddingLeft: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--iv-danger)' }}></span>
                    <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', color: 'var(--iv-muted)', textTransform: 'uppercase' }}>INCORRECT ANSWERS</span>
                  </div>
                  <span style={{ fontFamily: 'var(--iv-font-body)', fontWeight: 700, fontSize: '36px', color: 'var(--iv-body)' }}>{stats.total - stats.correct}</span>
                </div>

              </div>

              {/* Score Bar */}
              <div style={{ display: 'flex', height: '8px', borderRadius: '999px', overflow: 'hidden', background: 'var(--iv-bg)' }}>
                <span style={{ width: stats.barCorrect, background: 'var(--iv-muted)', transition: 'width 0.3s ease' }}></span>
                <span style={{ width: stats.barIncorrect, background: 'var(--iv-danger)', transition: 'width 0.3s ease' }}></span>
              </div>
            </div>

            {/* Answer breakdown list */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
              <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: 'var(--iv-accent)' }}></span>
              <h2 style={{ fontFamily: 'var(--iv-font-head)', fontWeight: 700, fontSize: '22px', margin: 0, color: 'var(--iv-body)' }}>Answer breakdown</h2>
              <span style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '0.16em', color: 'var(--iv-muted)', marginLeft: '4px', fontFamily: 'var(--iv-font-body)' }}>TAP A QUESTION TO REVIEW EXPLANATIONS</span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {questions.map((q, idx) => {
                const isMC = q.multiple_choice !== false && q.options && q.options.length > 1;
                const ans = userAnswers[q.id];
                const isCorrect = isMC ? (ans && ans.label === q.correct_answer) : (ans && ans.text_response && ans.text_response.trim().length > 0);
                const stemSnippet = questionPreview(q.question);

                return (
                  <div 
                    key={q.id} 
                    className="ivori-review-item-row"
                    onClick={() => handleExplainOpen(idx)}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '16px', 
                      background: 'var(--iv-bg)', 
                      border: '1px solid var(--iv-line)', 
                      borderLeft: `3.5px solid ${isCorrect ? 'var(--iv-success)' : 'var(--iv-danger)'}`, 
                      borderRadius: '10px', 
                      padding: '14px 18px', 
                      cursor: 'pointer', 
                      boxShadow: '0 1px 3px color-mix(in srgb, var(--iv-shadow) 4%, transparent)',
                      boxSizing: 'border-box'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flex: 1, minWidth: 0 }}>
                      <span style={{ flex: 'none', width: '36px', height: '36px', borderRadius: '8px', background: 'var(--iv-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--iv-font-body)', fontWeight: 600, fontSize: '15px', color: 'var(--iv-head)' }}>
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.12em', color: 'var(--iv-muted)', marginBottom: '3px', fontFamily: 'var(--iv-font-body)' }}>QUESTION {String(idx + 1).padStart(2, '0')}</div>
                        <div style={{ fontSize: '13.5px', fontWeight: 400, color: 'var(--iv-body)', lineHeight: '1.35', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'var(--iv-font-body)' }} className="mathjax-process">
                          {stemSnippet}
                        </div>
                      </div>
                    </div>

                    <div className="ivori-review-item-right" style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '9.5px', fontWeight: 600, letterSpacing: '0.08em', color: 'var(--iv-muted)', marginBottom: '2px', fontFamily: 'var(--iv-font-body)' }}>YOUR ANSWER</div>
                        <div style={{ fontSize: '13.5px', fontWeight: 700, color: isCorrect ? 'var(--iv-body)' : 'var(--iv-danger)', fontFamily: 'var(--iv-font-body)' }}>
                          {isMC ? (ans?.label || 'None') : (ans?.text_response ? 'Written' : 'None')}
                        </div>
                      </div>

                      {isMC && (
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '9.5px', fontWeight: 600, letterSpacing: '0.08em', color: 'var(--iv-muted)', marginBottom: '2px', fontFamily: 'var(--iv-font-body)' }}>CORRECT</div>
                          <div style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--iv-body)', fontFamily: 'var(--iv-font-body)' }}>{q.correct_answer}</div>
                        </div>
                      )}

                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '10px', fontWeight: 700, letterSpacing: '0.06em', color: isCorrect ? 'var(--iv-success)' : 'var(--iv-danger)', background: isCorrect ? 'var(--iv-bg)' : 'var(--iv-bg)', padding: '5px 11px', borderRadius: '999px', justifyContent: 'center', fontFamily: 'var(--iv-font-body)' }}>
                        {isCorrect ? '✓ CORRECT' : '✗ INCORRECT'}
                      </span>

                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--iv-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9,6 15,12 9,18"></polyline>
                      </svg>
                    </div>
                  </div>
                );
              })}
            </div>

          </div>
        </div>

        {/* Global Modal Explanation Overlay */}
        {renderExplainModal()}
      </div>
    );
  }

  /* =================================================================== */
  /* ===== 4. PEARSON VUE SIMULATION LAYOUT =========================== */
  /* =================================================================== */
  if (examMode === 'pearson') {
    // Pearson VUE Navigator List Overlay
    if (reviewScreenOpen) {
      return (
        <div className="quiz-gen-vh-fallback" style={{ minWidth: '100%', background: '#F4F4F4', fontFamily: "'Inter', sans-serif", color: '#333', padding: '30px' }} ref={containerRef}>
          <div style={{ background: 'var(--iv-surface)', border: '1px solid var(--iv-line)', padding: '24px', borderRadius: '4px', maxWidth: '900px', margin: '0 auto', boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }}>
            <h2 style={{ color: 'var(--iv-accent)', borderBottom: '2px solid var(--iv-accent)', paddingBottom: '10px', marginTop: 0 }}>Item Review Screen</h2>
            <p style={{ fontSize: '13px', margin: '10px 0 20px' }}>Below is a summary of your answers. Click any question row to select it, then click <strong>Go to Question</strong> to jump directly to that question. You can also review questions using the filters below.</p>
            
            <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid var(--iv-line)', marginBottom: '20px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: 'var(--iv-bg)', borderBottom: '1px solid var(--iv-line)' }}>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Question Number</th>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Status</th>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Flagged</th>
                  </tr>
                </thead>
                <tbody>

                  {(reviewTab === 'all'
                    ? questions
                    : questions.filter((q) => {
                        const ans = userAnswers[q.id];
                        const isMC =
                          q.multiple_choice !== false &&
                          q.options &&
                          q.options.length > 1;

                        return isMC
                          ? !ans?.label
                          : !ans?.text_response?.trim();
                      })
                  ).map((q) => {

                    const idx = questions.findIndex((question) => question.id === q.id);
                    const ans = userAnswers[q.id];

                    const isMC =
                      q.multiple_choice !== false &&
                      q.options &&
                      q.options.length > 1;

                    const status = isMC
                      ? (ans?.label ? 'Answered' : 'Unanswered')
                      : (ans?.text_response ? 'Answered' : 'Unanswered');

                    const isFlagged = flaggedQuestions[q.id];

                    const isSelected = selectedReviewQuestion === idx;

                    return (
                      <tr 
                        key={q.id}
                        onClick={() => setSelectedReviewQuestion(idx)}
                        style={{ borderBottom: '1px solid var(--iv-line)', 
                          background: isSelected ? 'var(--iv-raised)' : 'transparent',
                          cursor: 'pointer' }}
                      >
                        <td style={{ padding: '10px' }}>Question {idx + 1}</td>
                        <td style={{ padding: '10px', color: status === 'Answered' ? 'var(--iv-success)' : 'var(--iv-danger)', fontWeight: 'bold' }}>{status}</td>
                        <td style={{ padding: '10px' }}>{isFlagged ? '🚩 Flagged' : 'No'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button 
                onClick={() => {
                  setCurrentQuestionIndex(0);
                  setReviewScreenOpen(false);
                }} 
                style={{ background: 'var(--iv-band)', color: 'var(--iv-on-band)', border: 'none', padding: '10px 18px', borderRadius: '3px', cursor: 'pointer', fontWeight: 'bold' }}
              >
                Review All
              </button>
              <button 
                onClick={() => {
                  const idx = getUnansweredIndex();
                  if (idx !== -1) setCurrentQuestionIndex(idx);
                  setReviewScreenOpen(false);
                }} 
                style={{ background: 'var(--iv-band)', color: 'var(--iv-on-band)', border: 'none', padding: '10px 18px', borderRadius: '3px', cursor: 'pointer', fontWeight: 'bold' }}
              >
                Review Unanswered
              </button>
              <button 
                onClick={() => {
                  const idx = getFlaggedIndex();
                  if (idx !== -1) setCurrentQuestionIndex(idx);
                  setReviewScreenOpen(false);
                }} 
                style={{ background: 'var(--iv-band)', color: 'var(--iv-on-band)', border: 'none', padding: '10px 18px', borderRadius: '3px', cursor: 'pointer', fontWeight: 'bold' }}
              >
                Review Flagged
              </button>
              <button 
                onClick={() => {
                  setCurrentQuestionIndex(selectedReviewQuestion);
                  setReviewScreenOpen(false);
                }} 
                style={{ background: 'var(--iv-band)', color: 'var(--iv-on-band)', border: 'none', padding: '10px 18px', borderRadius: '3px', cursor: 'pointer', fontWeight: 'bold' }}
              >
                Go to Question
              </button>
              <button 
                onClick={() => setReviewScreenOpen(false)} 
                style={{ background: 'var(--iv-fill)', color: 'var(--iv-body)', border: '1px solid var(--iv-line)', padding: '10px 18px', borderRadius: '3px', cursor: 'pointer' }}
              >
                Close Navigator
              </button>
            </div>
          </div>
        </div>
      );
    }

    const isMC = currentQuestion.multiple_choice !== false && currentQuestion.options && currentQuestion.options.length > 1;

    // Accessibility Contrast Themes configuration
    const pearsonThemes = {
      default: { bg: '#FFFFFF', text: '#222222', cardBg: '#FAFAFA', cardBorder: '#E2E8F0', selectionBg: '#EBF8FF', selectionBorder: '#3182CE' },
      'black-on-light-yellow': { bg: '#FFFFE0', text: '#000000', cardBg: '#F9F9DB', cardBorder: '#D8D8B6', selectionBg: '#E6E6A8', selectionBorder: '#000000' },
      'black-on-salmon': { bg: '#FFE4E1', text: '#000000', cardBg: '#F5C4C0', cardBorder: '#D49F9B', selectionBg: '#EAA29D', selectionBorder: '#000000' },
      'black-on-white': { bg: '#FFFFFF', text: '#000000', cardBg: '#FFFFFF', cardBorder: '#CCCCCC', selectionBg: '#EAEAEA', selectionBorder: '#000000' },
      'black-on-yellow': { bg: '#FFFF00', text: '#000000', cardBg: '#EAE600', cardBorder: '#B3B000', selectionBg: '#C7C400', selectionBorder: '#000000' }
    };

    const currentPearsonTheme = pearsonThemes[pearsonColorScheme] || pearsonThemes.default;

    // Static Font Size configuration
    const currentPearsonFontSize = { passage: '15px', question: '15.5px', option: '14.5px' };

    return (
      <div className="quiz-gen-vh-fallback" style={{ minWidth: '100%', background: currentPearsonTheme.bg, fontFamily: "'Inter', sans-serif", color: currentPearsonTheme.text, display: 'flex', flexDirection: 'column' }} ref={containerRef}>
        
        {/* Pearson top bar with accessibility selectors */}
        <div style={{ background: '#005A9C', color: '#FFF', padding: '10px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '4px solid #F0C136' }}>
          <div style={{ fontWeight: 'bold', fontSize: '15px', display: 'flex', alignItems: 'center', gap: '24px' }}>
            <span>LNAT Practice — Section A</span>
            
            {/* Color Scheme Dropdown Selector */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 'normal' }}>
              <span style={{ color: 'var(--iv-on-band)' }}>Color Scheme:</span>
              <select 
                value={pearsonColorScheme} 
                onChange={(e) => setPearsonColorScheme(e.target.value)}
                style={{ background: '#FFF', color: '#000', border: '1px solid #707070', borderRadius: '3px', padding: '3px 8px', fontSize: '12px', cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}
              >
                <option value="default">Default</option>
                <option value="black-on-light-yellow">Black on Light Yellow</option>
                <option value="black-on-salmon">Black on Salmon</option>
                <option value="black-on-white">Black on White</option>
                <option value="black-on-yellow">Black on Yellow</option>
              </select>
            </div>


          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px', fontSize: '13px' }}>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              Time Remaining: <strong>{displayTime}</strong>
            </span>
            <span>|</span>
            <span>Question {currentQuestionIndex + 1} of {totalQuestions}</span>
            <span>|</span>
            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
              <input 
                type="checkbox" 
                checked={!!flaggedQuestions[currentQuestion.id]} 
                onChange={() => handleFlag(currentQuestion.id)} 
              />
              Flag for Review
            </label>
          </div>
        </div>

        {/* Content body split */}
        <div style={{ flex: 1, display: 'flex', background: currentPearsonTheme.bg, minHeight: '0' }}>
          
          {/* Left panel: Passage */}
          <div style={{ flex: 1.2, padding: '24px', overflowY: 'auto', borderRight: `2px solid ${currentPearsonTheme.cardBorder}`, background: currentPearsonTheme.bg, color: currentPearsonTheme.text }}>
            <h4 style={{ color: '#005A9C', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '1px', marginTop: 0, marginBottom: '14px', borderBottom: `1px solid ${currentPearsonTheme.cardBorder}`, paddingBottom: '6px' }}>Passage</h4>
            <div
              style={{ fontSize: currentPearsonFontSize.passage, lineHeight: '1.6', color: currentPearsonTheme.text }}
              className="mathjax-process tex2jax_process exhibit-clickable-html"
              onClick={handleExhibitTextClick}
              dangerouslySetInnerHTML={{ __html: prepareSittingHtml(currentQuestion.exhibit_text || 'Refer to the passage details.') }}
            />
          </div>

          {/* Right panel: Question + options */}
          <div style={{ flex: 1, padding: '24px', overflowY: 'auto', display: 'flex', flexDirection: 'column', background: currentPearsonTheme.bg, color: currentPearsonTheme.text }}>
            <h4 style={{ color: '#005A9C', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '1px', marginTop: 0, marginBottom: '14px', borderBottom: `1px solid ${currentPearsonTheme.cardBorder}`, paddingBottom: '6px' }}>Question</h4>
            <div 
              style={{ fontSize: currentPearsonFontSize.question, fontWeight: 400, lineHeight: '1.5', color: currentPearsonTheme.text, marginBottom: '20px' }} 
              className="question-text mathjax-process tex2jax_process"
              dangerouslySetInnerHTML={{ __html: prepareSittingHtml(currentQuestion.question) }}
            />

            {isMC ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {currentQuestion.options.map(option => {
                  const isSelected = userAnswers[currentQuestion.id]?.label === option.label;
                  return (
                    <div 
                      key={option.label}
                      onClick={() => selectOption(currentQuestion.id, option.label, currentQuestion)}
                      style={{ display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px', 
                        padding: '12px', 
                        background: isSelected ? currentPearsonTheme.selectionBg : currentPearsonTheme.cardBg, 
                        border: isSelected ? `1.5px solid ${currentPearsonTheme.selectionBorder}` : `1px solid ${currentPearsonTheme.cardBorder}`, 
                        borderRadius: '4px', 
                        cursor: 'pointer' }}
                    >
                      <input 
                        type="radio" 
                        name={`q_${currentQuestion.id}`} 
                        checked={isSelected}
                        onChange={() => {}} // handled by click
                        style={{ cursor: 'pointer' }}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: currentPearsonFontSize.option, color: currentPearsonTheme.text }}>{option.label}.</span>
                      <span style={{ fontSize: currentPearsonFontSize.option, color: currentPearsonTheme.text }} className="ivori-option-text mathjax-process tex2jax_process" dangerouslySetInnerHTML={{ __html: prepareSittingHtml(option.text) }} />
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <textarea
                  className="text-answer-input"
                  placeholder="Type your essay response here..."
                  value={userAnswers[currentQuestion.id]?.text_response || ''}
                  onChange={(e) => handleTextInput(currentQuestion.id, currentQuestion, e.target.value)}
                  style={{ width: '100%', minHeight: '220px', padding: '12px', border: `1px solid ${currentPearsonTheme.cardBorder}`, borderRadius: '4px', fontName: 'Arial', fontSize: currentPearsonFontSize.option, background: currentPearsonTheme.bg, color: currentPearsonTheme.text }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Pearson footer navigation */}
        <div style={{ background: '#E0E0E0', padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '2px solid #C0C0C0' }}>
          <div>
            <button 
              onClick={() => setReviewScreenOpen(true)}
              style={{ background: 'var(--iv-bg)', border: '1px solid var(--iv-line)', padding: '6px 16px', borderRadius: '3px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' }}
            >
              Navigator (Review Items)
            </button>
          </div>
          
          <div style={{ display: 'flex', gap: '10px' }}>
            <button 
              onClick={handlePrev}
              disabled={currentQuestionIndex === 0}
              style={{ background: currentQuestionIndex === 0 ? 'var(--iv-fill)' : 'var(--iv-surface)', border: '1px solid var(--iv-line)', padding: '6px 16px', borderRadius: '3px', cursor: currentQuestionIndex === 0 ? 'default' : 'pointer', fontWeight: 'bold', fontSize: '13px' }}
            >
              Previous
            </button>
            <button 
              onClick={handleNext}
              disabled={currentQuestionIndex === totalQuestions - 1}
              style={{ background: currentQuestionIndex === totalQuestions - 1 ? 'var(--iv-fill)' : 'var(--iv-surface)', border: '1px solid var(--iv-line)', padding: '6px 16px', borderRadius: '3px', cursor: currentQuestionIndex === totalQuestions - 1 ? 'default' : 'pointer', fontWeight: 'bold', fontSize: '13px' }}
            >
              Next
            </button>
            <button 
              onClick={handleReviewSubmit}
              style={{ background: 'color-mix(in srgb, var(--iv-accent) 22%, transparent)', border: '1px solid var(--iv-line)', padding: '6px 20px', borderRadius: '3px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' }}
            >
              Submit Quiz
            </button>
          </div>
        </div>

      </div>
    );
  }

  /* =================================================================== */
  /* ===== 5. IVORI SIMULATION LAYOUT (DEFAULT SPLIT SCREEN) =========== */
  /* =================================================================== */
  const isMCActive = currentQuestion.multiple_choice !== false && currentQuestion.options && currentQuestion.options.length > 1;
  const isFlagged = flaggedQuestions[currentQuestion.id];
  const userAnswer = userAnswers[currentQuestion.id];
  const progressPct = `${((currentQuestionIndex + 1) / totalQuestions * 100).toFixed(1)}%`;

  // Static Font Size configuration
  const currentIvoriFontSize = { passage: '16.5px', question: '17.5px', option: '14.5px' };

  // The header/footer redesign (title bar, End Exam button, hidden global back
  // button) is TMUA-only — every other exam (ESAT, LNAT, etc.) keeps the
  // original layout below. (isTmuaExam is computed near the top of the
  // component so it's also in scope for the results/review screens above.)

  return (
    <div 
      className="quiz-gen-simulation-fullscreen" 
      style={{ position: 'fixed', inset: 0, zIndex: 9999, background: t.ground, color: t.text, fontFamily: 'var(--iv-font-body)', display: 'flex', overflow: 'hidden', outline: 'none' }} 
      ref={containerRef}
      tabIndex={0}
      onWheel={handleContainerWheel}
    >
      <style>{`
        ${isTmuaExam ? `
        /* Hide the global floating back button while the exam is in progress.
           It reappears automatically once the exam ends (isSubmitted flips
           this component to the separate results-view return, which doesn't
           render this style block), so "back" is only available after ending.
           TMUA-only per product request. */
        .product-back-btn {
          display: none !important;
        }
        /* Override any grey baked into question HTML so stems stay near-black. */
        .question-text,
        .question-text * {
          color: ${SIM_SCHEME.text} !important;
        }
        /* Question tables follow the active TMUA color scheme (beats QuizGeneratorPage.css). */
        .quiz-gen-simulation-fullscreen .mathjax-process table {
          background: ${SIM_SCHEME.chrome} !important;
          border: 1px solid ${colorScheme === 'ivori' ? 'var(--iv-line)' : SIM_SCHEME.border} !important;
        }
        .quiz-gen-simulation-fullscreen .mathjax-process th,
        .quiz-gen-simulation-fullscreen .mathjax-process td {
          border-bottom: 1px solid ${colorScheme === 'ivori' ? 'var(--iv-line)' : SIM_SCHEME.border} !important;
          background: ${SIM_SCHEME.chrome} !important;
          color: ${SIM_SCHEME.text} !important;
        }
        .quiz-gen-simulation-fullscreen .mathjax-process th {
          background: ${colorScheme === 'ivori' ? 'var(--iv-surface)' : SIM_SCHEME.chrome} !important;
          border-bottom: 1.5px solid ${colorScheme === 'ivori' ? 'var(--iv-line)' : SIM_SCHEME.border} !important;
        }
        .quiz-gen-simulation-fullscreen .mathjax-process tr:hover {
          background: ${colorScheme === 'ivori' ? 'var(--iv-raised)' : SIM_SCHEME.chrome} !important;
        }
        .quiz-gen-simulation-fullscreen .mathjax-process tr:hover th,
        .quiz-gen-simulation-fullscreen .mathjax-process tr:hover td {
          background: ${colorScheme === 'ivori' ? 'var(--iv-raised)' : SIM_SCHEME.chrome} !important;
        }
        ` : ''}

        /* Sleek custom scrollbars for IVORI exam panes */
        .ivori-exam-main-panel,
        .ivori-exam-passage-pane,
        .ivori-exam-question-pane,
        .quiz-gen-simulation-fullscreen {
          scrollbar-width: thin;
          scrollbar-color: color-mix(in srgb, var(--iv-head) 35%, transparent) color-mix(in srgb, var(--iv-head) 4%, transparent);
        }
        .ivori-exam-main-panel::-webkit-scrollbar,
        .ivori-exam-passage-pane::-webkit-scrollbar,
        .ivori-exam-question-pane::-webkit-scrollbar,
        .quiz-gen-simulation-fullscreen::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        .ivori-exam-main-panel::-webkit-scrollbar-track,
        .ivori-exam-passage-pane::-webkit-scrollbar-track,
        .ivori-exam-question-pane::-webkit-scrollbar-track,
        .quiz-gen-simulation-fullscreen::-webkit-scrollbar-track {
          background: var(--iv-fill);
        }
        .ivori-exam-main-panel::-webkit-scrollbar-thumb,
        .ivori-exam-passage-pane::-webkit-scrollbar-thumb,
        .ivori-exam-question-pane::-webkit-scrollbar-thumb,
        .quiz-gen-simulation-fullscreen::-webkit-scrollbar-thumb {
          background: color-mix(in srgb, var(--iv-head) 35%, transparent);
          border-radius: 4px;
        }
        .ivori-exam-main-panel::-webkit-scrollbar-thumb:hover,
        .ivori-exam-passage-pane::-webkit-scrollbar-thumb:hover,
        .ivori-exam-question-pane::-webkit-scrollbar-thumb:hover,
        .quiz-gen-simulation-fullscreen::-webkit-scrollbar-thumb:hover {
          background: color-mix(in srgb, var(--iv-head) 55%, transparent);
        }

        /* Transparent wrapper round a standard question's body (see the 768px block). */
        .ivori-exam-body-slot {
          display: contents;
        }

        .ivori-exam-passage-pane,
        .ivori-exam-question-pane {
          box-sizing: border-box !important;
          word-break: break-word !important;
          overflow-wrap: break-word !important;
        }

        .ivori-review-score-left {
          flex: 0 0 260px;
          border-right: 1px solid var(--iv-line);
        }
        .ivori-review-score-right {
          flex: 1 1 auto;
        }

        /* Mobile & Tablet responsive scrollable layout */
        @media (max-width: 900px) {
          .quiz-gen-simulation-fullscreen {
            flex-direction: column !important;
            overflow-y: auto !important;
            position: fixed !important;
            inset: 0 !important;
            -webkit-overflow-scrolling: touch;
          }
          .ivori-exam-sidebar {
            flex: none !important;
            width: 100% !important;
            padding: 24px 20px !important;
            border-right: none !important;
            border-bottom: 1px solid var(--iv-line) !important;
          }
          .ivori-exam-main-panel {
            flex: 1 1 auto !important;
            width: 100% !important;
            min-height: max-content !important;
            overflow-y: visible !important;
          }
          .ivori-exam-top-bar {
            padding: 14px 16px 14px 60px !important;
          }
          .top-bar-category-title,
          .top-bar-dot {
            display: none !important;
          }
          .ivori-review-top-bar {
            padding: 12px 16px 12px 60px !important;
            flex-direction: row !important;
            align-items: center !important;
            justify-content: space-between !important;
          }
          .ivori-review-top-bar-title {
            display: none !important;
          }
          .ivori-review-container {
            padding: 16px 12px 40px !important;
          }
          .ivori-review-score-card {
            display: flex !important;
            flex-direction: column !important;
            width: 100% !important;
            box-sizing: border-box !important;
          }
          .ivori-review-score-left {
            flex: none !important;
            width: 100% !important;
            box-sizing: border-box !important;
            padding: 20px 16px !important;
            border-right: none !important;
            border-bottom: 1px solid var(--iv-line) !important;
            align-items: center !important;
            text-align: center !important;
          }
          .ivori-review-score-left > div {
            justify-content: center !important;
          }
          .ivori-review-score-right {
            flex: none !important;
            width: 100% !important;
            box-sizing: border-box !important;
            padding: 20px 16px !important;
          }
          .ivori-review-stats-row {
            display: flex !important;
            flex-direction: row !important;
            justify-content: space-around !important;
            gap: 12px !important;
            width: 100% !important;
          }
          .ivori-review-stats-row > div {
            align-items: center !important;
            text-align: center !important;
          }
          .ivori-review-item-row {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 12px !important;
            padding: 14px !important;
          }
          .ivori-review-item-right {
            width: 100% !important;
            justify-content: space-between !important;
            gap: 8px !important;
            border-top: 1px solid var(--iv-line);
            padding-top: 10px;
          }
        }

        /* Mobile-only rules (768px), added separately from the 900px block
           above per docs/plans/MOBILE_EXAM_RUNNER_PLAN.md — only touches split-pane /
           single-pane / MathJax selectors, never the review-screen or
           top-bar rules the 900px block also owns. */
        @media (max-width: 768px) {
          .ivori-exam-single-pane {
            padding-bottom: 64px !important;
          }
          .ivori-exam-question-pane label,
          .ivori-exam-passage-pane label {
            min-height: 44px;
          }
          mjx-container[display="true"] {
            overflow-x: auto !important;
            max-width: 100% !important;
            display: block !important;
          }

          /* Split-pane stacking + interactive divider. Replaces (not
             extends) the old 900px/992px split-pane declarations, which
             this plan deliberately leaves untouched at their own
             breakpoint for every OTHER selector they cover (review screen,
             top bar, sidebar). See docs/plans/MOBILE_EXAM_RUNNER_PLAN.md Phase 4. */
          .ivori-exam-main-panel {
            position: relative !important;
          }
          /* The body is absolutely positioned (below), so it has to sit in a box of its
             own between the top bar and the footer; against the whole main panel it
             covered both, hiding the timer, Flag for review and Next/Previous. */
          .ivori-exam-main-panel:has(> .ivori-exam-body-slot) {
            flex: 1 1 0% !important;
            min-height: 0 !important;
          }
          .ivori-exam-body-slot {
            display: block !important;
            position: relative !important;
            flex: 1 1 0% !important;
            min-height: 0 !important;
          }
          .ivori-exam-split-body {
            flex-direction: column !important;
            position: absolute !important;
            inset: 0 !important;
            overflow: hidden !important;
          }
          .ivori-exam-passage-pane,
          .ivori-exam-question-pane {
            flex: none !important;
            width: 100% !important;
            overflow-y: auto !important;
            min-height: 44px !important;
            max-height: none !important;
            box-sizing: border-box !important;
            height: var(--ivori-split-pane-height, 50%) !important;
          }
          /* A question with no passage has nothing to share the screen with: the
             pane takes the whole body (not half of it) and the column inside it
             the pane's full width (not the desktop 50% with its 48px and 32px
             of padding, which left a ~120px column cut off below option A). */
          .ivori-exam-no-passage .ivori-exam-question-pane {
            flex: 1 1 0% !important;
            height: auto !important;
            padding: 20px 16px 64px !important;
          }
          .ivori-exam-no-passage .ivori-exam-question-column {
            width: 100% !important;
            max-width: none !important;
            padding-right: 0 !important;
          }
          .ivori-exam-split-divider {
            width: 100% !important;
            height: 24px !important;
            margin: 0 !important;
            flex: none !important;
            position: relative !important;
            cursor: row-resize !important;
            touch-action: none !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            background: transparent !important;
          }
          .ivori-exam-split-divider-grip {
            width: 36px;
            height: 4px;
            border-radius: 2px;
            background: var(--iv-line);
            pointer-events: none;
          }

          /* Yes/No and Most/Least answer widgets: stack the two-per-row
             options vertically so "Most appropriate"/"Least appropriate"
             text labels don't wrap awkwardly at 360px. */
          .ivori-exam-yesno-row {
            flex-direction: column !important;
            gap: 8px !important;
          }
        }
      `}</style>
      

      {/* IVORI ITEM REVIEW SCREEN */}
      {reviewScreenOpen && !pearsonPlayer && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 20,
            background: isTmuaExam ? SIM_SCHEME.body : 'var(--iv-bg)',
            display: 'flex',
            flexDirection: 'column',
            color: tmuaInk('var(--iv-head)')
          }}
        >
          {/* Review top bar */}
          <div
            className="ivori-review-top-bar"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '18px 32px 18px 72px',
              background: isTmuaExam ? SIM_SCHEME.chrome : 'var(--iv-panel)',
              borderBottom: `1px solid ${isTmuaExam && colorScheme !== 'ivori' ? '#14161F' : 'var(--iv-line)'}`
            }}
          >
            <div className="ivori-review-top-bar-title">
              <div
                style={{
                  fontSize: '11px',
                  fontWeight: '800',
                  letterSpacing: '0.16em',
                  color: 'var(--iv-muted)',
                  textTransform: 'uppercase'
                }}
              >
                REVIEW
              </div>
              <div
                style={{
                  fontFamily: 'var(--iv-font-head)',
                  fontSize: '22px',
                  fontWeight: '700',
                  marginTop: '4px'
                }}
              >
                Item Review
              </div>
            </div>

            {isTmuaExam && (
              <select
                value={colorScheme}
                onChange={(e) => setColorScheme(e.target.value)}
                style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: SIM_SCHEME.text,
                  fontFamily: 'var(--iv-font-body)',
                  background: SIM_SCHEME.chrome,
                  border: `1.5px solid ${SIM_SCHEME.border}`,
                  borderRadius: '4px',
                  padding: '5px 10px',
                  cursor: 'pointer'
                }}
              >
                {Object.entries(getColorSchemesForExam('TMUA')).map(([key, scheme]) => (
                  <option key={key} value={key}>{scheme.label}</option>
                ))}
              </select>
            )}
          </div>

          {/* Review content */}
          <div
            className="ivori-review-container"
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '36px 40px 120px'
            }}
          >
            <div
              style={{
                maxWidth: '900px',
                width: '100%',
                margin: '0 auto'
              }}
            >

              <div
                style={{
                  display: 'flex',
                  gap: '28px',
                  borderBottom: `1px solid ${isTmuaExam && colorScheme !== 'ivori' ? SIM_SCHEME.border : 'var(--iv-line)'}`,
                  marginBottom: '20px'
                }}
              >
                <button
                  type="button"
                  onClick={() => setReviewTab('all')}
                  style={{
                    border: 'none',
                    background: 'none',
                    padding: '0 0 12px',
                    fontFamily: 'var(--iv-font-body)',
                    fontSize: '14px',
                    fontWeight: '700',
                    color: reviewTab === 'all' ? 'var(--iv-body)' : 'var(--iv-muted)',
                    borderBottom: reviewTab === 'all'
                      ? '2px solid var(--iv-head)'
                      : '2px solid transparent',
                    cursor: 'pointer'
                  }}
                >
                  All
                </button>

                <button
                  type="button"
                  onClick={() => setReviewTab('unanswered')}
                  style={{
                    border: 'none',
                    background: 'none',
                    padding: '0 0 12px',
                    fontFamily: 'var(--iv-font-body)',
                    fontSize: '14px',
                    fontWeight: '700',
                    color: reviewTab === 'unanswered' ? 'var(--iv-body)' : 'var(--iv-muted)',
                    borderBottom: reviewTab === 'unanswered'
                      ? '2px solid var(--iv-head)'
                      : '2px solid transparent',
                    cursor: 'pointer'
                  }}
                >
                  Unanswered
                </button>
              </div>

              {/* Question list */}
              <div
                style={{
                  background: isTmuaExam ? SIM_SCHEME.chrome : 'var(--iv-panel)',
                  border: `1px solid ${isTmuaExam ? SIM_SCHEME.border : 'var(--iv-line)'}`,
                  borderRadius: '14px',
                  overflow: 'hidden',
                  boxShadow: isTmuaExam && colorScheme !== 'ivori' ? 'none' : '0 2px 8px color-mix(in srgb, var(--iv-shadow) 4%, transparent)'
                }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '70px minmax(0, 1fr) 1fr 130px 100px 100px',
                    gap: '0',
                    padding: '14px 22px',
                    boxSizing: 'border-box',
                    background: isTmuaExam ? SIM_SCHEME.chrome : 'var(--iv-bg)',
                    borderBottom: `1px solid ${isTmuaExam ? SIM_SCHEME.border : 'var(--iv-line)'}`,
                    fontSize: '11px',
                    fontWeight: '800',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: tmuaInk('var(--iv-muted)')
                  }}
                >
                  
                  <div>Question</div>
                  <div style={{ textAlign: 'center' }}>Title</div>
                  <div style={{ textAlign: 'center' }}>Status</div>
                  <div style={{ textAlign: 'left', paddingLeft: '20px' }}>Flagged</div>
                  <div></div>
                </div>

                {(reviewTab === 'all'
                  ? questions
                  : questions.filter((q) => {
                      const ans = userAnswers[q.id];

                      const isMC =
                        q.multiple_choice !== false &&
                        q.options &&
                        q.options.length > 1;

                      return isMC
                        ? !ans?.label
                        : !ans?.text_response?.trim();
                    })
                ).map((q, displayIdx) => {
                  const idx = questions.findIndex(
                    (question) => question.id === q.id
                  );

                  const ans = userAnswers[q.id];

                  const isMC =
                    q.multiple_choice !== false &&
                    q.options &&
                    q.options.length > 1;

                  const answered = isMC
                    ? Boolean(ans?.label)
                    : Boolean(ans?.text_response?.trim());

                  const flagged = Boolean(flaggedQuestions[q.id]);
                  const rowBg = isTmuaExam
                    ? (displayIdx % 2 === 0
                      ? SIM_SCHEME.chrome
                      : SIM_SCHEME.body)
                    : 'var(--iv-panel)';

                  return (
                    <div
                      key={q.id}
                      className="ivori-review-item-row"
                      onClick={() => {
                        setCurrentQuestionIndex(idx);
                        setReviewScreenOpen(false);
                      }}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '70px minmax(0, 1fr) 1fr 130px 100px 100px',
                        alignItems: 'center',
                        gap: '0',
                        padding: '18px 22px',
                        boxSizing: 'border-box',
                        borderBottom: `1px solid ${isTmuaExam ? SIM_SCHEME.border : 'var(--iv-line)'}`,
                        cursor: 'pointer',
                        background: rowBg
                      }}
                    >
                      <div
                        style={{
                          fontSize: '14px',
                          fontWeight: '700',
                          color: 'var(--iv-body)'
                        }}
                      >
                        {idx + 1}
                      </div>

                      <div
                        style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: 'var(--iv-body)',
                          textAlign: 'center'
            
                        }}
                      >
                        Question {idx + 1}
                      </div>

                      <div
                        style={{
                          fontSize: '12px',
                          fontWeight: '700',
                          color: answered ? 'var(--iv-muted)' : 'var(--iv-danger)',
                          textAlign: 'center'
                        }}
                      >
                        {answered ? 'Answered' : 'Unanswered'}
                      </div>

                      <div
                        style={{
                          fontSize: '12px',
                          fontWeight: '700',
                          color: flagged ? 'var(--iv-accent)' : 'var(--iv-muted)',
                          transform: 'translateX(36px)'
                        }}
                      >
                        {flagged ? 'Yes' : 'No'}
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          setCurrentQuestionIndex(idx);
                          setReviewScreenOpen(false);
                        }}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          color: 'var(--iv-body)',
                          fontSize: '13px',
                          fontWeight: '700',
                          cursor: 'pointer',
                          padding: '6px 0'
                        }}
                      >
                        Review →
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Bottom action bar */}
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              padding: '16px 32px',
              background: isTmuaExam ? SIM_SCHEME.chrome : 'var(--iv-panel)',
              borderTop: `1px solid ${isTmuaExam && colorScheme !== 'ivori' ? '#14161F' : 'var(--iv-line)'}`
            }}
          >
            <button
              type="button"
              onClick={() => setShowEndReviewModal(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                border: 'none',
                background: 'transparent',
                color: 'var(--iv-body)',
                fontFamily: 'var(--iv-font-body)',
                fontSize: '14px',
                fontWeight: '700',
                cursor: 'pointer',
                padding: '10px 4px'
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16,17 21,12 16,7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              End Review
            </button>
          </div>
          
          
        </div>
      )}

      {/* MAIN EXAM PANEL — FULL WIDTH WITHOUT LEFT SIDEBAR */}
      <div className="ivori-exam-main-panel" style={{ flex: 1, minWidth: 0, background: isTmuaExam ? SIM_SCHEME.body : 'var(--iv-bg)', color: tmuaInk('var(--iv-head)'), display: 'flex', flexDirection: 'column' }}>
        
        {/* top bar with clean question indicator & timer */}
        {isTmuaExam ? (
        <div className="ivori-exam-top-bar" style={{ display: 'flex', flexDirection: 'column', borderBottom: `1px solid ${colorScheme !== 'ivori' ? '#14161F' : 'var(--iv-line)'}`, background: SIM_SCHEME.chrome, boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 32px', boxSizing: 'border-box' }}>
          {/* Top Left — Exam Title */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
            <span style={{ fontSize: '19px', fontWeight: '700', color: SIM_SCHEME.text, fontFamily: 'var(--iv-font-body)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              Test of Mathematics for University Admission
            </span>
          </div>

          {/* Top Right — Timer + question indicator, stacked */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
            {/* Timer — plain text, no pill */}
            {timeRemaining > 0 && (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <svg
                  style={{ display: 'block', flexShrink: 0 }}
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="var(--iv-head)"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="8"></circle>
                  <polyline points="12,8 12,12 15,14"></polyline>
                </svg>

                <span style={{
                  fontFamily: 'var(--iv-font-body)',
                  fontWeight: '700',
                  fontSize: '14px',
                  letterSpacing: '0.02em',
                  color: 'var(--iv-body)',
                  fontVariantNumeric: 'tabular-nums',
                  lineHeight: 1,
                  display: 'inline-flex',
                  alignItems: 'center'
                }}>
                  Time Remaining {displayTime}
                </span>
              </div>
            )}

            {/* Question indicator */}
            {(() => {
              if (isFreeTextPractice(currentQuestion) && String(examName).toLowerCase() === 'tmua') {
                return null;
              }
              const indicatorIcon = (
                <svg
                  style={{ display: 'block', flexShrink: 0 }}
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="var(--iv-head)"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 6h11" />
                  <path d="M9 12h11" />
                  <path d="M9 18h11" />
                  <path d="M4 6h.01" />
                  <path d="M4 12h.01" />
                  <path d="M4 18h.01" />
                </svg>
              );
              if (isEssayQuestion(currentQuestion)) {
                const taraEssay = isTaraExamContext(examName, currentQuestion, activeQuizSource);
                if (taraEssay) {
                  const essayPrompts = resolveEssayPrompts(currentQuestion);
                  const essayActiveIdx = essayPrompts.findIndex((p) => p.label === userAnswers[currentQuestion?.id]?.label);
                  const promptNum = essayActiveIdx >= 0 ? essayActiveIdx + 1 : 1;
                  const totalPages = 2 + Math.max(essayPrompts.length, 1) + 1; // nda + info + prompts + thanks
                  let pageLabel = 'Welcome';
                  let pageNum = 1;
                  if (taraEssayPhase === 'info') {
                    pageLabel = 'Test Information';
                    pageNum = 2;
                  } else if (taraEssayPhase === 'write') {
                    pageLabel = `Essay ${promptNum}`;
                    pageNum = 2 + promptNum;
                  } else if (taraEssayPhase === 'thanks') {
                    pageLabel = 'Complete';
                    pageNum = totalPages;
                  }
                  return (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600', color: 'var(--iv-body)', fontFamily: 'var(--iv-font-body)' }}>
                      {indicatorIcon}
                      {pageLabel} — {pageNum} of {totalPages}
                    </span>
                  );
                }
                if (essayShowInstructions) {
                  return (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600', color: 'var(--iv-body)', fontFamily: 'var(--iv-font-body)' }}>
                      {indicatorIcon}
                      Section B — Instructions
                    </span>
                  );
                }
              }
              const essayPrompts = isEssayQuestion(currentQuestion) ? resolveEssayPrompts(currentQuestion) : [];
              const essayActiveIdx = essayPrompts.findIndex((p) => p.label === userAnswers[currentQuestion?.id]?.label);
              const displayNum = essayPrompts.length
                ? (essayActiveIdx >= 0 ? essayActiveIdx + 1 : 1)
                : currentQuestionIndex + 1;
              const displayTotal = essayPrompts.length || totalQuestions;
              return (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600', color: 'var(--iv-head)', fontFamily: 'var(--iv-font-body)' }}>
                  {indicatorIcon}
                  {displayNum} of {displayTotal}
                </span>
              );
            })()}
          </div>
        </div>

        {/* second row — Flag for Review */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '16px', padding: '6px 32px 6px 72px', borderTop: `1px solid ${colorScheme !== 'ivori' ? '#14161F' : 'var(--iv-line)'}`, boxSizing: 'border-box' }}>
          <span
            onClick={() => handleFlag(currentQuestion.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '7px',
              fontSize: '13px',
              fontWeight: '600',
              color: 'var(--iv-body)',
              cursor: 'pointer',
              fontFamily: 'var(--iv-font-body)'
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill={isFlagged ? 'currentColor' : 'none'}
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 21V5a1 1 0 0 1 1-1h11l-2.5 4L17.5 12H6"></path>
            </svg>
            {isFlagged ? 'Flagged for review' : 'Flag for review'}
          </span>
          <select
            value={colorScheme}
            onChange={(e) => setColorScheme(e.target.value)}
            style={{
              fontSize: '13px',
              fontWeight: '600',
              color: SIM_SCHEME.text,
              fontFamily: 'var(--iv-font-body)',
              background: SIM_SCHEME.chrome,
              border: `1.5px solid ${SIM_SCHEME.border}`,
              borderRadius: '4px',
              padding: '5px 10px',
              cursor: 'pointer'
            }}
          >
            {Object.entries(getColorSchemesForExam('TMUA')).map(([key, scheme]) => (
              <option key={key} value={key}>{scheme.label}</option>
            ))}
          </select>
        </div>
        </div>
        ) : (
        <div className="ivori-exam-top-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 32px 14px 72px', borderBottom: '1px solid var(--iv-line)', background: 'var(--iv-raised)', boxSizing: 'border-box' }}>
          {/* Top Left — Question Indicator */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
            {(() => {
              if (isFreeTextPractice(currentQuestion)) {
                if (String(examName).toLowerCase() === 'tmua') {
                  return null;
                }

                const freeTextNum = currentQuestionIndex + 1;
                const freeTextTotal = totalQuestions;
                return (
                  <span style={{ fontSize: '11px', fontWeight: '800', letterSpacing: '0.14em', color: 'var(--iv-muted)', textTransform: 'uppercase', fontFamily: 'var(--iv-font-body)' }}>
                    QUESTION <strong style={{ color: 'var(--iv-head)', fontSize: '15px', fontWeight: '800' }}>{String(freeTextNum).padStart(2, '0')}</strong> <span style={{ opacity: 0.5, fontWeight: '600' }}>/ {freeTextTotal}</span>
                  </span>
                );
              }
              if (isEssayQuestion(currentQuestion)) {
                const taraEssay = isTaraExamContext(examName, currentQuestion, activeQuizSource);
                if (taraEssay) {
                  const essayPrompts = resolveEssayPrompts(currentQuestion);
                  const essayActiveIdx = essayPrompts.findIndex((p) => p.label === userAnswers[currentQuestion?.id]?.label);
                  const promptNum = essayActiveIdx >= 0 ? essayActiveIdx + 1 : 1;
                  const totalPages = 2 + Math.max(essayPrompts.length, 1) + 1; // nda + info + prompts + thanks
                  let pageLabel = 'Welcome';
                  let pageNum = 1;
                  if (taraEssayPhase === 'info') {
                    pageLabel = 'Test Information';
                    pageNum = 2;
                  } else if (taraEssayPhase === 'write') {
                    pageLabel = `Essay ${promptNum}`;
                    pageNum = 2 + promptNum;
                  } else if (taraEssayPhase === 'thanks') {
                    pageLabel = 'Complete';
                    pageNum = totalPages;
                  }
                  return (
                    <span style={{ fontSize: '11px', fontWeight: '800', letterSpacing: '0.14em', color: 'var(--iv-muted)', textTransform: 'uppercase', fontFamily: 'var(--iv-font-body)' }}>
                      WRITING TASK — <strong style={{ color: 'var(--iv-head)', fontSize: '15px', fontWeight: '800' }}>{pageLabel}</strong>
                      <span style={{ opacity: 0.5, fontWeight: '600', marginLeft: '8px' }}>{pageNum} / {totalPages}</span>
                    </span>
                  );
                }
                if (essayShowInstructions) {
                  return (
                    <span style={{ fontSize: '11px', fontWeight: '800', letterSpacing: '0.14em', color: 'var(--iv-muted)', textTransform: 'uppercase', fontFamily: 'var(--iv-font-body)' }}>
                      SECTION B — <strong style={{ color: 'var(--iv-head)', fontSize: '15px', fontWeight: '800' }}>Instructions</strong>
                    </span>
                  );
                }
              }
              const essayPrompts = isEssayQuestion(currentQuestion) ? resolveEssayPrompts(currentQuestion) : [];
              const essayActiveIdx = essayPrompts.findIndex((p) => p.label === userAnswers[currentQuestion?.id]?.label);
              const displayNum = essayPrompts.length
                ? (essayActiveIdx >= 0 ? essayActiveIdx + 1 : 1)
                : currentQuestionIndex + 1;
              const displayTotal = essayPrompts.length || totalQuestions;
              return (
                <span style={{ fontSize: '11px', fontWeight: '800', letterSpacing: '0.14em', color: 'var(--iv-muted)', textTransform: 'uppercase', fontFamily: 'var(--iv-font-body)' }}>
                  QUESTION <strong style={{ color: 'var(--iv-head)', fontSize: '15px', fontWeight: '800' }}>{String(displayNum).padStart(2, '0')}</strong> <span style={{ opacity: 0.5, fontWeight: '600' }}>/ {displayTotal}</span>
                </span>
              );
            })()}
          </div>

          {/* Top Right — Timer */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px' }}>
            {/* Timer */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              background: 'var(--iv-surface)',
              border: '1px solid var(--iv-line)',
              borderRadius: '999px',
              padding: '0 16px',
              height: '36px',
              boxShadow: '0 1px 4px color-mix(in srgb, var(--iv-shadow) 4%, transparent)',
              boxSizing: 'border-box'
            }}>
              <svg
                style={{ display: 'block', flexShrink: 0 }}
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--iv-muted)"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="8"></circle>
                <polyline points="12,8 12,12 15,14"></polyline>
              </svg>

              <span style={{
                fontFamily: 'var(--iv-font-body)',
                fontWeight: '700',
                fontSize: '14px',
                letterSpacing: '0.02em',
                color: 'var(--iv-body)',
                fontVariantNumeric: 'tabular-nums',
                lineHeight: 1,
                display: 'inline-flex',
                alignItems: 'center',
                marginTop: '-1px'
              }}>
                {displayTime}
              </span>
            </div>

            {/* Flag for Review */}
            <span
              onClick={() => handleFlag(currentQuestion.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '7px',
                fontSize: '13px',
                fontWeight: '600',
                color: isFlagged ? 'var(--iv-body)' : 'var(--iv-muted)',
                cursor: 'pointer',
                fontFamily: 'var(--iv-font-body)'
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill={isFlagged ? 'currentColor' : 'none'}
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 21V5a1 1 0 0 1 1-1h11l-2.5 4L17.5 12H6"></path>
              </svg>
              {isFlagged ? 'Flagged for review' : 'Flag for review'}
            </span>
          </div>
        </div>
        )}

        {/* split content body */}
        {(() => {
          const isFreeText = isFreeTextPractice(currentQuestion);
          const isEssay = isEssayQuestion(currentQuestion);
          const essayPromptsResolved = resolveEssayPrompts(currentQuestion);
          const essayPrompts =
            essayPromptsResolved.length > 0
              ? essayPromptsResolved
              : (isEssay
                  ? (currentQuestion.essay_options || []).map((opt, idx) => ({
                      ...opt,
                      label: opt.label || String.fromCharCode(65 + idx),
                      text: opt.text || opt.question_text || opt.prompt_text || '',
                    }))
                  : []);
          const activePromptIndex = essayPrompts.findIndex(p => p.label === userAnswer?.label) >= 0 
            ? essayPrompts.findIndex(p => p.label === userAnswer?.label) 
            : 0;
          const activePrompt = essayPrompts[activePromptIndex] || essayPrompts[0] || {};
          const promptNum = activePromptIndex + 1;
          const promptTotal = essayPrompts.length || 3;
          const wordCount = (userAnswer?.text_response || '').trim().split(/\s+/).filter(Boolean).length;
          const canGoNextPrompt = activePromptIndex < essayPrompts.length - 1;
          const canGoNextQuestion = currentQuestionIndex < totalQuestions - 1;
          const medintTopic = getMedintTopicLabel(currentQuestion, activeQuizSource);
          const formatTags = inferInterviewFormatTags(currentQuestion?.question, examName);
          const examDisplay = formatExamDisplayName(examName);

          // Medical Interviews / free-text practice — navigate the full question set
          if (isFreeText) {
            return (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--iv-surface)', minHeight: 0, overflow: 'hidden' }}>
                <div style={{ background: 'var(--iv-surface)', borderBottom: '1px solid var(--iv-line)', padding: '14px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--iv-body)', fontFamily: 'var(--iv-font-body)' }}>
                      {examDisplay}
                      {medintTopic ? ` — ${medintTopic}` : ''}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px', flexWrap: 'wrap' }}>
                      {(formatTags.length > 0 ? formatTags : ['Interview Practice']).map((tag) => (
                        <span
                          key={tag}
                          style={{
                            fontSize: '10px',
                            fontWeight: 700,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            color: 'var(--iv-accent)',
                            background: 'var(--iv-bg)',
                            padding: '3px 9px',
                            borderRadius: '6px',
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                      <span
                        onClick={() => handleExplainOpen(currentQuestionIndex)}
                        style={{ fontSize: '13px', color: 'var(--iv-body)', fontWeight: '600', textDecoration: 'underline', cursor: 'pointer', marginLeft: '4px' }}
                      >
                        Model Answer
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '20px', fontSize: '13.5px', fontWeight: '600', color: 'var(--iv-body)', flexShrink: 0 }}>
                    <span style={{ color: 'var(--iv-muted)' }}>{currentQuestionIndex + 1} of {totalQuestions}</span>
                    <span onClick={() => handleFlag(currentQuestion.id)} style={{ cursor: 'pointer', color: isFlagged ? 'var(--iv-body)' : 'var(--iv-muted)' }}>
                      {isFlagged ? 'Flagged for Review' : 'Flag for Review'}
                    </span>
                  </div>
                </div>

                <div style={{ padding: '24px 32px 12px', background: 'var(--iv-surface)' }}>
                  <div
                    style={{ fontSize: '17px', fontWeight: 400, color: 'var(--iv-body)', lineHeight: 1.5, marginBottom: '10px', fontFamily: 'var(--iv-font-body)' }}
                    className="mathjax-process tex2jax_process"
                    dangerouslySetInnerHTML={{ __html: prepareSittingHtml(currentQuestion.question || '') }}
                  />
                  <div style={{ fontSize: '12.5px', color: 'var(--iv-muted)', lineHeight: 1.5 }}>
                    Draft your spoken-style answer below. Aim for a clear structure: open with your point, support it with evidence or experience, then close briefly.
                  </div>
                </div>

                <div data-writing-keep-focus style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 32px', background: 'var(--iv-bg)', borderTop: '1px solid var(--iv-line)', borderBottom: '1px solid var(--iv-line)' }}>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={handleCut} title="Cut selected text" style={{ background: 'var(--iv-surface)', border: '1px solid var(--iv-line)', borderRadius: '4px', padding: '4px 12px', fontSize: '12px', fontWeight: 600, color: 'var(--iv-head)', cursor: 'pointer' }}>Cut</button>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={handleCopy} title="Copy selected text" style={{ background: 'var(--iv-surface)', border: '1px solid var(--iv-line)', borderRadius: '4px', padding: '4px 12px', fontSize: '12px', fontWeight: 600, color: 'var(--iv-head)', cursor: 'pointer' }}>Copy</button>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={handlePaste} title="Paste from clipboard" style={{ background: 'var(--iv-surface)', border: '1px solid var(--iv-line)', borderRadius: '4px', padding: '4px 12px', fontSize: '12px', fontWeight: 600, color: 'var(--iv-head)', cursor: 'pointer' }}>Paste</button>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={handleUndo} title="Undo" style={{ background: 'var(--iv-surface)', border: '1px solid var(--iv-line)', borderRadius: '4px', padding: '4px 12px', fontSize: '12px', fontWeight: 600, color: undoStack.length === 0 ? 'var(--iv-muted)' : 'var(--iv-head)', cursor: undoStack.length === 0 ? 'default' : 'pointer' }}>Undo</button>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={handleRedo} title="Redo" style={{ background: 'var(--iv-surface)', border: '1px solid var(--iv-line)', borderRadius: '4px', padding: '4px 12px', fontSize: '12px', fontWeight: 600, color: redoStack.length === 0 ? 'var(--iv-muted)' : 'var(--iv-head)', cursor: redoStack.length === 0 ? 'default' : 'pointer' }}>Redo</button>
                </div>

                <div style={{ flex: 1, padding: '16px 32px', background: 'var(--iv-surface)', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  <textarea
                    ref={essayTextareaRef}
                    data-writing-keep-focus
                    value={userAnswer?.text_response || ''}
                    onChange={(e) => handleEssayChange(e.target.value)}
                    placeholder="Type your answer here…"
                    style={{
                      flex: 1,
                      width: '100%',
                      padding: '16px',
                      border: '1.5px solid var(--iv-head)',
                      borderRadius: '8px',
                      fontSize: '15.5px',
                      lineHeight: 1.65,
                      fontFamily: 'var(--iv-font-body)',
                      boxSizing: 'border-box',
                      resize: 'none',
                      outline: 'none',
                      background: 'var(--iv-surface)',
                      color: 'var(--iv-body)',
                      boxShadow: 'none'
                    }}
                  />
                </div>

                <div style={{ background: 'var(--iv-surface)', borderTop: '1px solid var(--iv-line)', padding: '14px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '13.5px', fontWeight: '600', color: 'var(--iv-muted)' }}>
                    Word Count: <strong style={{ color: 'var(--iv-head)' }}>{wordCount}</strong>
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <button
                      type="button"
                      disabled={currentQuestionIndex <= 0}
                      onClick={() => {
                        if (currentQuestionIndex <= 0) return;
                        setUndoStack([]);
                        setRedoStack([]);
                        handlePrev();
                      }}
                      style={{
                        background: 'var(--iv-surface)',
                        color: currentQuestionIndex <= 0 ? 'var(--iv-muted)' : 'var(--iv-body)',
                        border: '1px solid var(--iv-line)',
                        borderRadius: '999px',
                        padding: '8px 20px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: currentQuestionIndex <= 0 ? 'default' : 'pointer',
                        opacity: currentQuestionIndex <= 0 ? 0.65 : 1,
                      }}
                    >
                      Previous Question
                    </button>
                    <button
                      type="button"
                      disabled={!canGoNextQuestion}
                      onClick={() => {
                        if (!canGoNextQuestion) return;
                        setUndoStack([]);
                        setRedoStack([]);
                        handleNext();
                      }}
                      style={{
                        background: 'var(--iv-surface)',
                        color: canGoNextQuestion ? 'var(--iv-body)' : 'var(--iv-muted)',
                        border: '1px solid var(--iv-line)',
                        borderRadius: '999px',
                        padding: '8px 20px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: canGoNextQuestion ? 'pointer' : 'default',
                        opacity: canGoNextQuestion ? 1 : 0.65
                      }}
                    >
                      Next Question
                    </button>
                    <button
                      type="button"
                      onClick={handleReviewSubmit}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'var(--iv-btn)',
                        color: 'var(--iv-btn-fg)',
                        border: 'none',
                        borderRadius: '999px',
                        padding: '11px 28px',
                        minHeight: '42px',
                        fontSize: '13.5px',
                        fontWeight: '700',
                        lineHeight: 1.2,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Submit
                    </button>
                  </div>
                </div>
              </div>
            );
          }

          // TARA Writing Task multi-page flow (NDA → info → essays → thanks)
          const taraEssay = isEssay && isTaraExamContext(examName, currentQuestion, activeQuizSource);
          if (taraEssay && taraEssayPhase === 'nda') {
            return (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--iv-surface)', minHeight: 0, overflow: 'hidden' }}>
                <div style={{ flex: 1, overflowY: 'auto', padding: '48px 56px', maxWidth: '820px', width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
                  <h1 style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: '22px', fontWeight: 700, color: 'var(--iv-body)', margin: '0 0 28px', textDecoration: 'underline', textUnderlineOffset: '4px' }}>
                    Writing Task
                  </h1>
                  <div style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: '17px', lineHeight: 1.75, color: 'var(--iv-body)', display: 'flex', flexDirection: 'column', gap: '18px' }}>
                    {TARA_NDA_PARAGRAPHS.map((text, idx) => (
                      <p key={`nda-${idx}`} style={{ margin: 0, fontWeight: idx === 1 ? 700 : 400 }}>
                        {text}
                      </p>
                    ))}
                  </div>
                </div>
                <div style={{ background: 'var(--iv-surface)', borderTop: '1px solid var(--iv-line)', padding: '14px 32px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                  <button type="button" onClick={() => setTaraEssayPhase('info')} style={essayNavBtnStyle(true)}>
                    Next
                  </button>
                </div>
              </div>
            );
          }

          if (taraEssay && taraEssayPhase === 'info') {
            const maxWords = currentQuestion?.essay_max_words || 750;
            const infoLines = TARA_TEST_INFO_PARAGRAPHS.map((line) => (
              line.text.includes('750') && maxWords !== 750
                ? { ...line, text: `Your answer should not exceed ${maxWords} words.` }
                : line
            ));
            return (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--iv-surface)', minHeight: 0, overflow: 'hidden' }}>
                <div style={{ flex: 1, overflowY: 'auto', padding: '48px 56px', maxWidth: '820px', width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
                  <h1 style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: '22px', fontWeight: 700, color: 'var(--iv-body)', margin: '0 0 28px', textDecoration: 'underline', textUnderlineOffset: '4px' }}>
                    Writing Task
                  </h1>
                  <div style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: '17px', lineHeight: 1.75, color: 'var(--iv-body)', display: 'flex', flexDirection: 'column', gap: '18px' }}>
                    {infoLines.map((line, idx) => (
                      <p
                        key={`info-${idx}`}
                        style={{
                          margin: line.heading ? '10px 0 0' : 0,
                          fontWeight: line.heading || line.bold ? 700 : 400,
                          textDecoration: line.heading ? 'underline' : 'none',
                          textUnderlineOffset: line.heading ? '3px' : undefined,
                        }}
                      >
                        {line.text}
                      </p>
                    ))}
                  </div>
                </div>
                <div style={{ background: 'var(--iv-surface)', borderTop: '1px solid var(--iv-line)', padding: '14px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <button type="button" onClick={() => setTaraEssayPhase('nda')} style={essayNavBtnStyle(true)}>
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setTaraEssayPhase('write');
                      goToEssayPrompt(0);
                      startTaraEssayTimer();
                    }}
                    style={essayNavBtnStyle(true)}
                  >
                    Next
                  </button>
                </div>
              </div>
            );
          }

          if (taraEssay && taraEssayPhase === 'thanks') {
            return (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--iv-surface)', minHeight: 0, overflow: 'hidden' }}>
                <div style={{ flex: 1, overflowY: 'auto', padding: '48px 56px', maxWidth: '820px', width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
                  <h1 style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: '22px', fontWeight: 700, color: 'var(--iv-body)', margin: '0 0 28px', textDecoration: 'underline', textUnderlineOffset: '4px' }}>
                    Writing Task
                  </h1>
                  <div style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: '17px', lineHeight: 1.75, color: 'var(--iv-body)', display: 'flex', flexDirection: 'column', gap: '18px' }}>
                    <p style={{ margin: 0 }}>Thank you for completing the Writing Task.</p>
                    <p style={{ margin: 0 }}>Click the End Exam button to continue.</p>
                  </div>
                </div>
                <div style={{ background: 'var(--iv-surface)', borderTop: '1px solid var(--iv-line)', padding: '14px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setTaraEssayPhase('write');
                      goToEssayPrompt(Math.max(0, essayPrompts.length - 1));
                    }}
                    style={essayNavBtnStyle(true)}
                  >
                    Previous
                  </button>
                  <button type="button" onClick={handleReviewSubmit} style={essayPrimaryBtnStyle}>
                    End Exam
                  </button>
                </div>
              </div>
            );
          }

          // LNAT Section B instructions page
          if (isEssay && !taraEssay && essayShowInstructions) {
            const sittingEssayN = (takingQuestions || []).filter((q) => isEssayQuestion(q)).length
              || resolveEssayPrompts(currentQuestion).length
              || 1;
            const instructionLines = buildLnatSectionBInstructionBody(sittingEssayN);

            return (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--iv-surface)', minHeight: 0, overflow: 'hidden' }}>
                <div style={{ flex: 1, overflowY: 'auto', padding: '48px 56px', maxWidth: '820px', width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
                  <h1 style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: '22px', fontWeight: 700, color: 'var(--iv-body)', margin: '0 0 28px', textDecoration: 'underline', textUnderlineOffset: '4px' }}>
                    Section B : Essay
                  </h1>
                  <div style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: '17px', lineHeight: 1.75, color: 'var(--iv-head)', display: 'flex', flexDirection: 'column', gap: '18px' }}>
                    {instructionLines.map((line, idx) => (
                      <p key={`${idx}-${line.text.slice(0, 24)}`} style={{ margin: 0, fontWeight: line.bold ? 700 : 400 }}>
                        {line.text}
                      </p>
                    ))}
                  </div>
                </div>
                <div style={{ background: 'var(--iv-surface)', borderTop: '1px solid var(--iv-line)', padding: '14px 32px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                  <button
                    type="button"
                    onClick={() => {
                      const prompts = resolveEssayPrompts(currentQuestion);
                      const fallbackPrompts =
                        prompts.length > 0
                          ? prompts
                          : (currentQuestion.essay_options || []).map((opt, idx) => ({
                              ...opt,
                              label: opt.label || String.fromCharCode(65 + idx),
                              text: opt.text || opt.question_text || opt.prompt_text || '',
                            }));
                      setEssayShowInstructions(false);
                      if (fallbackPrompts.length > 0) {
                        // Ensure first prompt is selected even if resolveEssayPrompts filtered aggressively
                        goToEssayPrompt(0);
                        if (prompts.length === 0) {
                          const first = fallbackPrompts[0];
                          const label = first.label || 'A';
                          setUserAnswers((prev) => ({
                            ...prev,
                            [currentQuestion.id]: {
                              ...(prev[currentQuestion.id] || {}),
                              label,
                              text_response: prev[currentQuestion.id]?.text_response || '',
                              prompt_drafts: prev[currentQuestion.id]?.prompt_drafts || {},
                              question_db_id: currentQuestion.db_id,
                              is_text_answer: true,
                            },
                          }));
                        }
                      }
                    }}
                    style={essayNavBtnStyle(true)}
                  >
                    Next Question
                  </button>
                </div>
              </div>
            );
          }

          // Essay writing canvas — Previous/Next switch prompts
          if (isEssay && essayPrompts.length > 0 && (!taraEssay || taraEssayPhase === 'write')) {
            const essayTitle = taraEssay
              ? 'Test of Academic Reasoning for Admissions (TARA) — Writing Task'
              : 'National Admissions Test for Law (LNAT) — Section B';
            const maxWords = currentQuestion?.essay_max_words || (taraEssay ? 750 : null);
            const parsedPrompt = taraEssay
              ? parseTaraEssayPrompt(getEssayPromptDisplayText(activePrompt, currentQuestion))
              : null;

            return (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--iv-surface)', minHeight: 0, overflow: 'hidden' }}>
                <style>{`
                  .tmua-ivori-essay-textarea { font-size: 15.5px; }
                  @media (max-width: 768px) {
                    .tmua-ivori-essay-textarea { font-size: 16px !important; }
                  }
                `}</style>
                {!ivoriEssayKeyboardOpen ? (
                  <div style={{ background: 'var(--iv-surface)', borderBottom: '1px solid var(--iv-line)', padding: '14px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--iv-head)', fontFamily: 'var(--iv-font-body)' }}>
                        {essayTitle}
                      </div>
                      <div
                        onClick={() => handleExplainOpen(currentQuestionIndex)}
                        style={{ fontSize: '13px', color: 'var(--iv-body)', fontWeight: '600', textDecoration: 'underline', cursor: 'pointer', marginTop: '3px' }}
                      >
                        Explain Answer
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px', fontSize: '13.5px', fontWeight: '600', color: 'var(--iv-body)' }}>
                      <span style={{ color: 'var(--iv-muted)' }}>{promptNum} of {promptTotal}</span>
                      <span onClick={() => handleFlag(currentQuestion.id)} style={{ cursor: 'pointer', color: isFlagged ? 'var(--iv-body)' : 'var(--iv-muted)' }}>
                        {isFlagged ? 'Flagged for Review' : 'Flag for Review'}
                      </span>
                    </div>
                  </div>
                ) : null}

                {!ivoriEssayKeyboardOpen ? (
                  <div
                    onClick={ivoriEssayPromptCollapsed ? expandIvoriEssayPrompt : undefined}
                    style={{
                      padding: '24px 32px 12px',
                      background: 'var(--iv-surface)',
                      overflowY: ivoriEssayPromptCollapsed ? 'hidden' : 'visible',
                      maxHeight: ivoriEssayPromptCollapsed ? '3.4em' : 'none',
                      cursor: ivoriEssayPromptCollapsed ? 'pointer' : 'default',
                    }}
                  >
                  {taraEssay && parsedPrompt ? (
                    <div style={{ marginBottom: '10px', color: 'var(--iv-body)' }}>
                      <div style={{ fontSize: '15px', fontWeight: 700, fontFamily: 'var(--iv-font-body)', marginBottom: '12px' }}>
                        Essay {promptNum}
                      </div>
                      {parsedPrompt.statement ? (
                        <div style={{ fontSize: '16.5px', fontWeight: 400, lineHeight: 1.5, fontFamily: 'var(--iv-font-body)', marginBottom: parsedPrompt.attribution ? '6px' : '12px' }}>
                          {'\u2018'}{parsedPrompt.statement}{'\u2019'}
                        </div>
                      ) : null}
                      {parsedPrompt.attribution ? (
                        <div style={{ fontSize: '14.5px', fontStyle: 'italic', color: 'var(--iv-muted)', marginBottom: '12px', fontFamily: 'var(--iv-font-body)' }}>
                          ({parsedPrompt.attribution})
                        </div>
                      ) : null}
                      <div style={{ fontSize: '15px', lineHeight: 1.65, fontFamily: 'var(--iv-font-body)', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {(parsedPrompt.tasks.length > 0
                          ? parsedPrompt.tasks
                          : [
                              'Explain what you think is meant by the statement.',
                              'Give a reasoned argument against the statement.',
                              'To what extent do you agree with the statement?',
                            ]
                        ).map((task) => (
                          <div key={task}>{task}</div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: '16.5px', fontWeight: 400, color: 'var(--iv-body)', lineHeight: 1.5, marginBottom: '10px', fontFamily: 'var(--iv-font-body)', whiteSpace: 'pre-wrap' }}>
                      {resolveLnatEssayItemPrompt({
                        ...currentQuestion,
                        question: activePrompt?.text || currentQuestion?.question,
                        essay_options: activePrompt ? [activePrompt] : currentQuestion.essay_options,
                      }) || 'Prompt unavailable'}
                    </div>
                  )}
                  <div style={{ fontSize: '12.5px', color: 'var(--iv-muted)', lineHeight: 1.5 }}>
                    Write in the window box below. To cut text, first highlight the text inside the window box, then select the cut button. The process is the same to copy text, except you use the copy button instead. Select the Paste button to paste the text which was cut or copied.
                  </div>
                  {ivoriEssayPromptCollapsed ? (
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--iv-muted)' }}>Show more</span>
                  ) : null}
                  </div>
                ) : null}

                {!ivoriEssayKeyboardOpen ? (
                  <div data-writing-keep-focus style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 32px', background: 'var(--iv-bg)', borderTop: '1px solid var(--iv-line)', borderBottom: '1px solid var(--iv-line)' }}>
                    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={handleCut} title="Cut selected text" style={{ background: 'var(--iv-surface)', border: '1px solid var(--iv-line)', borderRadius: '4px', padding: '4px 12px', fontSize: '12px', fontWeight: 600, color: 'var(--iv-head)', cursor: 'pointer' }}>Cut</button>
                    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={handleCopy} title="Copy selected text" style={{ background: 'var(--iv-surface)', border: '1px solid var(--iv-line)', borderRadius: '4px', padding: '4px 12px', fontSize: '12px', fontWeight: 600, color: 'var(--iv-head)', cursor: 'pointer' }}>Copy</button>
                    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={handlePaste} title="Paste from clipboard" style={{ background: 'var(--iv-surface)', border: '1px solid var(--iv-line)', borderRadius: '4px', padding: '4px 12px', fontSize: '12px', fontWeight: 600, color: 'var(--iv-head)', cursor: 'pointer' }}>Paste</button>
                    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={handleUndo} title="Undo" style={{ background: 'var(--iv-surface)', border: '1px solid var(--iv-line)', borderRadius: '4px', padding: '4px 12px', fontSize: '12px', fontWeight: 600, color: undoStack.length === 0 ? 'var(--iv-muted)' : 'var(--iv-head)', cursor: undoStack.length === 0 ? 'default' : 'pointer' }}>Undo</button>
                    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={handleRedo} title="Redo" style={{ background: 'var(--iv-surface)', border: '1px solid var(--iv-line)', borderRadius: '4px', padding: '4px 12px', fontSize: '12px', fontWeight: 600, color: redoStack.length === 0 ? 'var(--iv-muted)' : 'var(--iv-head)', cursor: redoStack.length === 0 ? 'default' : 'pointer' }}>Redo</button>
                  </div>
                ) : null}

                <div style={{ flex: 1, padding: '16px 32px', background: 'var(--iv-surface)', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  <textarea
                    ref={essayTextareaRef}
                    data-writing-keep-focus
                    value={userAnswer?.text_response || ''}
                    onChange={(e) => handleEssayChange(e.target.value)}
                    onFocus={() => {
                      if (typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches) {
                        handleIvoriEssayTextareaFocus();
                      }
                    }}
                    placeholder=""
                    className="tmua-ivori-essay-textarea"
                    style={{
                      flex: 1,
                      width: '100%',
                      padding: '16px',
                      border: '1.5px solid var(--iv-head)',
                      borderRadius: '8px',
                      lineHeight: 1.65,
                      fontFamily: 'var(--iv-font-body)',
                      boxSizing: 'border-box',
                      resize: 'none',
                      outline: 'none',
                      background: 'var(--iv-raised)',
                      color: 'var(--iv-head)',
                      boxShadow: 'none'
                    }}
                  />
                </div>

                <div style={{ background: 'var(--iv-surface)', borderTop: '1px solid var(--iv-line)', padding: '14px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '13.5px', fontWeight: '600', color: 'var(--iv-muted)' }}>
                    Word Count: <strong style={{ color: maxWords && wordCount > maxWords ? 'var(--iv-danger)' : 'var(--iv-head)' }}>{wordCount}</strong>
                    {maxWords ? <span style={{ color: 'var(--iv-muted)' }}> / {maxWords}</span> : null}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <button
                      type="button"
                      onClick={() => {
                        if (activePromptIndex <= 0) {
                          if (taraEssay) {
                            setTaraEssayPhase('info');
                            return;
                          }
                          setEssayShowInstructions(true);
                          return;
                        }
                        goToEssayPrompt(activePromptIndex - 1);
                      }}
                      style={essayNavBtnStyle(true)}
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (canGoNextPrompt) {
                          goToEssayPrompt(activePromptIndex + 1);
                          return;
                        }
                        if (taraEssay) {
                          setTaraEssayPhase('thanks');
                          return;
                        }
                        // LNAT Section B: after last prompt, open review/submit flow
                        handleReviewSubmit();
                      }}
                      style={essayNavBtnStyle(canGoNextPrompt || taraEssay || !taraEssay)}
                      disabled={false}
                    >
                      {canGoNextPrompt || taraEssay ? 'Next' : 'Finish'}
                    </button>
                    {!taraEssay ? (
                      <button type="button" onClick={handleReviewSubmit} style={essayPrimaryBtnStyle}>
                        Submit
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          }

          // STANDARD MULTIPLE CHOICE LAYOUT (Section A & Practice MCQs)
          const hasPassage = Boolean(currentQuestion.exhibit_text && currentQuestion.exhibit_text.trim());
          return (
            <div className="ivori-exam-body-slot">
            <div className={`ivori-exam-split-body${hasPassage ? '' : ' ivori-exam-no-passage'}`} ref={hasPassage ? splitBodyRef : null} style={{ flex: 1, display: 'flex', minHeight: 0, background: isTmuaExam ? SIM_SCHEME.body : 'var(--iv-bg)' }}>
              {hasPassage && (
                <>
                  {/* Passage / Instructions */}
                  <div
                    className="ivori-exam-passage-pane"
                    style={{ flex: 1.5, minWidth: 0, padding: '32px 40px', overflowY: 'auto', outline: 'none', ...splitPanePassageStyle }}
                    ref={passagePaneRef}
                    tabIndex={-1}
                    onScroll={handlePaneScroll(passagePaneSeenBottomRef)}
                    onClick={handlePassagePaneClick}
                  >
                    {splitPaneCollapsed === 'passage' ? (
                      <span style={{ fontSize: 13, fontWeight: 700 }}>Passage</span>
                    ) : (
                      <>
                        <div style={{ fontSize: '11px', fontWeight: '800', letterSpacing: '0.16em', color: 'var(--iv-muted)', marginBottom: '16px', textTransform: 'uppercase' }}>
                          PASSAGE
                        </div>
                        <MathContent
                          style={IVORI_PASSAGE_STYLE}
                          className="mathjax-process tex2jax_process"
                          html={prepareSittingHtml(currentQuestion.exhibit_text)}
                        />
                      </>
                    )}
                  </div>

                  {/* divider line */}
                  <div
                    className="ivori-exam-split-divider"
                    onMouseDown={handleSplitDividerPointerDown}
                    onTouchStart={handleSplitDividerPointerDown}
                    style={{ flex: 'none', width: 7, background: 'var(--iv-fill)' }}
                  >
                    <span className="ivori-exam-split-divider-grip" />
                  </div>
                </>
              )}

              {/* question options */}
              <div
                className="ivori-exam-question-pane"
                style={{ flex: 1, minWidth: 0, padding: hasPassage ? '32px 40px' : '36px 48px', overflowY: 'auto', outline: 'none', ...(hasPassage ? splitPaneQuestionStyle : null) }}
                ref={questionPaneRef}
                tabIndex={-1}
                onScroll={handlePaneScroll(questionPaneSeenBottomRef)}
                onClick={hasPassage ? handleQuestionPaneClick : handleExhibitTextClick}
              >
              {hasPassage && splitPaneCollapsed === 'question' ? (
                <span style={{ fontSize: 13, fontWeight: 700 }}>Question</span>
              ) : null}
              <div
                className="ivori-exam-question-column"
                style={{
                  display: (hasPassage && splitPaneCollapsed === 'question') ? 'none' : 'flex',
                  flexDirection: 'column',
                  width: hasPassage ? '100%' : '50%',
                  maxWidth: hasPassage ? 'none' : '720px',
                  margin: '0',
                  paddingRight: '32px',
                  boxSizing: 'border-box'
                }}
              >
                <MathContent
                  style={IVORI_QUESTION_STYLE}
                  className="question-text mathjax-process tex2jax_process"
                  html={prepareSittingHtml(
                    normalizeQuestionSpacing(
                      String(currentQuestion.question || '').replace(
                        String(examName).toLowerCase() === 'tmua'
                          ? /^Question\s+\d+\s*:\s*/i
                          : /^$/,
                        ''
                      )
                    )
                  )}
                />

                <PaperImage url={currentQuestion.image_url} alt="" />

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {isYesNoQuestion(currentQuestion) ? (
                    (currentQuestion.yes_no_items || []).map((item) => {
                      const yn = (userAnswer && userAnswer.yes_no_answers) || {};
                      const selected = Object.prototype.hasOwnProperty.call(yn, String(item.id))
                        ? yn[String(item.id)]
                        : null;
                      return (
                        <div
                          key={item.id}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                            padding: '16px 20px',
                            border: '1.5px solid var(--iv-line)',
                            borderRadius: '12px',
                            background: 'var(--iv-surface)',
                          }}
                        >
                          <MathContent
                            className="mathjax-process tex2jax_process"
                            style={IVORI_ITEM_TEXT_STYLE}
                            html={prepareSittingHtml(item.text || '')}
                          />
                          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            {[true, false].map((val) => {
                              const active = selected === val;
                              return (
                                <button
                                  key={String(val)}
                                  type="button"
                                  onClick={() => selectYesNo(currentQuestion.id, currentQuestion, item.id, val)}
                                  style={{
                                    minWidth: '88px',
                                    padding: '10px 18px',
                                    borderRadius: '999px',
                                    border: active ? '2px solid var(--iv-band-line)' : '1.5px solid var(--iv-line)',
                                    background: active ? 'var(--iv-band)' : 'var(--iv-surface)',
                                    color: active ? 'var(--iv-on-band)' : 'var(--iv-muted)',
                                    fontWeight: 700,
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                  }}
                                >
                                  {val ? 'Yes' : 'No'}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })
                  ) : isMostLeastQuestion(currentQuestion) ? (
                    <>
                      <p style={{ margin: '0 0 4px', fontSize: '14px', lineHeight: 1.55, color: 'var(--iv-muted)' }}>
                        Choose both the <strong style={{ color: 'var(--iv-head)' }}>most</strong> and{' '}
                        <strong style={{ color: 'var(--iv-head)' }}>least</strong> appropriate actions.
                      </p>
                      {(currentQuestion.most_least_options || []).map((opt) => {
                        const ml = (userAnswer && userAnswer.most_least_answers) || {};
                        const isMost = String(ml.most_option_id) === String(opt.id);
                        const isLeast = String(ml.least_option_id) === String(opt.id);
                        return (
                          <div
                            key={opt.id}
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '12px',
                              padding: '16px 20px',
                              border: isMost || isLeast ? '2px solid var(--iv-head)' : '1.5px solid var(--iv-line)',
                              borderRadius: '12px',
                              background: 'var(--iv-surface)',
                            }}
                          >
                            <MathContent
                              className="mathjax-process tex2jax_process"
                              style={IVORI_ITEM_TEXT_STYLE}
                              html={prepareSittingHtml(opt.text || '')}
                            />
                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                              <button
                                type="button"
                                onClick={() => selectMostLeast(currentQuestion.id, currentQuestion, 'most', opt.id)}
                                style={{
                                  padding: '8px 16px',
                                  borderRadius: '999px',
                                  border: isMost ? '2px solid var(--iv-band-line)' : '1.5px solid var(--iv-line)',
                                  background: isMost ? 'var(--iv-band)' : 'var(--iv-surface)',
                                  color: isMost ? 'var(--iv-on-band)' : 'var(--iv-muted)',
                                  fontWeight: 700,
                                  fontSize: '13px',
                                  cursor: 'pointer',
                                }}
                              >
                                Most appropriate
                              </button>
                              <button
                                type="button"
                                onClick={() => selectMostLeast(currentQuestion.id, currentQuestion, 'least', opt.id)}
                                style={{
                                  padding: '8px 16px',
                                  borderRadius: '999px',
                                  border: isLeast ? '2px solid var(--iv-band-line)' : '1.5px solid var(--iv-line)',
                                  background: isLeast ? 'var(--iv-band)' : 'var(--iv-surface)',
                                  color: isLeast ? 'var(--iv-on-band)' : 'var(--iv-muted)',
                                  fontWeight: 700,
                                  fontSize: '13px',
                                  cursor: 'pointer',
                                }}
                              >
                                Least appropriate
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    currentQuestion.options && currentQuestion.options.map(option => {
                    const isSelected = userAnswer && userAnswer.label === option.label;
                    return (
                      <div
                        key={option.label}
                        onClick={() => selectOption(currentQuestion.id, option.label, currentQuestion)}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '18px',
                          padding: '12px 0',
                          border: 'none',
                          borderRadius: 0,
                          background: 'transparent',
                          cursor: 'pointer',
                          boxShadow: 'none',
                          transition: 'none'
                        }}
                      >
                        <span
                          style={{
                            flex: 'none',
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            border: isSelected ? '2px solid var(--iv-band-line)' : '1.5px solid var(--iv-line)',
                            background: isSelected ? 'var(--iv-band)' : 'transparent',
                            boxSizing: 'border-box',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginTop: '3px',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          {isSelected && (
                            <span
                              style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                background: 'var(--iv-surface)'
                              }}
                            />
                          )}
                        </span>

                        {optionHasText(option.text) && (
                          <span
                            style={{
                              flex: 1,
                              fontWeight: isSelected ? '600' : '450',
                              color: (isTmuaExam || isSelected) ? 'var(--iv-body)' : 'var(--iv-body)',
                              marginTop: '3px'
                            }}
                          >
                            <MathContent
                              style={IVORI_OPTION_TEXT_STYLE}
                              className="ivori-option-text mathjax-process tex2jax_process exhibit-clickable-html"
                              html={prepareSittingHtml(option.text)}
                              onClick={handleExhibitTextClick}
                            />
                          </span>
                        )}

                        <PaperImage
                          url={option.image_url}
                          alt={`Option ${option.label}`}
                          maxHeight="260px"
                        />
                      </div>
                    );
                  })
                  )}
                </div>
              </div>
              </div>
            </div>
            </div>
          );
        })()}

        {/* footer buttons — hidden on Section B / free-text flows (own action bars) */}
        {!isEssayQuestion(currentQuestion) && !isFreeTextPractice(currentQuestion) && (
          isTmuaExam ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            padding: '16px 32px',
            borderTop: `1px solid ${colorScheme !== 'ivori' ? '#14161F' : 'var(--iv-line)'}`,
            background: SIM_SCHEME.chrome
          }}>
            <span
              onClick={() => setReviewScreenOpen(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                fontWeight: '600',
                color: SIM_SCHEME.text,
                background: SIM_SCHEME.chrome,
                border: `1.5px solid ${SIM_SCHEME.border}`,
                borderRadius: '4px',
                padding: '8px 20px',
                cursor: 'pointer'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16,17 21,12 16,7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              End Exam
            </span>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>

              {currentQuestionIndex > 0 && (
                <span
                  onClick={handlePrev}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: SIM_SCHEME.text,
                    background: SIM_SCHEME.chrome,
                    border: `1.5px solid ${SIM_SCHEME.border}`,
                    borderRadius: '4px',
                    padding: '8px 20px',
                    cursor: 'pointer'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="19" y1="12" x2="5" y2="12"></line>
                    <polyline points="11,18 5,12 11,6"></polyline>
                  </svg>
                  Previous
                </span>
              )}

              <span
                onClick={() => setShowNavigatorModal(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: SIM_SCHEME.text,
                  background: SIM_SCHEME.chrome,
                  border: `1.5px solid ${SIM_SCHEME.border}`,
                  borderRadius: '4px',
                  padding: '8px 20px',
                  cursor: 'pointer'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7" rx="1"></rect>
                  <rect x="14" y="3" width="7" height="7" rx="1"></rect>
                  <rect x="3" y="14" width="7" height="7" rx="1"></rect>
                  <rect x="14" y="14" width="7" height="7" rx="1"></rect>
                </svg>
                Navigator
              </span>

              <span
                onClick={() => {
                  handleNext();
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: SIM_SCHEME.text,
                  background: SIM_SCHEME.chrome,
                  border: `1.5px solid ${SIM_SCHEME.border}`,
                  borderRadius: '4px',
                  padding: '8px 20px',
                  cursor: 'pointer'
                }}
              >
                Next
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="13,6 19,12 13,18"></polyline>
                </svg>
              </span>

            </div>
          </div>
          ) : (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '12px',
            padding: '16px 32px',
            borderTop: '1px solid var(--iv-line)',
            background: 'var(--iv-surface)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>

              {currentQuestionIndex > 0 && (
                <span
                  onClick={handlePrev}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'var(--iv-body)',
                    background: 'var(--iv-surface)',
                    border: '1.5px solid var(--iv-line)',
                    borderRadius: '999px',
                    padding: '8px 20px',
                    cursor: 'pointer'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="19" y1="12" x2="5" y2="12"></line>
                    <polyline points="11,18 5,12 11,6"></polyline>
                  </svg>
                  Previous
                </span>
              )}

              <span
                onClick={() => {
                  handleNext();
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'var(--iv-body)',
                  background: 'var(--iv-surface)',
                  border: '1.5px solid var(--iv-line)',
                  borderRadius: '999px',
                  padding: '8px 20px',
                  cursor: 'pointer'
                }}
              >
                Next
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="13,6 19,12 13,18"></polyline>
                </svg>
              </span>

            </div>
          </div>
          )
        )}

        {/* Global Explanation & AI Reasoning Modal */}
        {renderExplainModal()}

        {/* End Review confirmation — from Item Review */}
        {showEndReviewModal && !pearsonPlayer && (() => {
          const incorrectCount = Math.max(0, (stats?.total ?? totalQuestions) - (stats?.correct ?? 0));
          const panelBg = isTmuaExam ? SIM_SCHEME.chrome : 'var(--iv-panel)';
          const panelBorder = isTmuaExam ? SIM_SCHEME.border : 'var(--iv-line)';
          return (
            <div style={{
              position: 'fixed',
              inset: 0,
              zIndex: 10001,
              background: 'color-mix(in srgb, var(--iv-band) 65%, transparent)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px'
            }}>
              <div style={{
                width: '480px',
                maxWidth: '92vw',
                background: panelBg,
                border: `1px solid ${panelBorder}`,
                borderRadius: '16px',
                boxShadow: '0 20px 50px color-mix(in srgb, var(--iv-shadow) 25%, transparent)',
                padding: '32px 28px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                fontFamily: 'var(--iv-font-body)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <div style={{
                    width: '42px',
                    height: '42px',
                    borderRadius: '50%',
                    background: isTmuaExam && colorScheme !== 'ivori' ? SIM_SCHEME.body : 'color-mix(in srgb, var(--iv-danger) 10%, transparent)',
                    border: `1.5px solid ${isTmuaExam ? SIM_SCHEME.border : 'color-mix(in srgb, var(--iv-danger) 30%, transparent)'}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: simDanger,
                    fontSize: 20,
                    fontWeight: 'bold',
                    flexShrink: 0
                  }}>
                    !
                  </div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: 'var(--iv-body)', fontFamily: 'var(--iv-font-head)' }}>
                    End Review
                  </h3>
                </div>

                <p style={{ margin: 0, fontSize: '14.5px', lineHeight: '1.6', color: 'var(--iv-body)' }}>
                  You have chosen to end the current review, but have <strong>{incorrectCount}</strong> incorrect question{incorrectCount === 1 ? '' : 's'}. If you click Yes, you will NOT be able to return to this review.
                </p>
                <p style={{ margin: 0, fontSize: '14.5px', lineHeight: '1.6', color: 'var(--iv-body)' }}>
                  Are you sure you want to end this review?
                </p>

                <div style={{ display: 'flex', gap: '12px', marginTop: '10px', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => setShowEndReviewModal(false)}
                    style={{
                      padding: '10px 22px',
                      borderRadius: '999px',
                      border: `1px solid ${panelBorder}`,
                      background: panelBg,
                      color: 'var(--iv-body)',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    No
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowEndReviewModal(false);
                      setShowEndReviewConfirmModal(true);
                    }}
                    style={{
                      padding: '10px 22px',
                      borderRadius: '999px',
                      border: 'none',
                      background: 'var(--iv-btn)',
                      color: 'var(--iv-btn-fg)',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* End Review confirmation step 2 — unanswered */}
        {showEndReviewConfirmModal && !pearsonPlayer && (() => {
          const unansweredCount = getUnansweredQuestions().length;
          const panelBg = isTmuaExam ? SIM_SCHEME.chrome : 'var(--iv-panel)';
          const panelBorder = isTmuaExam ? SIM_SCHEME.border : 'var(--iv-line)';
          return (
            <div style={{
              position: 'fixed',
              inset: 0,
              zIndex: 10001,
              background: 'color-mix(in srgb, var(--iv-band) 65%, transparent)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px'
            }}>
              <div style={{
                width: '480px',
                maxWidth: '92vw',
                background: panelBg,
                border: `1px solid ${panelBorder}`,
                borderRadius: '16px',
                boxShadow: '0 20px 50px color-mix(in srgb, var(--iv-shadow) 25%, transparent)',
                padding: '32px 28px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                fontFamily: 'var(--iv-font-body)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <div style={{
                    width: '42px',
                    height: '42px',
                    borderRadius: '50%',
                    background: isTmuaExam && colorScheme !== 'ivori' ? SIM_SCHEME.body : 'color-mix(in srgb, var(--iv-danger) 10%, transparent)',
                    border: `1.5px solid ${isTmuaExam ? SIM_SCHEME.border : 'color-mix(in srgb, var(--iv-danger) 30%, transparent)'}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: simDanger,
                    fontSize: 20,
                    fontWeight: 'bold',
                    flexShrink: 0
                  }}>
                    !
                  </div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: 'var(--iv-body)', fontFamily: 'var(--iv-font-head)' }}>
                    Confirm End Review
                  </h3>
                </div>

                <p style={{ margin: 0, fontSize: '14.5px', lineHeight: '1.6', color: 'var(--iv-body)' }}>
                  Please confirm that you want to end this review. If you click Yes, you will NOT be able to return to this review and review the <strong>{unansweredCount}</strong> question{unansweredCount === 1 ? '' : 's'} you have unanswered.
                </p>
                <p style={{ margin: 0, fontSize: '14.5px', lineHeight: '1.6', color: 'var(--iv-head)' }}>
                  Are you sure you want to end this review?
                </p>

                <div style={{ display: 'flex', gap: '12px', marginTop: '10px', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => setShowEndReviewConfirmModal(false)}
                    style={{
                      padding: '10px 22px',
                      borderRadius: '999px',
                      border: `1px solid ${panelBorder}`,
                      background: panelBg,
                      color: 'var(--iv-body)',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    No
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowEndReviewConfirmModal(false);
                      handleReviewSubmit();
                    }}
                    style={{
                      padding: '10px 22px',
                      borderRadius: '999px',
                      border: 'none',
                      background: 'var(--iv-btn)',
                      color: 'var(--iv-btn-fg)',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Entire-screen scroll gate — TMUA only (Pearson-style advisory) */}
        {isTmuaExam && !pearsonPlayer && showEntireScreenModal && (
          <div style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10001,
            background: 'color-mix(in srgb, var(--iv-band) 65%, transparent)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}>
            <div style={{
              width: '440px',
              maxWidth: '92vw',
              background: SIM_SCHEME.chrome,
              border: `1px solid ${SIM_SCHEME.border}`,
              borderRadius: '16px',
              boxShadow: '0 20px 50px color-mix(in srgb, var(--iv-shadow) 25%, transparent)',
              padding: '32px 28px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              fontFamily: 'var(--iv-font-body)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '50%',
                  background: colorScheme === 'ivori' ? 'color-mix(in srgb, var(--iv-danger) 10%, transparent)' : SIM_SCHEME.body,
                  border: `1.5px solid ${SIM_SCHEME.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: simDanger,
                  fontSize: 20,
                  fontWeight: 'bold',
                  flexShrink: 0
                }}>
                  !
                </div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: 'var(--iv-body)', fontFamily: 'var(--iv-font-head)' }}>
                  Unseen Content
                </h3>
              </div>

              <p style={{ margin: 0, fontSize: '14.5px', lineHeight: '1.6', color: 'var(--iv-body)' }}>
                You have not yet viewed the entire screen. Make sure you play all multimedia content, select every tab and scroll to every corner.
              </p>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                <button
                  type="button"
                  onClick={() => setShowEntireScreenModal(false)}
                  style={{
                    padding: '10px 22px',
                    borderRadius: '999px',
                    border: `1px solid ${SIM_SCHEME.border}`,
                    background: SIM_SCHEME.chrome,
                    color: SIM_SCHEME.text,
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Navigator Modal — TMUA only */}
        {isTmuaExam && !pearsonPlayer && showNavigatorModal && (
          <div style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}>
            <div style={{
              width: '900px',
              height: '420px',
              minWidth: '900px',
              minHeight: '420px',
              resize: 'both',
              background: SIM_SCHEME.chrome,
              border: `1px solid ${SIM_SCHEME.border}`,
              borderRadius: '4px',
              boxShadow: '0 8px 24px color-mix(in srgb, var(--iv-shadow) 18%, transparent)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              fontFamily: 'var(--iv-font-body)'
            }}>
              <div style={{ padding: '14px 20px', borderBottom: `1px solid ${colorScheme !== 'ivori' ? SIM_SCHEME.border : 'var(--iv-line)'}`, display: 'flex', alignItems: 'center', gap: '8px', background: SIM_SCHEME.chrome }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="9"></circle>
                  <polygon points="12,7 14,12 12,17 10,12"></polygon>
                </svg>
                <span style={{ fontSize: '14px', fontWeight: 700, color: simInk('var(--iv-body)') }}>
                  Navigator — select a question to go to it
                </span>
              </div>

              <div style={{ overflowY: 'auto', flex: 1, background: SIM_SCHEME.chrome }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '110px 1fr 160px',
                  padding: '8px 20px',
                  background: SIM_SCHEME.chrome,
                  borderBottom: `1px solid ${colorScheme !== 'ivori' ? SIM_SCHEME.border : 'var(--iv-line)'}`,
                  fontSize: '11px',
                  fontWeight: 800,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: simInk('var(--iv-muted)'),
                  position: 'sticky',
                  top: 0
                }}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setNavigatorSortAsc((prev) => !prev)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setNavigatorSortAsc((prev) => !prev);
                      }
                    }}
                    style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', userSelect: 'none' }}
                    aria-sort={navigatorSortAsc ? 'ascending' : 'descending'}
                  >
                    Question #
                    <span style={{ fontSize: '9px' }}>{navigatorSortAsc ? '▴' : '▾'}</span>
                  </div>
                  <div>Status</div>
                  <div>Flagged for Review</div>
                </div>
                {(() => {
                  const indexPairs = questions.map((q, idx) => ({ q, idx }));
                  if (!navigatorSortAsc) indexPairs.reverse();
                  return indexPairs.map(({ q, idx }, displayIdx) => {
                    const answered = isQuestionAnswered(q);
                    const status = answered ? 'Complete' : (visitedQuestions[q.id] ? 'Incomplete' : 'Unseen');
                    const flagged = Boolean(flaggedQuestions[q.id]);
                    const statusColor = simInk(status === 'Complete' ? 'var(--iv-success)' : status === 'Incomplete' ? 'var(--iv-danger)' : 'var(--iv-muted)');
                    const isCurrent = idx === currentQuestionIndex;
                    const isIvori = colorScheme === 'ivori';
                    const zebra = displayIdx % 2 === 0 ? SIM_SCHEME.chrome : SIM_SCHEME.body;
                    return (
                      <div
                        key={q.id ?? idx}
                        onClick={() => { setCurrentQuestionIndex(idx); setShowNavigatorModal(false); }}
                        onMouseEnter={(e) => {
                          if (isIvori) e.currentTarget.style.background = 'var(--iv-surface)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = (isIvori && isCurrent) ? 'var(--iv-surface)' : zebra;
                        }}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '110px 1fr 160px',
                          alignItems: 'center',
                          padding: '8px 20px',
                          borderBottom: `1px solid ${colorScheme !== 'ivori' ? SIM_SCHEME.border : 'var(--iv-line)'}`,
                          cursor: 'pointer',
                          background: (isIvori && isCurrent) ? 'var(--iv-surface)' : zebra
                        }}
                      >
                        <div style={{ fontSize: '13.5px', fontWeight: 700, color: simInk('var(--iv-body)') }}>Question {idx + 1}</div>
                        <div style={{ fontSize: '12.5px', fontWeight: 700, color: statusColor }}>{status}</div>
                        <div style={{ fontSize: '12.5px', fontWeight: 700, color: simInk(flagged ? 'var(--iv-accent)' : 'var(--iv-muted)') }}>
                          {flagged ? 'Flagged' : '—'}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>

              <div style={{
                padding: '12px 20px',
                borderTop: `1px solid ${colorScheme !== 'ivori' ? SIM_SCHEME.border : 'var(--iv-line)'}`,
                background: SIM_SCHEME.body,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <span style={{ fontSize: '13px', color: simInk('var(--iv-body)') }}>
                  {getUnansweredQuestions().length} Unseen/Incomplete
                </span>
                <button
                  type="button"
                  onClick={() => setShowNavigatorModal(false)}
                  style={{
                    padding: '10px 22px',
                    borderRadius: '999px',
                    border: `1px solid ${SIM_SCHEME.border}`,
                    background: SIM_SCHEME.chrome,
                    color: simInk('var(--iv-muted)'),
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Wraps the real component so ExhibitLightboxContext + the lightbox itself
// cover every early-return render path inside QuizDisplaySectionInner (select
// screen, Pearson player, review, etc.) from a single place, without having
// to duplicate provider/lightbox wiring at each of those return statements.
const QuizDisplaySection = (props) => (
  <ExhibitLightboxProvider>
    <QuizDisplaySectionInner {...props} />
  </ExhibitLightboxProvider>
);

export default QuizDisplaySection;
